/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Clock, Mail, Users, Scissors, Plus, Edit2, Trash2, X, Check, Settings, Upload, Download, Search, Send, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, LogOut, User as UserIcon } from 'lucide-react';
import { supabase } from './supabaseClient';
import { sendEmail } from './emailService';
import { useAuth } from './AuthContext';
import AuthUI from './AuthUI';
import { generateInvoice, generateInvoiceFilename } from './invoiceGenerator';

// Minimum notice (in hours) required for public/client bookings
const NOTICE_HOURS = 24;

// Enquiry Form Page Component
const EnquiryFormPage = () => {
  const [formFields, setFormFields] = useState([]);
  const [formInfo, setFormInfo] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [businessSettings, setBusinessSettings] = useState({});
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);
  const formId = urlParams.get('form') || 1;

  useEffect(() => {
    loadForm();
    loadBusinessSettings();
  }, [formId]);

  const loadBusinessSettings = async () => {
  try {
    // First get the form to find the organization_id
    const formResponse = await fetch(
      `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/enquiry_forms?id=eq.${formId}&select=organization_id`,
      {
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
        }
      }
    );
    const formData = await formResponse.json();
    const orgId = formData?.[0]?.organization_id || 1;

    // Then get business settings for that organization
    const response = await fetch(
      `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/business_settings?organization_id=eq.${orgId}&select=*`,
      {
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
        }
      }
    );
    const data = await response.json();
    
    if (data?.[0]) {
      setBusinessSettings({
  companyName: data[0].company_name || 'Pet Grooming',
  logo: data[0].logo_url,
  privacyPolicyUrl: data[0].privacy_policy_url,
  adminEmail: data[0].admin_email,
  emailTemplateHtml: data[0].email_template_html || '',
  redirectUrl: data[0].redirect_url || '' 
});
    }
  } catch (error) {
    console.error('Error loading business settings:', error);
  }
};

  const loadForm = async () => {
  try {
    // Load form
    const formResponse = await fetch(
      `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/enquiry_forms?id=eq.${formId}&is_active=eq.true&select=*`,
      {
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
        }
      }
    );
    const formData = await formResponse.json();
    const form = formData?.[0];

    if (!form) {
      setLoading(false);
      return;
    }

    setFormInfo(form);

    // Load form field references
    const fieldsResponse = await fetch(
      `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/enquiry_form_fields?form_id=eq.${formId}&select=*&order=display_order`,
      {
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
        }
      }
    );
    const fieldReferences = await fieldsResponse.json();
    
    // Now load the actual shared fields via booking_field_id
    if (fieldReferences && fieldReferences.length > 0) {
      const sharedFieldIds = fieldReferences
        .map(f => f.booking_field_id)
        .filter(id => id); // Remove nulls
      
      if (sharedFieldIds.length > 0) {
        const sharedFieldsResponse = await fetch(
          `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/enquiry_form_fields?id=in.(${sharedFieldIds.join(',')})&select=*`,
          {
            headers: {
              'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
            }
          }
        );
        const sharedFields = await sharedFieldsResponse.json();
        
        // Map the shared fields back in the correct order
        const orderedFields = fieldReferences.map(ref => {
          const sharedField = sharedFields.find(sf => sf.id === ref.booking_field_id);
          return sharedField || ref; // Fallback to reference if shared field not found
        });
        
        console.log('✅ Loaded shared fields for enquiry form:', orderedFields);
        setFormFields(orderedFields);
      } else {
        // No booking_field_id links, use the fields directly
        setFormFields(fieldReferences);
      }
    }
    
    setLoading(false);
  } catch (error) {
    console.error('Error loading form:', error);
    setLoading(false);
  }
};

  const handleSubmit = async (e) => {
  e.preventDefault();
  setSubmitting(true);

  try {
    const emailField = formFields.find(f => f.type === 'email');
    const email = emailField ? formData[emailField.id] : '';

    // Email is only required if there's an email field in the form
    const requiresEmail = formFields.some(f => f.type === 'email' && f.required);

    if (requiresEmail && !email) {
      alert('Email address is required');
      setSubmitting(false);
      return;
    }

    const nameField = formFields.find(f => f.label.toLowerCase().includes('name') && !f.label.toLowerCase().includes('pet'));
    const name = nameField ? formData[nameField.id] : '';

    const messageField = formFields.find(f => f.type === 'textarea' || f.label.toLowerCase().includes('message'));
    const message = messageField ? formData[messageField.id] : '';

    // Get organization_id from formInfo (loaded from database)
    const orgId = formInfo?.organization_id || 1;

    const insertData = {
      task_type: 'enquiry',
      enquiry_form_id: formId,
      client_email: email || 'anonymous@feedback.local',
      client_name: name || 'Anonymous',
      enquiry_message: message,
      form_data: formData,
      status: 'pending',
      date: new Date().toISOString().split('T')[0],
      time: new Date().toTimeString().split(' ')[0].substring(0, 5),
      service_name: 'Enquiry',
      is_read: false,
      organization_id: orgId
    };

    // Use direct fetch to insert
    const insertResponse = await fetch(
      'https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/appointments',
      {
        method: 'POST',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation'
        },
        body: JSON.stringify(insertData)
      }
    );

    const data = await insertResponse.json();

    if (!insertResponse.ok) {
      console.error('❌ Error creating enquiry:', data);
      throw new Error(data.message || 'Failed to create enquiry');
    }

    console.log('🎉 Enquiry created:', data);

    // Send email notification to admin
    try {
      const adminEmail = businessSettings.adminEmail;

      if (adminEmail) {
        console.log('✅ Sending notification to:', adminEmail);

        const emailData = {
          to_email: adminEmail,
          to_name: businessSettings.companyName || 'Admin',
          subject: `🔔 New Enquiry from ${name || 'Customer'}`,
          message: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <h2 style="color: #7c3aed;">New Enquiry Received!</h2>
              <p>You have received a new enquiry through your website.</p>
              
              <div style="background-color: #f3f4f6; padding: 20px; border-radius: 8px; margin: 20px 0;">
                <h3 style="margin-top: 0;">Enquiry Details:</h3>
                <p><strong>From:</strong> ${name || 'Anonymous'}</p>
                <p><strong>Email:</strong> ${email || 'Not provided (anonymous)'}</p>
                <p><strong>Date:</strong> ${new Date().toLocaleString()}</p>
                <p><strong>Form:</strong> ${formInfo?.name || 'General Enquiry'}</p>
                
                ${message ? `
                  <div style="margin-top: 15px; padding: 15px; background-color: white; border-left: 4px solid #7c3aed; border-radius: 4px;">
                    <strong>Message:</strong><br/>
                    <p style="margin: 10px 0 0 0; white-space: pre-wrap;">${message}</p>
                  </div>
                ` : ''}
              </div>
              
              <div style="margin-top: 30px; padding: 20px; background-color: #ede9fe; border-radius: 8px;">
                <p style="margin: 0 0 10px 0;"><strong>Next Steps:</strong></p>
                <p style="margin: 5px 0;">1. Log in to your admin panel</p>
                <p style="margin: 5px 0;">2. Go to the Tasks tab</p>
                <p style="margin: 5px 0;">3. Click on this enquiry to view full details</p>
                <p style="margin: 5px 0;">4. Click "Reply" to respond to the customer</p>
                
                <a href="${window.location.origin}" 
                   style="display: inline-block; margin-top: 15px; padding: 12px 24px; background-color: #7c3aed; color: white; text-decoration: none; border-radius: 6px; font-weight: bold;">
                  View in Admin Panel →
                </a>
              </div>
              
              <p style="color: #6b7280; font-size: 12px; margin-top: 30px;">
                This is an automated notification from your Pet Grooming CRM.
              </p>
            </div>
          `,
          customTemplate: businessSettings.emailTemplateHtml,
          companyName: businessSettings.companyName,
          adminEmail: businessSettings.adminEmail,
          reply_to: email
        };

        await sendEmail('followup', emailData);
        console.log('✅ Email notification sent successfully');
      } else {
        console.warn('⚠️ No admin email configured');
      }
    } catch (emailError) {
      console.error('❌ Error sending notification email:', emailError);
      // Don't fail the enquiry submission if email fails
    }

    // NEW: Send custom email template to client if configured
    if (formInfo.custom_email_template_id && email) {
      try {
        console.log('📧 Sending custom template email to client...');
        
        // Fetch the custom template
        const templateResponse = await fetch(
          `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/custom_email_templates?id=eq.${formInfo.custom_email_template_id}&select=*`,
          {
            headers: {
              'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
            }
          }
        );
        
        const templateData = await templateResponse.json();
        
        if (templateData && templateData.length > 0) {
          const template = templateData[0];
          
          // Replace placeholders in template
          const replacePlaceholders = (text, data) => {
            return text
              .replace(/{OwnerName}/g, data.name || '')
              .replace(/{Email}/g, data.email || '')
              .replace(/{PetName}/g, data.petName || '')
              .replace(/{Phone}/g, data.phone || '')
              .replace(/{date}/g, new Date().toLocaleDateString())
              .replace(/{time}/g, new Date().toLocaleTimeString());
          };
          
          const clientEmailData = {
            to_email: email,
            to_name: name || 'Customer',
            subject: replacePlaceholders(template.subject, {
              name,
              email,
              petName: formData[formFields.find(f => f.label.toLowerCase().includes('pet'))?.id] || '',
              phone: formData[formFields.find(f => f.type === 'tel')?.id] || ''
            }),
            message: replacePlaceholders(template.body, {
              name,
              email,
              petName: formData[formFields.find(f => f.label.toLowerCase().includes('pet'))?.id] || '',
              phone: formData[formFields.find(f => f.type === 'tel')?.id] || ''
            }),
            customTemplate: businessSettings.emailTemplateHtml,
            companyName: businessSettings.companyName,
            adminEmail: businessSettings.adminEmail
          };
          
          await sendEmail('followup', clientEmailData);
          console.log('✅ Custom template email sent to client');
        }
      } catch (templateEmailError) {
        console.error('❌ Error sending custom template email:', templateEmailError);
        // Don't fail the submission if custom email fails
      }
    }

    // Check if redirect URL is configured
    if (formInfo.redirect_url) {
      console.log('🔄 Redirecting to:', formInfo.redirect_url);
      window.location.href = formInfo.redirect_url;
    } else {
      console.log('✅ No redirect URL - showing success message');
      setSubmitted(true);
      setFormData({});
      setPrivacyAccepted(false);
    }
  } catch (error) {
    console.error('Error submitting enquiry:', error);
    alert(`Error submitting enquiry: ${error.message || 'Unknown error'}`);
  } finally {
    setSubmitting(false);
  }
};

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-2xl w-full">
          <p className="text-gray-600 text-center">Loading form...</p>
        </div>
      </div>
    );
  }

  if (!formInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-2xl w-full text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Form Not Found</h2>
          <p className="text-gray-600">This enquiry form is not available or has been disabled.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-2xl w-full">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Thank You!</h2>
            <p className="text-gray-600 mb-6">Your enquiry has been submitted successfully.</p>
            <p className="text-gray-600">We'll get back to you as soon as possible.</p>
            <button
              onClick={() => {
                setSubmitted(false);
                setFormData({});
                setPrivacyAccepted(false);
              }}
              className="mt-6 text-purple-600 hover:text-purple-700 font-medium"
            >
              Submit Another Enquiry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          {businessSettings.logo && (
            <img src={businessSettings.logo} alt="Logo" className="h-16 mx-auto mb-4" />
          )}
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            {businessSettings.companyName || 'Contact Us'}
          </h1>
        </div>

        <div className="bg-white rounded-lg shadow-xl p-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-2">{formInfo.name}</h2>
          {formInfo.description && (
            <p className="text-gray-600 mb-6">{formInfo.description}</p>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
  {formFields.map(field => (
    <div key={field.id}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {field.label}
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {field.type === 'textarea' ? (
        <textarea
          value={formData[field.id] || ''}
          onChange={(e) => setFormData({ ...formData, [field.id]: e.target.value })}
          required={field.required}
          rows="5"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
          placeholder={field.help_text || `Enter your ${field.label.toLowerCase()}`}
        />
      ) : field.type === 'select' ? (
        <select
          value={formData[field.id] || ''}
          onChange={(e) => setFormData({ ...formData, [field.id]: e.target.value })}
          required={field.required}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
        >
          <option value="">-- Select --</option>
          {field.options?.map((option, i) => (
            <option key={i} value={option}>{option}</option>
          ))}
        </select>
      ) : (
        <input
          type={field.type}
          value={formData[field.id] || ''}
          onChange={(e) => setFormData({ ...formData, [field.id]: e.target.value })}
          required={field.required}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
          placeholder={field.help_text || `Enter your ${field.label.toLowerCase()}`}
        />
      )}
    </div>
  ))}

            {/* Privacy Policy Checkbox */}
            <div className="border-t pt-4 mt-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  required
                  className="w-5 h-5 mt-0.5 text-purple-600 border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
                />
                <span className="text-sm text-gray-700">
                  I agree to the{' '}
                  <a
                    href={businessSettings.privacyPolicyUrl || 'https://yourwebsite.com/privacy-policy'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:text-purple-700 underline font-medium"
                    onClick={(e) => {
                      if (!businessSettings.privacyPolicyUrl || businessSettings.privacyPolicyUrl === 'https://yourwebsite.com/privacy-policy') {
                        e.preventDefault();
                        alert('Privacy Policy URL not configured. Please set it in your business settings.');
                      }
                    }}
                  >
                    Privacy Policy
                  </a>
                  {' '}and consent to the collection and use of my personal information as described. *
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={submitting || !privacyAccepted}
              className="w-full bg-purple-600 text-white py-4 rounded-lg font-semibold text-lg hover:bg-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
            >
              {submitting ? 'Submitting...' : 'Submit Enquiry'}
            </button>
          </form>
        </div>

        <div className="text-center mt-6 text-sm text-gray-500">
          <p>We respect your privacy and will never share your information.</p>
        </div>
      </div>
    </div>
  );
};

// Rich Text Editor Component for Email Templates
const RichTextEditor = ({ value, onChange, placeholder, rows = 6 }) => {
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const textareaRef = useRef(null);

  const insertFormatting = (prefix, suffix = '') => {
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);

    const newText = value.substring(0, start) + prefix + selectedText + suffix + value.substring(end);
    onChange({ target: { value: newText } });

    // Set cursor position after insertion
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + prefix.length + selectedText.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const insertLink = () => {
    if (!linkUrl || !linkText) {
      alert('Please enter both URL and text for the link');
      return;
    }

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const linkMarkdown = `<a href="${linkUrl}">${linkText}</a>`;

    const newText = value.substring(0, start) + linkMarkdown + value.substring(start);
    onChange({ target: { value: newText } });

    setShowLinkInput(false);
    setLinkUrl('');
    setLinkText('');

    setTimeout(() => textarea.focus(), 0);
  };

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="bg-gray-50 border-b border-gray-300 p-2 flex flex-wrap gap-1">
        <button
          type="button"
          onClick={() => insertFormatting('<b>', '</b>')}
          className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-100 font-bold"
          title="Bold"
        >
          B
        </button>
        <button
          type="button"
          onClick={() => insertFormatting('<i>', '</i>')}
          className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-100 italic"
          title="Italic"
        >
          I
        </button>
        <button
          type="button"
          onClick={() => insertFormatting('<u>', '</u>')}
          className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-100 underline"
          title="Underline"
        >
          U
        </button>
        <div className="w-px bg-gray-300 mx-1"></div>
        <button
          type="button"
          onClick={() => setShowLinkInput(!showLinkInput)}
          className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-100"
          title="Insert Link"
        >
          🔗 Link
        </button>
        <button
          type="button"
          onClick={() => insertFormatting('\n• ')}
          className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-100"
          title="Bullet Point"
        >
          • List
        </button>
      </div>

      {/* Link Input */}
      {showLinkInput && (
        <div className="bg-blue-50 border-b border-blue-200 p-3 space-y-2">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Link Text:</label>
            <input
              type="text"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              placeholder="Click here"
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">URL:</label>
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://example.com"
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={insertLink}
              className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
            >
              Insert Link
            </button>
            <button
              type="button"
              onClick={() => {
                setShowLinkInput(false);
                setLinkUrl('');
                setLinkText('');
              }}
              className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Text Area */}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        className="w-full px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
      />

      {/* Help Text */}
      <div className="bg-gray-50 border-t border-gray-300 px-3 py-2 text-xs text-gray-600">
        💡 Use <b>&lt;b&gt;</b> for bold, <i>&lt;i&gt;</i> for italic, <u>&lt;u&gt;</u> for underline, <code>&lt;a href="url"&gt;text&lt;/a&gt;</code> for links
      </div>
    </div>
  );
};

const PetGroomingCRM = () => {
  const { user, userProfile, organization, loading: authLoading, initialized, signOut } = useAuth();
  const CURRENT_ORG_ID = organization?.id;
  const [userMode, setUserMode] = useState('admin');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [loading, setLoading] = useState(true);
  const [publicOrgSlug, setPublicOrgSlug] = useState(null);
  const [publicOrgId, setPublicOrgId] = useState(null);

  const [services, setServices] = useState([]);
  const [newService, setNewService] = useState({ name: '', duration: 60, price: 0 });
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientEnquiries, setClientEnquiries] = useState([]);
  const [editingClient, setEditingClient] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [clientVisitHistory, setClientVisitHistory] = useState([]);
  const [formFields, setFormFields] = useState([]);
  const [allFormFields, setAllFormFields] = useState([]);
  const [emailSettings, setEmailSettings] = useState({
    confirmationEnabled: true,
    confirmationSubject: 'Appointment Confirmed',
    confirmationBody: 'Your appointment has been confirmed for {date} at {time}.',
    reminderEnabled: true,
    reminderDays: 1,
    reminderSubject: 'Appointment Reminder',
    reminderBody: 'This is a reminder of your appointment tomorrow at {time}.',
    followUpEnabled: true,
    followUpDays: 1,
    followUpSubject: 'Thank You for Visiting Us!',
    followUpBody: 'Thank you for choosing our grooming service! We hope {PetName} enjoyed their visit.',
    rebookingEnabled: true,
    rebookingDays: 30,
    rebookingSubject: 'Time for Another Grooming Session?',
    rebookingBody: 'Hi! It has been a while since {PetName} last visit. We would love to see you both again!'
  });
  const [businessHours, setBusinessHours] = useState({
    monday: { open: true, start: '10:00', end: '16:00' },
    tuesday: { open: true, start: '10:00', end: '18:00' },
    wednesday: { open: false, start: '', end: '' },
    thursday: { open: true, start: '12:00', end: '20:00' },
    friday: { open: true, start: '10:00', end: '16:00' },
    saturday: { open: true, start: '09:00', end: '14:00' },
    sunday: { open: true, start: '10:00', end: '13:00' }
  });

  const [bookingForm, setBookingForm] = useState({
  service: '',
  date: '',
  time: '',
  formData: {}
});
const [bookingSubmitting, setBookingSubmitting] = useState(false);
const [bookingClientMode, setBookingClientMode] = useState('existing'); // 'existing' | 'new'
const [selectedBookingClientId, setSelectedBookingClientId] = useState('');
const [bookingClientSearch, setBookingClientSearch] = useState('');

const [availableSlots, setAvailableSlots] = useState([]);
const [allSlots, setAllSlots] = useState([]);
const [timeSlotData, setTimeSlotData] = useState({ available: [], all: [], closed: false });

// UTM Tracking - Capture from URL parameters
const [utmParams, setUtmParams] = useState({
  utm_source: null,
  utm_medium: null,
  utm_campaign: null,
  utm_term: null,
  utm_content: null
});

  // Capture UTM parameters on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const utm = {
      utm_source: urlParams.get('utm_source'),
      utm_medium: urlParams.get('utm_medium'),
      utm_campaign: urlParams.get('utm_campaign'),
      utm_term: urlParams.get('utm_term'),
      utm_content: urlParams.get('utm_content')
    };

    // Only set if at least one UTM parameter exists
    if (Object.values(utm).some(v => v !== null)) {
      setUtmParams(utm);
      console.log('📊 UTM parameters captured:', utm);
    }
  }, []);

  // Load all form fields when viewing Booking Form, Fields, or Enquiry Forms tab
    useEffect(() => {
    if ((activeTab === 'bookingForm' || activeTab === 'form' || activeTab === 'enquiryForms') && allFormFields.length === 0) {
    const loadAllFields = async () => {
      const { data: fields } = await supabase
        .from('enquiry_form_fields')
        .select('*')
        .eq('organization_id', CURRENT_ORG_ID)
        .order('display_order');
      
      if (fields) {
        console.log('✅ All fields loaded:', fields.length);
        setAllFormFields(fields);
      }
    };
    
    loadAllFields();
  }
}, [activeTab, CURRENT_ORG_ID]);

  // Generate time slots when date or service changes
useEffect(() => {
  const loadTimeSlots = async () => {
    if (bookingForm.date && bookingForm.service) {
      const selectedService = services.find(s => s.id === parseInt(bookingForm.service));
      if (selectedService) {
        const slots = await generateTimeSlots(bookingForm.date, selectedService.duration);
        setTimeSlotData(slots);
        setAvailableSlots(slots.available);
        setAllSlots(slots.all);
      }
    } else {
      setTimeSlotData({ available: [], all: [], closed: false });
      setAvailableSlots([]);
      setAllSlots([]);
    }
  };
  
  loadTimeSlots();
}, [bookingForm.date, bookingForm.service, services]);

  const [editingService, setEditingService] = useState(null);
  const [newField, setNewField] = useState({ label: '', type: 'text', required: false, helpText: '' });
  const [showAddField, setShowAddField] = useState(false);
  const [showAddClient, setShowAddClient] = useState(false);
  const [newClientForm, setNewClientForm] = useState({});
  const [editingOptions, setEditingOptions] = useState([]);
  const [businessSettings, setBusinessSettings] = useState({
    companyName: 'Paws & Claws Grooming CRM',
    adminName: '',
    adminEmail: '',
    logo: null,
    redirectUrl: '',
    privacyPolicyUrl: ''
  });
  const [editingCompanyName, setEditingCompanyName] = useState(false);
  const [tempCompanyName, setTempCompanyName] = useState('');
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [clientFilter, setClientFilter] = useState('');
  const [followUpModal, setFollowUpModal] = useState(null);
  const [followUpEmail, setFollowUpEmail] = useState({ subject: '', message: '' });
  const [emailImages, setEmailImages] = useState([]); // Array of uploaded images for current email
  const [tasksDateFilter, setTasksDateFilter] = useState('all');
  const [tasksSortOrder, setTasksSortOrder] = useState('oldest'); // 'oldest' or 'newest'
  const [customEmailTemplates, setCustomEmailTemplates] = useState([]);
  const [showAddTemplate, setShowAddTemplate] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name: '', subject: '', body: '' });
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [calendarView, setCalendarView] = useState('month'); // 'day', 'week', 'month'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [closureDates, setClosureDates] = useState([]); // Array of closure dates
  const [showAddClosure, setShowAddClosure] = useState(false);
  const [newClosure, setNewClosure] = useState({ startDate: '', endDate: '', reason: '' });

  // Attendance/Payment state
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [attendanceAppointment, setAttendanceAppointment] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [processingPayment, setProcessingPayment] = useState(false);

  // Enquiry Forms state
  const [enquiryForms, setEnquiryForms] = useState([]);
  const [enquiryFormFields, setEnquiryFormFields] = useState([]);
  const [selectedEnquiryForm, setSelectedEnquiryForm] = useState(null);
  const [showAddEnquiryForm, setShowAddEnquiryForm] = useState(false);
  const [newEnquiryForm, setNewEnquiryForm] = useState({ name: '', description: '', customEmailTemplateId: null, redirectUrl: '' });
  const [showAddEnquiryField, setShowAddEnquiryField] = useState(false);
  const [newEnquiryField, setNewEnquiryField] = useState({ label: '', type: 'text', required: false, useExisting: false, existingFieldId: null });
  const [taskTypeFilter, setTaskTypeFilter] = useState('all'); // 'all', 'appointment', 'enquiry'
  const [taskCompletionFilter, setTaskCompletionFilter] = useState('all');
  const [taskAttendanceFilter, setTaskAttendanceFilter] = useState('all');
  const [editingEnquiryField, setEditingEnquiryField] = useState(null);
  const [editingEnquiryOptions, setEditingEnquiryOptions] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null); // Track which dropdown is open
  const [selectedTask, setSelectedTask] = useState(null); // For task detail modal
  const [selectedTaskPayment, setSelectedTaskPayment] = useState(null); // Payment info for task
const checkPublicMode = () => {
  const path = window.location.pathname;
  const hash = window.location.hash;
  const params = new URLSearchParams(window.location.search);
  const hasFormParam = params.get('form');
  const orgSlug = params.get('org');
  
  if (path === '/book' || hash === '#client') {
    setUserMode('client');
    if (orgSlug) {
      setPublicOrgSlug(orgSlug);
    }
  } else if (path === '/enquiry' || hash === '#enquiry' || hasFormParam) {
    setUserMode('enquiry');
  }
};

const loadData = async () => {
  // Determine which organization to load
  let orgId = CURRENT_ORG_ID;
  
  // If in public client mode with org slug, fetch org ID from slug
  if (userMode === 'client' && !user && publicOrgSlug) {
    console.log('🔍 Loading public booking page for org:', publicOrgSlug);
    try {
      const orgResponse = await fetch(
        `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/organizations?slug=eq.${publicOrgSlug}&select=id`,
        {
          headers: {
            'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
          }
        }
      );
      const orgData = await orgResponse.json();
      if (orgData && orgData.length > 0) {
        orgId = orgData[0].id;
        setPublicOrgId(orgId);
        console.log('✅ Found organization ID:', orgId);
      } else {
        console.error('❌ Organization not found with slug:', publicOrgSlug);
        alert('Booking page not found. Please check the URL.');
        setLoading(false);
        return;
      }
    } catch (error) {
      console.error('Error fetching organization:', error);
      setLoading(false);
      return;
    }
  }
  
  // If still no orgId, can't load data
  if (!orgId) {
    console.log('⚠️ No organization ID available, skipping data load');
    setLoading(false);
    return;
  }
  
  console.log('🔍 Loading data for organization:', orgId);
  
  try {
    // Services - always load (needed for both admin and client modes)
    console.log('📥 Fetching services...');
    const servicesResponse = await fetch(
      `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/services?organization_id=eq.${orgId}&select=*&order=id`,
      {
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
        }
      }
    );
    const servicesData = await servicesResponse.json();
    console.log('✅ Services loaded:', servicesData.length);
    if (servicesData && !servicesData.error) {
      setServices(servicesData);
    }

    // Business hours - always load (needed for booking availability)
    console.log('📥 Fetching business hours...');
    const hoursResponse = await fetch(
      `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/business_hours?organization_id=eq.${orgId}&select=*`,
      {
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
        }
      }
    );
    const hoursData = await hoursResponse.json();
    if (hoursData && hoursData.length > 0 && !hoursData.error) {
      const hoursObj = {};
      hoursData.forEach(h => {
        hoursObj[h.day] = { open: h.open, start: h.start_time || '', end: h.end_time || '' };
      });
      setBusinessHours(hoursObj);
    }

    // Form fields - load from enquiry_form_fields for booking form
console.log('📥 Fetching form fields...');
const fieldsResponse = await fetch(
  `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/enquiry_form_fields?select=*&order=display_order`,
  {
    headers: {
      'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
    }
  }
);
const fieldsData = await fieldsResponse.json();
console.log('📋 All enquiry form fields loaded:', fieldsData?.length || 0);

// Don't set formFields yet - wait until business settings loads to filter by saved selection

    // Business settings - always load (needed for branding)
const settingsResponse = await fetch(
  `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/business_settings?organization_id=eq.${orgId}&select=*`,
  {
    headers: {
      'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
    }
  }
);
const settingsDataArray = await settingsResponse.json();
const settingsData = settingsDataArray && settingsDataArray.length > 0 ? settingsDataArray[0] : null;
if (settingsData && !settingsData.error) {
  setBusinessSettings({
    companyName: settingsData.company_name || 'Paws & Claws Grooming CRM',
    adminName: settingsData.admin_name || '',
    adminEmail: settingsData.admin_email || '',
    logo: settingsData.logo_url || null,
    redirectUrl: settingsData.redirect_url || '',
    privacyPolicyUrl: settingsData.privacy_policy_url || '',
    emailTemplateHtml: settingsData.email_template_html || '',
    bookingFormFieldIds: settingsData.booking_form_field_ids || [] // ADD THIS LINE
  });
  
  // Filter formFields based on saved selection
  if (settingsData.booking_form_field_ids && settingsData.booking_form_field_ids.length > 0 && fieldsData && fieldsData.length > 0) {
    // Sort fields in the same order as booking_form_field_ids
    const selectedFields = settingsData.booking_form_field_ids
      .map(id => fieldsData.find(field => field.id === id))
      .filter(field => field); // Remove any null/undefined
    
    // Apply required status from booking_form_required_field_ids
    const fieldsWithRequiredStatus = selectedFields.map(field => ({
      ...field,
      required: settingsData.booking_form_required_field_ids?.includes(field.id) || false
    }));
    
    console.log('✅ Loaded saved booking form fields:', fieldsWithRequiredStatus.length);
    console.log('🔍 First field required:', fieldsWithRequiredStatus[0]?.required);
    setFormFields(fieldsWithRequiredStatus);
  }
}

    // Closure dates - always load (needed for booking availability)
    const closureResponse = await fetch(
  `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/closure_dates?organization_id=eq.${orgId}&select=*&order=closure_date`,
  {
    headers: {
      'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
    }
  }
);
const closureData = await closureResponse.json();
if (closureData && !closureData.error) {
  setClosureDates(closureData);
}

// Email settings - always load (needed for confirmation emails in both admin and public modes)
console.log('📧 Fetching email settings for org:', orgId);
const emailSettingsResponse = await fetch(
  `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/email_settings?organization_id=eq.${orgId}&select=*`,
  {
    headers: {
      'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
    }
  }
);
const emailSettingsArray = await emailSettingsResponse.json();
const emailData = emailSettingsArray && emailSettingsArray.length > 0 ? emailSettingsArray[0] : null;
console.log('📧 Email settings loaded:', emailData);
if (emailData && !emailData.error) {
  setEmailSettings({
    confirmationEnabled: emailData.confirmation_enabled ?? true,
    confirmationSubject: emailData.confirmation_subject || 'Appointment Confirmed',
    confirmationBody: emailData.confirmation_body || 'Your appointment has been confirmed for {date} at {time}.',
    reminderEnabled: emailData.reminder_enabled ?? true,
    reminderDays: emailData.reminder_days || 1,
    reminderSubject: emailData.reminder_subject || 'Appointment Reminder',
    reminderBody: emailData.reminder_body || 'This is a reminder of your appointment tomorrow at {time}.',
    followUpEnabled: emailData.follow_up_enabled ?? true,
    followUpDays: emailData.follow_up_days || 1,
    followUpSubject: emailData.follow_up_subject || 'Thank You for Visiting Us!',
    followUpBody: emailData.follow_up_body || 'Thank you for choosing our grooming service! We hope {PetName} enjoyed their visit.',
    rebookingEnabled: emailData.rebooking_enabled ?? true,
    rebookingDays: emailData.rebooking_days || 30,
    rebookingSubject: emailData.rebooking_subject || 'Time for Another Grooming Session?',
    rebookingBody: emailData.rebooking_body || 'Hi! It has been a while since {PetName} last visit. We would love to see you both again!'
  });
  console.log('✅ Email settings configured with confirmation body:', emailData.confirmation_body?.substring(0, 50));
}

// Admin-only data (only load when logged in as admin)
if (user && userMode === 'admin') {
  // Clients
  console.log('📥 Fetching clients...');
  const clientsResponse = await fetch(
    `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/clients?organization_id=eq.${orgId}&select=*&order=id`,
    {
      headers: {
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
      }
    }
  );
  const clientsData = await clientsResponse.json();
  console.log('✅ Clients loaded:', clientsData.length);
  if (clientsData && !clientsData.error) {
    setClients(clientsData);
  }

  // Appointments
  console.log('📥 Fetching appointments...');
  const appointmentsResponse = await fetch(
    `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/appointments?organization_id=eq.${orgId}&select=*&order=created_at.desc`,
    {
      headers: {
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
      }
    }
  );
  const appointmentsData = await appointmentsResponse.json();
  console.log('✅ Appointments loaded:', appointmentsData.length);
  if (appointmentsData && !appointmentsData.error) {
    setAppointments(appointmentsData);
  }

  // Custom email templates
  const templatesResponse = await fetch(
    `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/custom_email_templates?organization_id=eq.${orgId}&select=*&order=id`,
    {
      headers: {
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
      }
    }
  );
  const templatesData = await templatesResponse.json();
  console.log('📧 Custom templates response:', templatesResponse.status, templatesData);
  if (templatesData && !templatesData.error) {
    console.log('✅ Setting custom templates:', templatesData.length);
    setCustomEmailTemplates(templatesData);
  } else {
    console.error('❌ Error loading templates:', templatesData);
  }

  // Enquiry forms
  const formsResponse = await fetch(
    `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/enquiry_forms?organization_id=eq.${orgId}&select=*&order=id`,
    {
      headers: {
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
      }
    }
  );
  const formsData = await formsResponse.json();
  if (formsData && !formsData.error) {
    setEnquiryForms(formsData);
  }
}

console.log('✅ All data loaded successfully');
    
  } catch (error) {
    console.error('❌ Error loading data:', error);
  } finally {
    setLoading(false);
  }
};

// All useEffect hooks MUST come before any conditional returns
useEffect(() => {
  const initialize = async () => {
    // Only run if both user and organization are loaded
    if (!user || !organization) {
      console.log('⏳ Waiting for user or organization...');
      return;
    }
    
    console.log('✅ User authenticated:', user.email);
    console.log('✅ Organization:', organization.name, '(ID:', organization.id, ')');
    loadData();
  };
  initialize();
}, [user?.id, organization?.id]);

useEffect(() => {
  console.log('🔍 Checking public mode on mount...');
  checkPublicMode();
}, []);

// Reload data when publicOrgSlug changes (for public booking pages)
useEffect(() => {
  if (publicOrgSlug && userMode === 'client' && !user) {
    console.log('🔄 Public org slug detected, reloading data for:', publicOrgSlug);
    loadData();
  }
}, [publicOrgSlug]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest('.user-menu-container')) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.relative')) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

if (authLoading && !initialized) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading...</p>
      </div>
    </div>
  );
}

console.log('🔍 Checking auth - user:', user?.email, 'userMode:', userMode);

// Allow public access to enquiry forms
if (userMode === 'enquiry') {
  console.log('🌐 Public enquiry form access');
  return <EnquiryFormPage />;
}

if (!user && userMode === 'admin') {
  console.log('🔐 Showing AuthUI');
  return <AuthUI />;
}

  console.log('✅ Auth check passed, loading app...');
  // Functions come after auth checks
  const openTaskDetail = async (task) => {
    setSelectedTask(task);

    // Load payment info if it's an attended appointment
    if (task.task_type !== 'enquiry' && task.attendance_status === 'attended') {
      const { data: payment, error } = await supabase
        .from('payments')
        .select('*')
        .eq('appointment_id', task.id)
        .single();

      if (!error && payment) {
        setSelectedTaskPayment(payment);
      } else {
        setSelectedTaskPayment(null);
      }
    } else {
      setSelectedTaskPayment(null);
    }
  };

const generateTimeSlots = async (date, serviceDuration) => {
  if (!date) return { available: [], all: [] };

  // Check if date is a closure date
  if (isClosureDate(date)) {
    return { available: [], all: [], closed: true };
  }

  const dayOfWeek = new Date(date).toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
  const hours = businessHours[dayOfWeek];
  if (!hours.open) return { available: [], all: [], closed: true };

  const allSlots = [];
  const availableSlots = [];
  const [startHour, startMin] = hours.start.split(':').map(Number);
  const [endHour, endMin] = hours.end.split(':').map(Number);
  const startTime = startHour * 60 + startMin;
  const endTime = endHour * 60 + endMin;

  // Fetch appointments for this specific date (works in both admin and public modes)
  const orgId = CURRENT_ORG_ID || publicOrgId;
  let dateAppointments = [];
  
  try {
    console.log('🔍 Fetching appointments for date:', date, 'org:', orgId);
    const response = await fetch(
      `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/appointments?organization_id=eq.${orgId}&date=eq.${date}&or=(task_type.is.null,task_type.neq.enquiry)&select=*`,
      {
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
        }
      }
    );
    const data = await response.json();
    console.log('📅 Appointments found:', data);
    if (data && !data.error) {
      dateAppointments = data;
      console.log('✅ Using', dateAppointments.length, 'appointments for conflict check');
    }
  } catch (error) {
    console.error('❌ Error fetching appointments for availability:', error);
  }

  for (let time = startTime; time + serviceDuration <= endTime; time += 30) {
    const hour = Math.floor(time / 60);
    const min = time % 60;
    const slotTime = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;

    // Check if this slot conflicts with any existing appointment
    const hasConflict = dateAppointments.some(appt => {
      const [apptHour, apptMin] = appt.time.split(':').map(Number);
      const apptStartMinutes = apptHour * 60 + apptMin;

      // Get the service duration for the existing appointment
      const existingService = services.find(s => s.id === appt.service_id);
      const existingDuration = existingService?.duration || 60;
      const apptEndMinutes = apptStartMinutes + existingDuration;

      // Check if the new appointment would overlap
      const newSlotEnd = time + serviceDuration;

      // Overlap occurs if:
      // - New appointment starts during existing appointment
      // - New appointment ends during existing appointment
      // - New appointment completely covers existing appointment
      return (time < apptEndMinutes && newSlotEnd > apptStartMinutes);
    });

    // Enforce minimum notice period for public (client) bookings
    const slotDateTime = new Date(`${date}T${slotTime}:00`);
    const tooSoon = userMode === 'client' &&
      (slotDateTime - new Date()) < NOTICE_HOURS * 60 * 60 * 1000;

    allSlots.push(slotTime);
    if (!hasConflict && !tooSoon) {
      availableSlots.push(slotTime);
    }
  }
  
  console.log('🎯 Generated slots - Available:', availableSlots.length, 'Total:', allSlots.length);
  return { available: availableSlots, all: allSlots };
};

const replacePlaceholders = (text, data) => {
  let result = text;

  // Basic appointment placeholders
  result = result.replace(/{date}/g, data.date || '');
  result = result.replace(/{time}/g, data.time || '');
  result = result.replace(/{serviceName}/g, data.serviceName || '');

  // Client/Pet placeholders - use dedicated fields first, then form_data as fallback
  result = result.replace(/{OwnerName}/gi, data.ownerName || data.clientName || '');
  result = result.replace(/{PetName}/gi, data.petName || '');
  result = result.replace(/{Email}/gi, data.email || data.clientEmail || '');
  result = result.replace(/{Phone}/gi, data.phone || '');
  result = result.replace(/{PetBreed}/gi, data.petBreed || '');
  result = result.replace(/{SpecialRequirements}/gi, data.specialRequirements || '');

  // Form field placeholders (for any custom fields)
  formFields.forEach(field => {
    const placeholder = `{${field.label.replace(/\s+/g, '')}}`;
    const value = data.formData?.[field.id] || '';
    result = result.replace(new RegExp(placeholder, 'g'), value);
  });

  return result;
};

    const handleBookingSubmit = async (e) => {
      e.preventDefault();

      if (bookingSubmitting) return; // Prevent double submission

      console.log('🔵 Booking submission started');

      // Check privacy consent
      if (!privacyConsent) {
        console.log('❌ Privacy consent not checked');
        alert('Please agree to the Privacy Policy to continue.');
        return;
      }

      console.log('✅ Privacy consent checked');

      // Admin must pick an existing client (or switch to "New client")
      if (userMode === 'admin' && bookingClientMode === 'existing' && !selectedBookingClientId) {
        alert('Please choose an existing client, or switch to "New client".');
        return;
      }

      // Re-check notice period at submit time (page may have been left open)
      if (userMode === 'client') {
        const apptStart = new Date(`${bookingForm.date}T${bookingForm.time}:00`);
        if (apptStart - new Date() < NOTICE_HOURS * 60 * 60 * 1000) {
          alert(`Sorry, bookings need at least ${NOTICE_HOURS} hours' notice. Please choose a later time.`);
          return;
        }
      }

      setBookingSubmitting(true);

      try {
        const selectedService = services.find(s => s.id === parseInt(bookingForm.service));
        const serviceName = selectedService?.name;

        // Re-check availability right before saving (the slot may have been taken since the form was opened)
        const freshSlots = await generateTimeSlots(bookingForm.date, selectedService?.duration || 60);
        if (!freshSlots.available.includes(bookingForm.time)) {
          alert('Sorry, that time slot has just been taken. Please choose another time.');
          setBookingForm(prev => ({ ...prev, time: '' }));
          setTimeSlotData(freshSlots);
          setAvailableSlots(freshSlots.available);
          setAllSlots(freshSlots.all);
          setBookingSubmitting(false);
          return;
        }

        console.log('📝 Creating appointment for:', serviceName);
        console.log('📋 Form data:', bookingForm.formData);

        // Find fields dynamically (in case field IDs changed)
        const emailField = formFields.find(f => f.type === 'email');
        const ownerNameField = formFields.find(f => f.label.toLowerCase().includes('owner') || f.label.toLowerCase().includes('name'));
        const petNameField = formFields.find(f => f.label.toLowerCase().includes('pet') && f.label.toLowerCase().includes('name'));
        const phoneField = formFields.find(f => f.type === 'tel' || f.label.toLowerCase().includes('phone'));
        const breedField = formFields.find(f => f.label.toLowerCase().includes('breed'));
        const specialReqField = formFields.find(f => f.label.toLowerCase().includes('special') || f.label.toLowerCase().includes('requirement'));

        const email = emailField ? bookingForm.formData[emailField.id] : bookingForm.formData[2];
        const ownerName = ownerNameField ? bookingForm.formData[ownerNameField.id] : bookingForm.formData[1];
        const petName = petNameField ? bookingForm.formData[petNameField.id] : bookingForm.formData[4];
        const phone = phoneField ? bookingForm.formData[phoneField.id] : bookingForm.formData[3];
        const petBreed = breedField ? bookingForm.formData[breedField.id] : bookingForm.formData[6];
        const specialRequirements = specialReqField ? bookingForm.formData[specialReqField.id] : '';

        console.log('📧 Email:', email, 'Owner:', ownerName, 'Pet:', petName, 'Breed:', petBreed);

        if (!email) {
          alert('Email address is required. Please fill in all required fields.');
          return;
        }

        // Get session token (for public booking, we might not have one, so we'll use anon key)
const { data: { session } } = await supabase.auth.getSession();
const accessToken = session?.access_token || process.env.REACT_APP_SUPABASE_ANON_KEY;

// Insert appointment
const apptResponse = await fetch(
  'https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/appointments',
  {
    method: 'POST',
    headers: {
      'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      'Prefer': 'return=representation'
    },
    body: JSON.stringify({
      service_id: bookingForm.service,
      service_name: serviceName,
      date: bookingForm.date,
      time: bookingForm.time,
      client_email: email,
      client_name: ownerName,
      pet_name: petName,
      phone: phone,
      pet_breed: petBreed,
      special_requirements: specialRequirements,
      form_data: bookingForm.formData,
      organization_id: CURRENT_ORG_ID || publicOrgId,
      utm_source: utmParams.utm_source,
      utm_medium: utmParams.utm_medium,
      utm_campaign: utmParams.utm_campaign,
      utm_term: utmParams.utm_term,
      utm_content: utmParams.utm_content
    })
  }
);

const apptData = await apptResponse.json();

if (!apptResponse.ok || apptData.error) {
  console.error('❌ Appointment insert error:', apptData);
  throw new Error(apptData.message || 'Failed to create appointment');
}

console.log('✅ Appointment created');

// Check for existing client
console.log('👤 Checking for existing client:', email);
const clientResponse = await fetch(
  `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/clients?email=eq.${encodeURIComponent(email)}&organization_id=eq.${CURRENT_ORG_ID || publicOrgId}&select=*`,
  {
    headers: {
      'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${accessToken}`
    }
  }
);

const clientData = await clientResponse.json();
const existingClient = clientData?.[0];

if (existingClient) {
  console.log('✅ Updating existing client');
  const updatedServices = [...(existingClient.services || []), { name: serviceName, date: new Date() }];
  
  await fetch(
    `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/clients?id=eq.${existingClient.id}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        last_visit: new Date().toISOString(),
        appointment_count: existingClient.appointment_count + 1,
        phone: phone,
        form_data: bookingForm.formData,
        services: updatedServices
      })
    }
  );
} else {
  console.log('✅ Creating new client');
  await fetch(
    'https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/clients',
    {
      method: 'POST',
      headers: {
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify({
        owner_name: ownerName,
        email: email,
        phone: phone,
        pet_name: petName,
        form_data: bookingForm.formData,
        appointment_count: 1,
        services: [{ name: serviceName, date: new Date() }],
        organization_id: CURRENT_ORG_ID || publicOrgId
      })
    }
  );
}

console.log('📧 Checking email settings...');
if (emailSettings.confirmationEnabled) {
  const emailData = {
    to_email: email,
    to_name: ownerName,
    subject: emailSettings.confirmationSubject,
    message: replacePlaceholders(emailSettings.confirmationBody, {
      date: bookingForm.date,
      time: bookingForm.time,
      serviceName,
      ownerName,
      petName,
      email,
      phone,
      petBreed,
      specialRequirements,
      formData: bookingForm.formData
    }),
    customTemplate: businessSettings.emailTemplateHtml,
    companyName: businessSettings.companyName,
    adminEmail: businessSettings.adminEmail,
    pet_name: petName,
    service_name: serviceName,
    date: bookingForm.date,
    time: bookingForm.time
  };

          try {
            console.log('📧 Sending confirmation email...');
            await sendEmail('confirmation', emailData);
            console.log('✅ Email sent');
          } catch (emailError) {
            console.error('⚠️ Email error (non-fatal):', emailError);
          }
        } else {
          console.log('⏭️ Email confirmation disabled');
        }

        // Send notification email to business owner
        try {
          if (businessSettings.adminEmail) {
            console.log('📧 Sending owner notification email...');

            // Create calendar event details
            const appointmentDate = new Date(bookingForm.date + 'T' + bookingForm.time);
            const serviceDuration = selectedService?.duration || 60;
            const endDate = new Date(appointmentDate.getTime() + (serviceDuration * 60000));

            // Format dates for calendar link
            const calendarStartDate = appointmentDate.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
            const calendarEndDate = endDate.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

            // Create Google Calendar URL
            const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(serviceName + ' - ' + petName)}&dates=${calendarStartDate}/${calendarEndDate}&details=${encodeURIComponent('Client: ' + ownerName + '\nPet: ' + petName + '\nEmail: ' + email + '\nPhone: ' + (phone || 'N/A'))}&location=${encodeURIComponent(businessSettings.companyName || 'Grooming Salon')}`;

            const ownerEmailData = {
              to_email: businessSettings.adminEmail,
              to_name: businessSettings.adminName || 'Admin',
              subject: `New Appointment: ${serviceName} - ${petName}`,
              message: `New appointment booked!\n\nService: ${serviceName}\nDate: ${new Date(bookingForm.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}\nTime: ${bookingForm.time}\n\nClient Details:\nOwner: ${ownerName}\nEmail: ${email}\nPhone: ${phone || 'N/A'}\nPet: ${petName}\n\n📅 Add to Calendar:\n${googleCalendarUrl}\n\nView in admin panel: ${window.location.origin}`,
              pet_name: petName,
              service_name: serviceName,
              date: bookingForm.date,
              time: bookingForm.time,
              client_name: ownerName,
              client_email: email
            };

            await sendEmail('confirmation', ownerEmailData);
            console.log('✅ Owner notification sent');
          }
        } catch (emailError) {
          // Don't block booking if owner notification fails
          console.error('⚠️ Owner notification error (non-fatal):', emailError);
        }

        // Success! Now handle post-booking action
        console.log('🎉 Booking successful!');
        console.log('🔍 User mode:', userMode);
        console.log('🔍 Checking redirect URL:', businessSettings.redirectUrl);

        // If booking was made from admin panel, reload and show appointments
        if (userMode === 'admin') {
          console.log('✅ Admin booking - reloading data and switching to appointments tab');
          alert('Appointment booked successfully!');
          await loadData();
          setActiveTab('appointments');
          setBookingForm({ service: '', date: '', time: '', formData: {} });
          setPrivacyConsent(false);
          setSelectedBookingClientId('');
          setBookingClientSearch('');
        } else {
          // Client booking - check for redirect URL
          const redirectUrl = businessSettings.redirectUrl?.trim();
          if (redirectUrl && redirectUrl !== '') {
            // Ensure URL is absolute (starts with http:// or https://)
            let finalUrl = redirectUrl;
            if (!redirectUrl.startsWith('http://') && !redirectUrl.startsWith('https://')) {
              finalUrl = 'https://' + redirectUrl;
            }
            // Redirect to custom URL
            console.log('🔄 Redirecting to:', finalUrl);
            window.location.href = finalUrl;
          } else {
            // No redirect URL set - show default success message and stay on page
            console.log('✅ No redirect URL - showing success message');
            alert('Appointment booked successfully! Thank you for booking with us.');
            setBookingForm({ service: '', date: '', time: '', formData: {} });
            setPrivacyConsent(false);
          }
        }

      } catch (error) {
        console.error('❌ BOOKING ERROR:', error);
        alert('Error booking appointment. Please try again.');
      } finally {
        setBookingSubmitting(false);
      }
    };

    const deleteAppointment = async (id) => {
  if (!window.confirm('Are you sure you want to delete this appointment?')) return;
  
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;

    if (!accessToken) {
      alert('Session expired. Please refresh and try again.');
      return;
    }

    // First delete related email logs
    await fetch(
      `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/email_log?appointment_id=eq.${id}`,
      {
        method: 'DELETE',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      }
    );

    // Then delete the appointment
    const response = await fetch(
      `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/appointments?id=eq.${id}`,
      {
        method: 'DELETE',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.ok) {
      console.log('✅ Appointment deleted');
      await loadData();
    } else {
      const data = await response.json();
      console.error('❌ Error deleting appointment:', data);
      alert('Error deleting appointment');
    }
  } catch (error) {
    console.error('Error deleting appointment:', error);
    alert('Error deleting appointment');
  }
};

const markTaskComplete = async (appointmentId) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;

    if (!accessToken) {
      alert('Session expired. Please refresh and try again.');
      return;
    }

    const response = await fetch(
      `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/appointments?id=eq.${appointmentId}`,
      {
        method: 'PATCH',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ completed: true })
      }
    );

    if (response.ok) {
      console.log('✅ Task marked as complete');
      await loadData();
    } else {
      const data = await response.json();
      console.error('Error marking task complete:', data);
      alert('Error marking task as complete');
    }
  } catch (error) {
    console.error('Error marking task complete:', error);
    alert('Error marking task as complete');
  }
};

    const markAsAttended = async (appointment, paymentMethod) => {
  setProcessingPayment(true);

  try {
    console.log('🎫 Starting attendance marking process...');

    // Get session token
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;

    if (!accessToken) {
      alert('Session expired. Please refresh and try again.');
      return;
    }

    // 1. Generate invoice number
    const { data: invoiceNumber, error: invoiceError } = await supabase.rpc('generate_invoice_number');

    if (invoiceError) throw invoiceError;

    console.log('📄 Generated invoice number:', invoiceNumber);

    // 2. Get service price
    const service = services.find(s => s.id === appointment.service_id);
    const amount = service?.price || 0;

    console.log('💰 Service amount:', amount);

    // 3. Generate invoice PDF
    console.log('📝 Generating invoice PDF...');
    const invoiceBlob = await generateInvoice({
      invoiceNumber,
      date: appointment.date,
      clientName: appointment.client_name || 'Customer',
      clientEmail: appointment.client_email,
      petName: appointment.pet_name,
      serviceName: appointment.service_name,
      amount,
      paymentMethod,
      businessName: businessSettings.companyName,
      businessEmail: businessSettings.adminEmail,
      businessPhone: businessSettings.phone,
      businessAddress: businessSettings.address,
      logoUrl: businessSettings.logo
    });

    console.log('✅ Invoice PDF generated');

    // 4. Upload invoice to Supabase Storage
    const filename = generateInvoiceFilename(invoiceNumber);
    const filepath = `${CURRENT_ORG_ID}/${filename}`;

    console.log('📤 Uploading to:', filepath);

    const { error: uploadError } = await supabase.storage
      .from('invoices')
      .upload(filepath, invoiceBlob, {
        contentType: 'application/pdf',
        upsert: false
      });

    if (uploadError) {
      console.error('Upload error:', uploadError);
      throw uploadError;
    }

    console.log('✅ Invoice uploaded to storage');

    // 5. Get signed URL (valid for 1 year)
    const { data: urlData, error: urlError } = await supabase.storage
      .from('invoices')
      .createSignedUrl(filepath, 31536000); // 1 year in seconds

    if (urlError) {
      console.error('URL error:', urlError);
      throw urlError;
    }

    const invoiceUrl = urlData.signedUrl;
    console.log('🔗 Invoice URL:', invoiceUrl);

    // 6. Create payment record using direct fetch with session token
    console.log('💳 Creating payment record...');
    const paymentResponse = await fetch(
      'https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/payments',
      {
        method: 'POST',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation'
        },
        body: JSON.stringify({
          appointment_id: appointment.id,
          client_email: appointment.client_email,
          amount,
          payment_method: paymentMethod,
          invoice_number: invoiceNumber,
          invoice_url: invoiceUrl,
          status: 'paid',
          organization_id: CURRENT_ORG_ID
        })
      }
    );

    const paymentData = await paymentResponse.json();

    if (!paymentResponse.ok || paymentData.error) {
      console.error('Payment error:', paymentData);
      throw new Error(paymentData.message || 'Failed to create payment record');
    }

    const payment = paymentData[0];
    console.log('✅ Payment record created:', payment);

    // 7. Update appointment status using direct fetch
    console.log('📅 Updating appointment status...');
    const updateResponse = await fetch(
      `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/appointments?id=eq.${appointment.id}`,
      {
        method: 'PATCH',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          attendance_status: 'attended',
          attended_at: new Date().toISOString(),
          payment_id: payment.id
        })
      }
    );

    if (!updateResponse.ok) {
      const updateData = await updateResponse.json();
      console.error('Update error:', updateData);
      throw new Error('Failed to update appointment');
    }

    console.log('✅ Appointment updated');

    // 8. Send invoice email to client
    console.log('📧 Sending invoice email to client...');
    try {
      // Generate a fresh signed URL for the email (valid for 7 days)
      const { data: emailUrlData, error: emailUrlError } = await supabase.storage
        .from('invoices')
        .createSignedUrl(filepath, 604800); // 7 days in seconds

      if (emailUrlError) {
        console.error('⚠️ Could not generate download URL:', emailUrlError);
      } else {
        const downloadUrl = emailUrlData.signedUrl;

        const emailData = {
          to_email: appointment.client_email,
          to_name: appointment.client_name || 'Valued Customer',
          subject: `Receipt for Your Appointment - ${invoiceNumber}`,
          message: `Thank you for visiting ${businessSettings.companyName || 'our salon'}!

Your payment has been received:

Service: ${appointment.service_name}
Date: ${new Date(appointment.date).toLocaleDateString()}
Amount: £${amount.toFixed(2)}
Payment Method: ${paymentMethod === 'card' ? 'Card' : 'Cash'}
Invoice Number: ${invoiceNumber}

Download your invoice here:
${downloadUrl}

(Link valid for 7 days)

${appointment.pet_name ? `We hope to see ${appointment.pet_name} again soon!\n\n` : ''}Best wishes,
${businessSettings.companyName || 'The Team'}`,
          customTemplate: businessSettings.emailTemplateHtml,
          companyName: businessSettings.companyName,
          adminEmail: businessSettings.adminEmail
        };

        await sendEmail('invoice', emailData);
        console.log('✅ Invoice email sent to client');
      }
    } catch (emailError) {
      console.error('⚠️ Failed to send invoice email (non-fatal):', emailError);
      // Don't fail the whole process if email fails
    }

    // 9. Reload data
    await loadData();

    alert(`✅ Attendance marked! Invoice ${invoiceNumber} created and emailed to client.`);
    return { invoiceNumber, invoiceUrl };

  } catch (error) {
    console.error('❌ Error marking attendance:', error);
    alert(`Error: ${error.message || 'Unknown error occurred'}`);
    throw error;
  } finally {
    setProcessingPayment(false);
  }
};

    const getInvoiceUrl = async (invoiceUrl) => {
      // Extract the file path from the URL
      // URL format: https://xxx.supabase.co/storage/v1/object/sign/invoices/1/INV-2025-00001.pdf?token=xxx
      // We need: 1/INV-2025-00001.pdf

      try {
        const urlParts = invoiceUrl.split('/invoices/');
        if (urlParts.length < 2) return invoiceUrl; // If URL format unexpected, return as-is

        const filepath = urlParts[1].split('?')[0]; // Remove query params

        // Generate new signed URL
        const { data, error } = await supabase.storage
          .from('invoices')
          .createSignedUrl(filepath, 3600); // 1 hour

        if (error) {
          console.error('Error generating signed URL:', error);
          return invoiceUrl; // Fallback to original URL
        }

        return data.signedUrl;
      } catch (error) {
        console.error('Error parsing invoice URL:', error);
        return invoiceUrl;
      }
    };

    const sendInvoiceEmail = async (appointment, payment) => {
  if (!payment || !payment.invoice_url) {
    alert('No invoice found for this appointment');
    return;
  }

  try {
    console.log('📧 Sending invoice email...');
    console.log('💾 Payment object:', payment);
    console.log('🔗 Invoice URL:', payment.invoice_url);

    // Extract filepath from invoice URL
    const urlParts = payment.invoice_url.split('/invoices/');
    console.log('🔍 URL parts:', urlParts);
    
    if (urlParts.length < 2) {
      console.error('❌ Invalid URL structure');
      alert('Invalid invoice URL');
      return;
    }
    
    const filepath = urlParts[1].split('?')[0];
    console.log('📄 Extracted filepath:', filepath);

    // Generate fresh signed URL using REST API (valid for 7 days)
    const url = `https://bfffxyrfgvgprxjgqrvf.supabase.co/storage/v1/object/sign/invoices/${filepath}`;
    console.log('🌐 Calling URL:', url);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        expiresIn: 604800 // 7 days in seconds
      })
    });

    console.log('📡 Response status:', response.status);
    const data = await response.json();
    console.log('📦 Response data:', data);

    if (!response.ok || data.error) {
      console.error('❌ URL generation error:', data);
      alert(`Failed to generate download link: ${data.error || data.message || 'Unknown error'}`);
      return;
    }

    const downloadUrl = `https://bfffxyrfgvgprxjgqrvf.supabase.co${data.signedURL}`;
    console.log('✅ Generated download URL:', downloadUrl);

    const emailData = {
      to_email: appointment.client_email,
      to_name: appointment.client_name || 'Valued Customer',
      subject: `Receipt for Your Appointment - ${payment.invoice_number}`,
      message: `Here is your invoice as requested:

Service: ${appointment.service_name}
Date: ${new Date(appointment.date).toLocaleDateString()}
Amount: £${payment.amount?.toFixed(2)}
Payment Method: ${payment.payment_method === 'card' ? 'Card' : 'Cash'}
Invoice Number: ${payment.invoice_number}

Download your invoice here:
${downloadUrl}

(Link valid for 7 days)

Best wishes,
${businessSettings.companyName || 'The Team'}`,
      customTemplate: businessSettings.emailTemplateHtml,
      companyName: businessSettings.companyName,
      adminEmail: businessSettings.adminEmail
    };

    await sendEmail('invoice', emailData);
    console.log('✅ Invoice email sent');
    alert('✅ Invoice sent to client!');

  } catch (error) {
    console.error('❌ Error sending invoice email:', error);
    console.error('❌ Error stack:', error.stack);
    alert(`Failed to send invoice: ${error.message}`);
  }
};

    const addService = async () => {
  if (!newService.name.trim()) return;
  
  console.log('addService called');
  
  const serviceData = {
    name: newService.name,
    duration: newService.duration,
    price: newService.price,
    organization_id: CURRENT_ORG_ID  // ← Make sure this is included!
  };
  
  console.log('Attempting to insert service:', serviceData);
  
  try {
    const response = await fetch(
      'https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/services',
      {
        method: 'POST',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation'
        },
        body: JSON.stringify(serviceData)
      }
    );
    
    const data = await response.json();
    
    if (response.ok && data) {
      console.log('✅ Service created:', data);
      setServices([...services, data[0]]);
      setShowServiceModal(false);
      setNewService({ name: '', duration: 60, price: 0 });
    } else {
      console.error('❌ Error creating service:', data);
      alert('Error creating service: ' + (data.message || 'Unknown error'));
    }
  } catch (error) {
    console.error('❌ Error creating service:', error);
    alert('Error creating service');
  }
};

    const updateService = async (id, field, value) => {
  // Update local state immediately for responsive UI
  setServices(services.map(s =>
    s.id === id ? { ...s, [field]: value } : s
  ));

  // Then update database
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;

    if (!accessToken) {
      console.error('No access token available');
      await loadData(); // Reload to revert optimistic update
      return;
    }

    const response = await fetch(
      `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/services?id=eq.${id}`,
      {
        method: 'PATCH',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [field]: value })
      }
    );

    if (!response.ok) {
      const data = await response.json();
      console.error('Error updating service:', data);
      await loadData(); // Reload to revert optimistic update
    } else {
      console.log('✅ Service updated');
    }
  } catch (error) {
    console.error('Error updating service:', error);
    await loadData(); // Reload to revert optimistic update
  }
};

    const deleteService = async (id) => {
  if (!window.confirm('Are you sure you want to delete this service?')) return;
  try {
    const response = await fetch(
      `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/services?id=eq.${id}`,
      {
        method: 'DELETE',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (response.ok) {
      console.log('✅ Service deleted');
      // Remove from local state immediately
      setServices(services.filter(s => s.id !== id));
    } else {
      const error = await response.json();
      console.error('❌ Error deleting service:', error);
      alert('Error deleting service');
    }
  } catch (error) {
    console.error('❌ Error deleting service:', error);
    alert('Error deleting service');
  }
};

    const addFormField = async () => {
  if (!newField.label) {
    alert('Please enter a field label');
    return;
  }

  // Check for duplicate field
  const existingField = allFormFields.find(f => 
    f.label.toLowerCase() === newField.label.toLowerCase() && 
    f.type === newField.type
  );

  if (existingField) {
    alert(`A ${newField.type} field named "${newField.label}" already exists. Please choose a different name.`);
    return;
  }

  try {
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;

    if (!accessToken) {
      alert('Session expired. Please refresh and try again.');
      return;
    }

    // Create a shared field (no form_id, can be used anywhere)
    const response = await fetch(
      'https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/enquiry_form_fields',
      {
        method: 'POST',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation'
        },
        body: JSON.stringify({
          label: newField.label,
          type: newField.type,
          required: false, // Required status controlled by Booking Form Config
          help_text: newField.helpText || null,
          options: newField.type === 'select' ? ['Option 1', 'Option 2'] : null,
          display_order: allFormFields.length + 1,
          form_id: 4, // Shared fields form
          organization_id: CURRENT_ORG_ID
        })
      }
    );

    const data = await response.json();

    if (response.ok && !data.error) {
      console.log('✅ Field created');
      setAllFormFields([...allFormFields, data[0]]);
      setNewField({ label: '', type: 'text', required: false, helpText: '' });
      setShowAddField(false);
      alert('Field added successfully!');
    } else {
      console.error('Error adding field:', data);
      alert('Error adding field');
    }
  } catch (error) {
    console.error('Error adding field:', error);
    alert('Error adding field');
  }
};

    const deleteFormField = async (id) => {
  if (!window.confirm('Are you sure you want to delete this field?')) return;

  try {
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;

    if (!accessToken) {
      alert('Session expired. Please refresh and try again.');
      return;
    }

    const response = await fetch(
      `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/form_fields?id=eq.${id}`,
      {
        method: 'DELETE',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.ok) {
      console.log('✅ Field deleted');
      setFormFields(formFields.filter(f => f.id !== id));
      alert('Field deleted successfully');
    } else {
      const data = await response.json();
      console.error('Error deleting field:', data);
      alert('Error deleting field');
    }
  } catch (error) {
    console.error('Error deleting field:', error);
    alert('Error deleting field');
  }
};

    const toggleFieldRequired = async (id) => {
      const field = formFields.find(f => f.id === id);
      if (!field) return;

      try {
        const { error } = await supabase
          .from('form_fields')
          .update({ required: !field.required })
          .eq('id', id);

        if (error) {
          console.error('Error updating field:', error);
          alert('Error updating field');
        } else {
          setFormFields(formFields.map(f => f.id === id ? { ...f, required: !f.required } : f));
        }
      } catch (error) {
        console.error('Error updating field:', error);
        alert('Error updating field');
      }
    };

    const addManualClient = async (e) => {
  e.preventDefault();
  
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;

    if (!accessToken) {
      alert('Session expired. Please refresh and try again.');
      return;
    }

    const response = await fetch(
      'https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/clients',
      {
        method: 'POST',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation'
        },
        body: JSON.stringify({
          owner_name: newClientForm[1],
          email: newClientForm[2],
          pet_name: newClientForm[4],
          form_data: newClientForm,
          appointment_count: 0,
          services: [],
          organization_id: CURRENT_ORG_ID
        })
      }
    );

    const data = await response.json();

    if (response.ok && !data.error) {
      console.log('✅ Client added');
      await loadData();
      setNewClientForm({});
      setShowAddClient(false);
      alert('Client added successfully!');
    } else {
      console.error('Error adding client:', data);
      alert('Error adding client');
    }
  } catch (error) {
    console.error('Error adding client:', error);
    alert('Error adding client');
  }
};

    const addManualAppointment = async (appointmentData) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;

    if (!accessToken) {
      alert('Session expired. Please refresh and try again.');
      return;
    }

    const client = clients.find(c => c.id === parseInt(appointmentData.clientId));
    if (!client) return;

    const selectedService = services.find(s => s.id === parseInt(appointmentData.service));
    const serviceName = selectedService?.name;

    // Insert appointment
    const apptResponse = await fetch(
      'https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/appointments',
      {
        method: 'POST',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation'
        },
        body: JSON.stringify({
          service_id: appointmentData.service,
          service_name: serviceName,
          date: appointmentData.date,
          time: appointmentData.time,
          client_email: client.email,
          client_name: client.owner_name,
          pet_name: client.pet_name,
          phone: client.form_data?.[3],
          pet_breed: client.form_data?.[6],
          special_requirements: client.form_data?.[7] || '',
          form_data: client.form_data,
          organization_id: CURRENT_ORG_ID
        })
      }
    );

    if (!apptResponse.ok) {
      const data = await apptResponse.json();
      console.error('Error adding appointment:', data);
      return;
    }

    // Update client
    const updatedServices = [...(client.services || []), { name: serviceName, date: new Date(appointmentData.date) }];
    
    const clientResponse = await fetch(
      `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/clients?id=eq.${client.id}`,
      {
        method: 'PATCH',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          last_visit: new Date(appointmentData.date).toISOString(),
          appointment_count: client.appointment_count + 1,
          services: updatedServices
        })
      }
    );

    if (clientResponse.ok) {
      console.log('✅ Appointment and client updated');
      await loadData();
    } else {
      const data = await clientResponse.json();
      console.error('Error updating client:', data);
    }
  } catch (error) {
    console.error('Error adding appointment:', error);
  }
};

    const switchToClientMode = () => { setUserMode('client'); };

    const handleLogoUpload = async (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = async () => {
          const logoData = reader.result;
          setBusinessSettings({ ...businessSettings, logo: logoData });

          // Save to Supabase
          try {
            const { error } = await supabase
              .from('business_settings')
              .update({ logo_url: logoData })
              .eq('organization_id', CURRENT_ORG_ID);

            if (error) {
              console.error('Error saving logo:', error);
              alert('Error saving logo');
            }
          } catch (error) {
            console.error('Error saving logo:', error);
          }
        };
        reader.readAsDataURL(file);
      }
    };

    const handlePasswordReset = (e) => {
      e.preventDefault();
      if (passwordForm.newPassword !== passwordForm.confirmPassword) {
        alert('New passwords do not match!');
        return;
      }
      if (passwordForm.newPassword.length < 6) {
        alert('Password must be at least 6 characters long');
        return;
      }
      // In production, you would update password via Supabase auth here
      alert('Password updated successfully!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    };

    const saveCompanyName = async () => {
      setBusinessSettings({ ...businessSettings, companyName: tempCompanyName });
      setEditingCompanyName(false);

      // Save to Supabase
      try {
        const { error } = await supabase
          .from('business_settings')
          .update({ company_name: tempCompanyName })
          .eq('organization_id', CURRENT_ORG_ID);

        if (error) {
          console.error('Error saving company name:', error);
          alert('Error saving company name');
        }
      } catch (error) {
        console.error('Error saving company name:', error);
      }
    };

    const startEditingCompanyName = () => {
      setTempCompanyName(businessSettings.companyName);
      setEditingCompanyName(true);
    };

    const saveAdminInfo = async () => {
      try {
        const { error } = await supabase
          .from('business_settings')
          .update({
            admin_name: businessSettings.adminName,
            admin_email: businessSettings.adminEmail
          })
          .eq('organization_id', CURRENT_ORG_ID);

        if (error) {
          console.error('Error saving admin info:', error);
          alert('Error saving admin information');
        } else {
          alert('Admin information updated successfully!');
        }
      } catch (error) {
        console.error('Error saving admin info:', error);
        alert('Error saving admin information');
      }
    };

    const saveRedirectUrl = async () => {
      try {
        const { error } = await supabase
          .from('business_settings')
          .update({
            redirect_url: businessSettings.redirectUrl,
            privacy_policy_url: businessSettings.privacyPolicyUrl
          })
          .eq('organization_id', CURRENT_ORG_ID);

        if (error) {
          console.error('Error saving settings:', error);
          alert('Error saving settings');
        } else {
          alert('Settings saved successfully!');
        }
      } catch (error) {
        console.error('Error saving settings:', error);
        alert('Error saving settings');
      }
    };

    const saveEmailSettings = async () => {
      try {
        const { error } = await supabase
          .from('email_settings')
          .update({
            confirmation_enabled: emailSettings.confirmationEnabled,
            confirmation_subject: emailSettings.confirmationSubject,
            confirmation_body: emailSettings.confirmationBody,
            reminder_enabled: emailSettings.reminderEnabled,
            reminder_days: emailSettings.reminderDays,
            reminder_subject: emailSettings.reminderSubject,
            reminder_body: emailSettings.reminderBody,
            follow_up_enabled: emailSettings.followUpEnabled,
            follow_up_days: emailSettings.followUpDays,
            follow_up_subject: emailSettings.followUpSubject,
            follow_up_body: emailSettings.followUpBody,
            rebooking_enabled: emailSettings.rebookingEnabled,
            rebooking_days: emailSettings.rebookingDays,
            rebooking_subject: emailSettings.rebookingSubject,
            rebooking_body: emailSettings.rebookingBody
          })
          .eq('organization_id', CURRENT_ORG_ID);

        if (error) {
          console.error('Error saving email settings:', error);
          alert('Error saving email settings');
        } else {
          alert('Email settings saved successfully!');
        }
      } catch (error) {
        console.error('Error saving email settings:', error);
        alert('Error saving email settings');
      }
    };

    const openFollowUpModal = (appointment) => {
      // Extract all placeholder data
      const ownerName = appointment.client_name || appointment.form_data?.[1] || '';
      const petName = appointment.pet_name || appointment.form_data?.[4] || '';
      const email = appointment.client_email || '';
      const phone = appointment.phone || appointment.form_data?.[3] || '';
      const petBreed = appointment.pet_breed || appointment.form_data?.[6] || '';
      const specialRequirements = appointment.special_requirements || '';

      // Pre-fill with follow-up template
      const subject = replacePlaceholders(emailSettings.followUpSubject, {
        date: appointment.date,
        time: appointment.time,
        serviceName: appointment.service_name,
        ownerName,
        petName,
        email,
        phone,
        petBreed,
        specialRequirements,
        formData: appointment.form_data
      });

      const message = replacePlaceholders(emailSettings.followUpBody, {
        date: appointment.date,
        time: appointment.time,
        serviceName: appointment.service_name,
        ownerName,
        petName,
        email,
        phone,
        petBreed,
        specialRequirements,
        formData: appointment.form_data
      });

      setFollowUpEmail({ subject, message });
      setSelectedTemplateId(''); // Reset template selection
      setFollowUpModal(appointment);
    };

    const loadEmailTemplate = (templateId) => {
      if (!followUpModal) return;

      // Extract all placeholder data
      const ownerName = followUpModal.client_name || followUpModal.form_data?.[1] || '';
      const petName = followUpModal.pet_name || followUpModal.form_data?.[4] || '';
      const email = followUpModal.client_email || '';
      const phone = followUpModal.phone || followUpModal.form_data?.[3] || '';
      const petBreed = followUpModal.pet_breed || followUpModal.form_data?.[6] || '';
      const specialRequirements = followUpModal.special_requirements || '';

      const placeholderData = {
        date: followUpModal.date,
        time: followUpModal.time,
        serviceName: followUpModal.service_name,
        ownerName,
        petName,
        email,
        phone,
        petBreed,
        specialRequirements,
        formData: followUpModal.form_data
      };

      if (templateId === 'followup') {
        // Load default follow-up template
        const subject = replacePlaceholders(emailSettings.followUpSubject, placeholderData);
        const message = replacePlaceholders(emailSettings.followUpBody, placeholderData);
        setFollowUpEmail({ subject, message });
      } else if (templateId === 'rebooking') {
        // Load rebooking template
        const subject = replacePlaceholders(emailSettings.rebookingSubject, placeholderData);
        const message = replacePlaceholders(emailSettings.rebookingBody, placeholderData);
        setFollowUpEmail({ subject, message });
      } else {
        // Load custom template
        const template = customEmailTemplates.find(t => t.id === parseInt(templateId));
        if (template) {
          const subject = replacePlaceholders(template.subject, placeholderData);
          const message = replacePlaceholders(template.body, placeholderData);
          setFollowUpEmail({ subject, message });
        }
      }
      setSelectedTemplateId(templateId);
    };

    const addCustomTemplate = async () => {
  if (!newTemplate.name || !newTemplate.subject) {
    alert('Please provide a template name and subject');
    return;
  }

  try {
    // Get the session token
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;

    if (!accessToken) {
      alert('Session expired. Please refresh and try again.');
      return;
    }

    // Check if we're editing (has id) or creating new
    const isEditing = !!newTemplate.id;
    const url = isEditing
      ? `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/custom_email_templates?id=eq.${newTemplate.id}`
      : 'https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/custom_email_templates';

    const response = await fetch(url, {
      method: isEditing ? 'PATCH' : 'POST',
      headers: {
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify({
        name: newTemplate.name,
        subject: newTemplate.subject,
        body: newTemplate.body,
        organization_id: CURRENT_ORG_ID
      })
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      console.error('Error saving template:', data);
      alert('Error saving template');
    } else {
      await loadData();
      setNewTemplate({ name: '', subject: '', body: '' });
      setShowAddTemplate(false);
      alert(isEditing ? 'Template updated successfully!' : 'Template created successfully!');
    }
  } catch (error) {
    console.error('Error saving template:', error);
    alert('Error saving template');
  }
};

    const deleteCustomTemplate = async (id) => {
      if (!window.confirm('Are you sure you want to delete this template?')) return;

      try {
        const { error } = await supabase
          .from('custom_email_templates')
          .delete()
          .eq('id', id);

        if (error) {
          console.error('Error deleting template:', error);
          alert('Error deleting template');
        } else {
          await loadData();
        }
      } catch (error) {
        console.error('Error deleting template:', error);
        alert('Error deleting template');
      }
    };

    const deleteClientData = async (clientId) => {
  if (!window.confirm('Are you sure you want to permanently delete this client and all their data? This action cannot be undone.')) return;

  try {
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;

    if (!accessToken) {
      alert('Session expired. Please refresh and try again.');
      return;
    }

    const client = clients.find(c => c.id === clientId);
    if (!client) return;

    // Delete client appointments first
    const apptResponse = await fetch(
      `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/appointments?client_email=eq.${encodeURIComponent(client.email)}`,
      {
        method: 'DELETE',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!apptResponse.ok) {
      const data = await apptResponse.json();
      console.error('Error deleting appointments:', data);
    }

    // Delete client record
    const clientResponse = await fetch(
      `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/clients?id=eq.${clientId}`,
      {
        method: 'DELETE',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (clientResponse.ok) {
      console.log('✅ Client data deleted');
      await loadData();
      alert('Client data has been permanently deleted');
    } else {
      const data = await clientResponse.json();
      console.error('Error deleting client:', data);
      alert('Error deleting client data');
    }
  } catch (error) {
    console.error('Error deleting client:', error);
    alert('Error deleting client data');
  }
};

    const openClientProfile = async (client) => {
  setSelectedClient(client);
  setEditingClient({ ...client });

  console.log('📋 Loading appointments for client:', client.email);

  // Load appointments for this client
  const { data: visits, error: visitsError } = await supabase
    .from('appointments')
    .select('*')
    .eq('client_email', client.email)
    .neq('task_type', 'enquiry')
    .order('date', { ascending: false });

  if (visitsError) {
    console.error('❌ Error loading appointments:', visitsError);
    setClientVisitHistory([]);
  } else {
    console.log('✅ Appointments loaded:', visits?.length || 0);

    // Load payment data for these appointments
    if (visits && visits.length > 0) {
      const appointmentIds = visits.map(v => v.id);
      const { data: payments, error: paymentsError } = await supabase
        .from('payments')
        .select('*')
        .in('appointment_id', appointmentIds);

      if (paymentsError) {
        console.error('❌ Error loading payments:', paymentsError);
      } else {
        console.log('✅ Payments loaded:', payments?.length || 0);

        // Attach payment data to appointments
        const visitsWithPayments = visits.map(visit => {
          const payment = payments?.find(p => p.appointment_id === visit.id);
          return { ...visit, payment };
        });

        setClientVisitHistory(visitsWithPayments);
      }
    } else {
      setClientVisitHistory(visits || []);
    }
  }

  // Load enquiry form submissions for this client
  console.log('📋 Loading enquiry submissions for client:', client.email);
  const { data: enquiries, error: enquiriesError } = await supabase
    .from('appointments')
    .select('*')
    .eq('client_email', client.email)
    .eq('task_type', 'enquiry')
    .order('created_at', { ascending: false });

  if (enquiriesError) {
    console.error('❌ Error loading enquiries:', enquiriesError);
    setClientEnquiries([]);
  } else {
    console.log('✅ Enquiries loaded:', enquiries?.length || 0);
    setClientEnquiries(enquiries || []);
    
    // Load ALL form fields (both booking and enquiry forms) for this organization
    console.log('📋 Loading all form fields for complete client data...');
    const { data: allFields } = await supabase
      .from('enquiry_form_fields')
      .select('*')
      .order('form_id');
    
    if (allFields) {
      console.log('✅ All form fields loaded:', allFields.length);
      // Merge with existing formFields but don't duplicate
      setFormFields(prevFields => {
        const existingIds = new Set(prevFields.map(f => f.id));
        const newFields = allFields.filter(f => !existingIds.has(f.id));
        return [...prevFields, ...newFields];
      });
    }
  }
};

    const updateClientInfo = async () => {
      if (!editingClient) return;

      try {
        // Update client record
        const { error } = await supabase
          .from('clients')
          .update({
            owner_name: editingClient.owner_name,
            email: editingClient.email,
            pet_name: editingClient.pet_name,
            form_data: editingClient.form_data,
            pet_photo_url: editingClient.pet_photo_url
          })
          .eq('id', editingClient.id);

        if (error) throw error;

        // Also update appointments with this client's email
        await supabase
          .from('appointments')
          .update({
            client_email: editingClient.email,
            client_name: editingClient.owner_name
          })
          .eq('client_email', selectedClient.email);

        alert('Client updated successfully!');
        await loadData();
        setSelectedClient(null);
        setEditingClient(null);
      } catch (error) {
        console.error('Error updating client:', error);
        alert('Error updating client information');
      }
    };

    const uploadPetPhoto = async (file) => {
      if (!file || !editingClient) return;

      try {
        // Convert to base64
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64String = reader.result;

          // Update editing client with photo
          setEditingClient({ ...editingClient, pet_photo_url: base64String });

          // Save to database
          const { error } = await supabase
            .from('clients')
            .update({ pet_photo_url: base64String })
            .eq('id', editingClient.id);

          if (error) throw error;

          alert('Pet photo uploaded successfully!');
          await loadData();
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Error uploading pet photo:', error);
        alert('Error uploading photo');
      }
    };

    // Closure date management
const addClosureDate = async () => {
  if (!newClosure.startDate) {
    alert('Please select a start date');
    return;
  }

  try {
    const startDate = new Date(newClosure.startDate);
    const endDate = newClosure.endDate ? new Date(newClosure.endDate) : startDate;

    // Generate array of dates between start and end
    const datesToAdd = [];
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      datesToAdd.push({
        closure_date: currentDate.toISOString().split('T')[0],
        reason: newClosure.reason || 'Closed',
        organization_id: CURRENT_ORG_ID
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Get the session token
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;

    console.log('🔑 Session token:', accessToken ? 'exists' : 'missing');

    if (!accessToken) {
      alert('Session expired. Please refresh and try again.');
      return;
    }

    const response = await fetch(
      'https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/closure_dates',
      {
        method: 'POST',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation'
        },
        body: JSON.stringify(datesToAdd)
      }
    );

const data = await response.json();

if (!response.ok || data.error) {
  console.error('Error adding closure:', data);
  console.error('Response status:', response.status);
  console.error('Data to add:', datesToAdd);
  alert(`Error adding closure date(s): ${data.message || data.error || 'Unknown error'}`);
} else {
  await loadData();
  setNewClosure({ startDate: '', endDate: '', reason: '' });
  setShowAddClosure(false);
  const daysAdded = datesToAdd.length;
  alert(`Successfully added ${daysAdded} closure day${daysAdded > 1 ? 's' : ''}!`);
}
      } catch (error) {
        console.error('Error adding closure:', error);
        alert('Error adding closure date(s)');
      }
    };

    const deleteClosureDate = async (id) => {
      if (!window.confirm('Remove this closure date?')) return;

      try {
        const response = await fetch(
          `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/closure_dates?id=eq.${id}`,
          {
            method: 'DELETE',
            headers: {
              'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          const data = await response.json();
          console.error('Error deleting closure:', data);
          alert('Error deleting closure date');
        } else {
          console.log('✅ Closure date deleted');
          await loadData();
        }
      } catch (error) {
        console.error('Error deleting closure:', error);
        alert('Error deleting closure date');
      }
    };

    // Check if a date is a closure date
    const isClosureDate = (dateString) => {
      return closureDates.some(closure => closure.closure_date === dateString);
    };

    // Calendar helper functions
    const navigateCalendar = (direction) => {
      const newDate = new Date(currentDate);
      if (calendarView === 'day') {
        newDate.setDate(newDate.getDate() + direction);
      } else if (calendarView === 'week') {
        newDate.setDate(newDate.getDate() + (direction * 7));
      } else {
        newDate.setMonth(newDate.getMonth() + direction);
      }
      setCurrentDate(newDate);
    };

    const goToToday = () => {
      setCurrentDate(new Date());
    };

    const getDaysInMonth = (date) => {
      const year = date.getFullYear();
      const month = date.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const daysInMonth = lastDay.getDate();
      const startingDayOfWeek = firstDay.getDay();

      return { daysInMonth, startingDayOfWeek, year, month };
    };

    const getWeekDays = (date) => {
      const startOfWeek = new Date(date);
      const day = startOfWeek.getDay();
      const diff = startOfWeek.getDate() - day;
      startOfWeek.setDate(diff);

      const days = [];
      for (let i = 0; i < 7; i++) {
        const currentDay = new Date(startOfWeek);
        currentDay.setDate(startOfWeek.getDate() + i);
        days.push(currentDay);
      }
      return days;
    };

    const getAppointmentsForDate = (date) => {
      const dateString = date.toISOString().split('T')[0];
      return appointments.filter(appt => appt.date === dateString && appt.task_type !== 'enquiry');
    };

    const formatCalendarDate = () => {
      if (calendarView === 'day') {
        return currentDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
      } else if (calendarView === 'week') {
        const weekDays = getWeekDays(currentDate);
        return `${weekDays[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekDays[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      } else {
        return currentDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      }
    };

    // Generate time slots for day/week view (8 AM to 6 PM in 30-minute increments)
    const generateCalendarTimeSlots = () => {
      const slots = [];
      for (let hour = 8; hour < 18; hour++) {
        slots.push(`${hour.toString().padStart(2, '0')}:00`);
        slots.push(`${hour.toString().padStart(2, '0')}:30`);
      }
      return slots;
    };

    // Check if appointment overlaps with time slot
    const isAppointmentInSlot = (appointment, slotTime) => {
      const [slotHour, slotMin] = slotTime.split(':').map(Number);
      const slotMinutes = slotHour * 60 + slotMin;

      const [apptHour, apptMin] = appointment.time.split(':').map(Number);
      const apptStartMinutes = apptHour * 60 + apptMin;

      // Get service duration
      const service = services.find(s => s.id === appointment.service_id);
      const duration = service?.duration || 60;
      const apptEndMinutes = apptStartMinutes + duration;

      // Check if slot falls within appointment time
      return slotMinutes >= apptStartMinutes && slotMinutes < apptEndMinutes;
    };

    // Get appointment height based on duration (for week/day view)
    const getAppointmentHeight = (appointment) => {
      const service = services.find(s => s.id === appointment.service_id);
      const duration = service?.duration || 60;
      // Each 30-minute slot is approximately 40px high
      return (duration / 30) * 40;
    };

    const sendFollowUpEmail = async () => {
      if (!followUpModal) return;

      try {
        // Build HTML for embedded images
        let messageWithImages = followUpEmail.message;

        if (emailImages.length > 0) {
          const imagesHtml = emailImages.map(img =>
            `<img src="${img.url}" alt="${img.name}" style="max-width: 400px; margin: 10px 0; border-radius: 8px;" />`
          ).join('\n');

          messageWithImages += '\n\n📸 Photos:\n' + imagesHtml;
        }

        const emailData = {
  to_email: followUpModal.client_email,
  to_name: followUpModal.form_data?.[1] || '',
  subject: followUpEmail.subject,
  message: messageWithImages,
  customTemplate: businessSettings.emailTemplateHtml,
  companyName: businessSettings.companyName,
  adminEmail: businessSettings.adminEmail,
  pet_name: followUpModal.form_data?.[4] || '',
  service_name: followUpModal.service_name,
  date: followUpModal.date,
  time: followUpModal.time
};

        await sendEmail('followup', emailData);
        alert('Follow-up email sent successfully!');
        setFollowUpModal(null);
        setFollowUpEmail({ subject: '', message: '' });
        setEmailImages([]);
      } catch (error) {
        console.error('Error sending follow-up email:', error);
        alert('Error sending email. Please try again.');
      }
    };

    const handleImageUpload = async (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;

      try {
        const uploadedImages = [];

        for (const file of files) {
          // Validate file type
          if (!file.type.startsWith('image/')) {
            alert(`${file.name} is not an image file`);
            continue;
          }

          // Validate file size (max 5MB)
          if (file.size > 5 * 1024 * 1024) {
            alert(`${file.name} is too large. Maximum size is 5MB.`);
            continue;
          }

          // Create unique filename
          const fileExt = file.name.split('.').pop();
          const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

          // Upload to Supabase Storage
          const { data, error } = await supabase.storage
            .from('email-images')
            .upload(fileName, file, {
              cacheControl: '3600',
              upsert: false
            });

          if (error) throw error;

          // Get public URL
          const { data: { publicUrl } } = supabase.storage
            .from('email-images')
            .getPublicUrl(fileName);

          uploadedImages.push({
            name: file.name,
            url: publicUrl,
            path: fileName
          });
        }

        setEmailImages([...emailImages, ...uploadedImages]);
      } catch (error) {
        console.error('Error uploading images:', error);
        alert('Error uploading images. Please try again.');
      }
    };

    const removeImage = async (index) => {
      const image = emailImages[index];

      try {
        // Delete from storage
        await supabase.storage
          .from('email-images')
          .remove([image.path]);

        // Remove from state
        setEmailImages(emailImages.filter((_, i) => i !== index));
      } catch (error) {
        console.error('Error removing image:', error);
        alert('Error removing image.');
      }
    };

    const exportClientsToCSV = () => {
      // Create CSV header
      const headers = ['Owner Name', 'Email', 'Phone', 'Pet Name', 'Pet Type', 'Pet Breed', 'Last Service', 'Visits', 'First Visit', 'Last Visit'];

      // Create CSV rows
      const rows = filteredClients.map(client => [
        client.owner_name || '',
        client.email || '',
        client.form_data?.[3] || '',
        client.pet_name || '',
        client.form_data?.[5] || '',
        client.form_data?.[6] || '',
        client.services && client.services.length > 0 ? client.services[client.services.length - 1].name : '',
        client.appointment_count || 0,
        new Date(client.first_visit).toLocaleDateString(),
        new Date(client.last_visit).toLocaleDateString()
      ]);

      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `clients_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    };

    // ============== ENQUIRY FORM MANAGEMENT FUNCTIONS ==============

    const createEnquiryForm = async () => {
  if (!newEnquiryForm.name.trim()) {
    alert('Please enter a form name');
    return;
  }

  try {
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;

    if (!accessToken) {
      alert('Session expired. Please refresh and try again.');
      return;
    }

    const isEditing = !!newEnquiryForm.id;
    const url = isEditing
      ? `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/enquiry_forms?id=eq.${newEnquiryForm.id}`
      : 'https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/enquiry_forms';

    const response = await fetch(url, {
      method: isEditing ? 'PATCH' : 'POST',
      headers: {
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify({
        name: newEnquiryForm.name,
        description: newEnquiryForm.description,
        ...(isEditing ? {} : { is_active: true, organization_id: CURRENT_ORG_ID }),
        custom_email_template_id: newEnquiryForm.customEmailTemplateId,
        redirect_url: newEnquiryForm.redirectUrl
      })
    });

    const data = await response.json();

    if (response.ok && !data.error) {
      if (isEditing) {
        console.log('✅ Form updated');
        setEnquiryForms(enquiryForms.map(f => f.id === newEnquiryForm.id ? data[0] : f));
        alert('Form settings updated successfully!');
      } else {
        console.log('✅ Form created');
        setEnquiryForms([data[0], ...enquiryForms]);
        setSelectedEnquiryForm(data[0].id);
        alert('Enquiry form created successfully!');
      }
      setShowAddEnquiryForm(false);
      setNewEnquiryForm({ id: null, name: '', description: '', customEmailTemplateId: null, redirectUrl: '' });
    } else {
      console.error('Error saving form:', data);
      alert('Error saving enquiry form');
    }
  } catch (error) {
    console.error('Error saving enquiry form:', error);
    alert('Error saving enquiry form');
  }
};

    const updateEnquiryFormField = async (fieldId, updates) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;

    if (!accessToken) {
      alert('Session expired. Please refresh and try again.');
      return;
    }

    const response = await fetch(
      `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/enquiry_form_fields?id=eq.${fieldId}`,
      {
        method: 'PATCH',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      }
    );

    if (response.ok) {
      console.log('✅ Field updated successfully');
      setEnquiryFormFields(enquiryFormFields.map(f =>
        f.id === fieldId ? { ...f, ...updates } : f
      ));
    } else {
      const data = await response.json();
      console.error('Error updating field:', data);
      alert('Error updating field');
    }
  } catch (error) {
    console.error('Error updating enquiry form field:', error);
    alert('Error updating field');
  }
};

const updateEnquiryForm = async (formId, updates) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;

    if (!accessToken) {
      alert('Session expired. Please refresh and try again.');
      return;
    }

    const response = await fetch(
      `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/enquiry_forms?id=eq.${formId}`,
      {
        method: 'PATCH',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      }
    );

    if (response.ok) {
      console.log('✅ Form updated successfully');
      setEnquiryForms(enquiryForms.map(f =>
        f.id === formId ? { ...f, ...updates } : f
      ));
    } else {
      const data = await response.json();
      console.error('Error updating form:', data);
      alert('Error updating form');
    }
  } catch (error) {
    console.error('Error updating enquiry form:', error);
    alert('Error updating form');
  }
};

    const deleteEnquiryForm = async (formId) => {
  if (!window.confirm('Are you sure you want to delete this enquiry form? This cannot be undone.')) {
    return;
  }

  try {
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;

    if (!accessToken) {
      alert('Session expired. Please refresh and try again.');
      return;
    }

    // Delete associated fields first
    const fieldsResponse = await fetch(
      `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/enquiry_form_fields?form_id=eq.${formId}`,
      {
        method: 'DELETE',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!fieldsResponse.ok) {
      const data = await fieldsResponse.json();
      console.error('Error deleting form fields:', data);
    }

    // Delete the form
    const formResponse = await fetch(
      `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/enquiry_forms?id=eq.${formId}`,
      {
        method: 'DELETE',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (formResponse.ok) {
      console.log('✅ Enquiry form deleted');
      setEnquiryForms(enquiryForms.filter(f => f.id !== formId));
      if (selectedEnquiryForm === formId) {
        setSelectedEnquiryForm(null);
        setEnquiryFormFields([]);
      }
      alert('Enquiry form deleted successfully');
    } else {
      const data = await formResponse.json();
      console.error('Error deleting form:', data);
      alert('Error deleting enquiry form');
    }
  } catch (error) {
    console.error('Error deleting enquiry form:', error);
    alert('Error deleting enquiry form');
  }
};
const saveBookingFormConfig = async (fields) => {
  const { data: { session } } = await supabase.auth.getSession();
  const accessToken = session?.access_token;
  
  const fieldIds = fields.map(f => f.id);
  const requiredFieldIds = fields.filter(f => f.required).map(f => f.id);
  
  await fetch(
    `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/business_settings?organization_id=eq.${CURRENT_ORG_ID}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        booking_form_field_ids: fieldIds,
        booking_form_required_field_ids: requiredFieldIds
      })
    }
  );
  
  console.log('✅ Booking form config saved');
};

    const loadEnquiryFormFields = async (formId) => {
  try {
    const response = await fetch(
      `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/enquiry_form_fields?form_id=eq.${formId}&select=*&order=display_order`,
      {
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
        }
      }
    );
    
    const data = await response.json();
    
    if (response.ok && !data.error) {
      console.log('✅ Enquiry form fields loaded:', data.length);
      setEnquiryFormFields(data || []);
    } else {
      console.error('❌ Error loading enquiry form fields:', data);
      setEnquiryFormFields([]);
    }
  } catch (error) {
    console.error('❌ Error loading enquiry form fields:', error);
    setEnquiryFormFields([]);
  }
};

    const addEnquiryFormField = async () => {
  if (!selectedEnquiryForm) {
    alert('Please select an enquiry form first');
    return;
  }

  if (newEnquiryField.useExisting && !newEnquiryField.existingFieldId) {
    alert('Please select an existing field');
    return;
  }

  if (!newEnquiryField.useExisting && !newEnquiryField.label.trim()) {
    alert('Please enter a field label');
    return;
  }

  try {
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;

    if (!accessToken) {
      alert('Session expired. Please refresh and try again.');
      return;
    }

    const maxOrder = enquiryFormFields.length > 0
      ? Math.max(...enquiryFormFields.map(f => f.display_order))
      : 0;

    // When using existing field, get the field details from allFormFields
    let fieldData;
    if (newEnquiryField.useExisting) {
      const existingField = allFormFields.find(f => f.id === newEnquiryField.existingFieldId);
      if (!existingField) {
        alert('Selected field not found');
        return;
      }
      fieldData = {
        form_id: selectedEnquiryForm,
        label: existingField.label,
        type: existingField.type,
        required: existingField.required,
        help_text: existingField.help_text,
        options: existingField.options,
        display_order: maxOrder + 1,
        booking_field_id: existingField.id,
        organization_id: CURRENT_ORG_ID
      };
    } else {
      fieldData = {
        form_id: selectedEnquiryForm,
        label: newEnquiryField.label,
        type: newEnquiryField.type,
        required: newEnquiryField.required,
        options: newEnquiryField.type === 'select' ? editingEnquiryOptions : null,
        display_order: maxOrder + 1,
        booking_field_id: null,
        organization_id: CURRENT_ORG_ID
      };
    }

    const response = await fetch(
      'https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/enquiry_form_fields',
      {
        method: 'POST',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation'
        },
        body: JSON.stringify(fieldData)
      }
    );

    const data = await response.json();

    if (response.ok && !data.error) {
      console.log('✅ Field added successfully');
      setEnquiryFormFields([...enquiryFormFields, data[0]]);
      setShowAddEnquiryField(false);
      setNewEnquiryField({ label: '', type: 'text', required: false, useExisting: false, existingFieldId: null });
      setEditingEnquiryOptions([]);
    } else {
      console.error('Error adding field:', data);
      alert('Error adding field');
    }
  } catch (error) {
    console.error('Error adding enquiry form field:', error);
    alert('Error adding field');
  }
};

    const deleteEnquiryFormField = async (fieldId) => {
      if (!window.confirm('Are you sure you want to delete this field?')) {
        return;
      }

      try {
        const { error } = await supabase
          .from('enquiry_form_fields')
          .delete()
          .eq('id', fieldId);

        if (error) throw error;

        setEnquiryFormFields(enquiryFormFields.filter(f => f.id !== fieldId));
      } catch (error) {
        console.error('Error deleting enquiry form field:', error);
        alert('Error deleting field');
      }
    };

    const moveEnquiryFormField = async (fieldId, direction) => {
      const currentIndex = enquiryFormFields.findIndex(f => f.id === fieldId);
      if (currentIndex === -1) return;

      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (newIndex < 0 || newIndex >= enquiryFormFields.length) return;

      const newFields = [...enquiryFormFields];
      [newFields[currentIndex], newFields[newIndex]] = [newFields[newIndex], newFields[currentIndex]];

      // Update display_order for both fields
      try {
        await supabase
          .from('enquiry_form_fields')
          .update({ display_order: newIndex })
          .eq('id', fieldId);

        await supabase
          .from('enquiry_form_fields')
          .update({ display_order: currentIndex })
          .eq('id', newFields[currentIndex].id);

        setEnquiryFormFields(newFields);
      } catch (error) {
        console.error('Error reordering fields:', error);
      }
    };

    const startEditingEnquiryField = (field) => {
      setEditingEnquiryField(field.id);
      if (field.options) {
        setEditingEnquiryOptions([...field.options]);
      } else {
        setEditingEnquiryOptions([]);
      }
    };

    const saveEnquiryFieldOptions = async (fieldId) => {
      try {
        const { error } = await supabase
          .from('enquiry_form_fields')
          .update({ options: editingEnquiryOptions })
          .eq('id', fieldId);

        if (error) throw error;

        setEnquiryFormFields(enquiryFormFields.map(f =>
          f.id === fieldId ? { ...f, options: editingEnquiryOptions } : f
        ));
        setEditingEnquiryField(null);
        setEditingEnquiryOptions([]);
      } catch (error) {
        console.error('Error saving field options:', error);
        alert('Error saving options');
      }
    };

    // ============== END ENQUIRY FORM MANAGEMENT FUNCTIONS ==============

    // ============== TASK/ENQUIRY COMPLETION FUNCTIONS ==============

    const completeAndRemoveTask = async (taskId) => {
      if (!window.confirm('Mark this task as complete and remove it from the list?')) {
        return;
      }

      try {
        const { error } = await supabase
          .from('appointments')
          .delete()
          .eq('id', taskId);

        if (error) throw error;

        // Remove from local state
        setAppointments(appointments.filter(a => a.id !== taskId));
        setSelectedTask(null); // Close modal
        alert('Task completed and removed successfully!');
      } catch (error) {
        console.error('Error removing task:', error);
        alert('Error removing task');
      }
    };

    // ============== END TASK/ENQUIRY COMPLETION FUNCTIONS ==============

    // Filter clients based on search
    const filteredClients = clients.filter(client => {
      if (!clientFilter) return true;
      const searchLower = clientFilter.toLowerCase();
      return (
        client.owner_name?.toLowerCase().includes(searchLower) ||
        client.email?.toLowerCase().includes(searchLower) ||
        client.pet_name?.toLowerCase().includes(searchLower) ||
        client.form_data?.[5]?.toLowerCase().includes(searchLower) ||
        client.form_data?.[6]?.toLowerCase().includes(searchLower)
      );
    });


    const updateBusinessHours = async (day, field, value) => {
  const newHours = { ...businessHours, [day]: { ...businessHours[day], [field]: value } };
  setBusinessHours(newHours);
  
  try {
    // Get the session token
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;

    if (!accessToken) {
      console.error('No access token available');
      alert('Session expired. Please refresh and try again.');
      return;
    }

    // First, check if a record exists for this day
    const checkResponse = await fetch(
      `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/business_hours?organization_id=eq.${CURRENT_ORG_ID}&day=eq.${day}`,
      {
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`
        }
      }
    );
    
    const existing = await checkResponse.json();
    
    if (existing && existing.length > 0) {
      // UPDATE existing record
      const response = await fetch(
        `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/business_hours?organization_id=eq.${CURRENT_ORG_ID}&day=eq.${day}`,
        {
          method: 'PATCH',
          headers: {
            'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            open: newHours[day].open,
            start_time: newHours[day].start,
            end_time: newHours[day].end
          })
        }
      );

      if (!response.ok) {
        const data = await response.json();
        console.error('Error updating business hours:', data);
        alert('Error saving business hours');
      } else {
        console.log('✅ Business hours updated for', day);
      }
    } else {
      // INSERT new record
      const response = await fetch(
        'https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/business_hours',
        {
          method: 'POST',
          headers: {
            'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'Prefer': 'return=representation'
          },
          body: JSON.stringify({
            day: day,
            open: newHours[day].open,
            start_time: newHours[day].start,
            end_time: newHours[day].end,
            organization_id: CURRENT_ORG_ID
          })
        }
      );

      if (!response.ok) {
        const data = await response.json();
        console.error('Error creating business hours:', data);
        alert('Error saving business hours');
      } else {
        console.log('✅ Business hours created for', day);
      }
    }
  } catch (error) {
    console.error('Error updating business hours:', error);
    alert('Error saving business hours');
  }
};

    const startEditingField = (field) => {
      setEditingField(field.id);
      if (field.options) setEditingOptions([...field.options]);
    };

    const updateFieldOptions = (index, value) => {
      const newOptions = [...editingOptions];
      newOptions[index] = value;
      setEditingOptions(newOptions);
    };

    const addFieldOption = () => { setEditingOptions([...editingOptions, 'New Option']); };
    const removeFieldOption = (index) => { setEditingOptions(editingOptions.filter((_, i) => i !== index)); };

    const saveFieldOptions = async (fieldId) => {
      try {
        const { error } = await supabase
          .from('form_fields')
          .update({ options: editingOptions })
          .eq('id', fieldId);

        if (error) {
          console.error('Error updating options:', error);
          alert('Error updating field options');
        } else {
          setFormFields(formFields.map(f => f.id === fieldId ? { ...f, options: editingOptions } : f));
          setEditingField(null);
          setEditingOptions([]);
          alert('Options saved successfully');
        }
      } catch (error) {
        console.error('Error updating options:', error);
        alert('Error updating field options');
      }
    };

    const cancelEditingField = () => {
      setEditingField(null);
      setEditingOptions([]);
    };

    const selectExistingClient = (clientId) => {
      setSelectedBookingClientId(clientId);
      const client = clients.find(c => c.id === parseInt(clientId));
      if (!client) {
        setBookingForm(prev => ({ ...prev, formData: {} }));
        return;
      }

      const fd = { ...(client.form_data || {}) };
      const fill = (field, value) => { if (field && value && !fd[field.id]) fd[field.id] = value; };

      const ownerField = formFields.find(f => f.label.toLowerCase().includes('owner')) ||
        formFields.find(f => f.label.toLowerCase().includes('name') && !f.label.toLowerCase().includes('pet'));

      fill(formFields.find(f => f.type === 'email'), client.email);
      fill(ownerField, client.owner_name);
      fill(formFields.find(f => f.label.toLowerCase().includes('pet') && f.label.toLowerCase().includes('name')), client.pet_name);

      setBookingForm(prev => ({ ...prev, formData: fd }));
    };

    const renderBookingForm = () => (
      <div>
        {userMode === 'admin' && (
          <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
            <label className="block text-sm font-medium text-gray-700 mb-2">Client</label>
            <div className="flex gap-2 mb-3">
              <button
                type="button"
                onClick={() => setBookingClientMode('existing')}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${bookingClientMode === 'existing' ? 'bg-purple-600 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'}`}
              >
                Existing client
              </button>
              <button
                type="button"
                onClick={() => {
                  setBookingClientMode('new');
                  setSelectedBookingClientId('');
                  setBookingClientSearch('');
                  setBookingForm({ ...bookingForm, formData: {} });
                }}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${bookingClientMode === 'new' ? 'bg-purple-600 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'}`}
              >
                New client
              </button>
            </div>

            {bookingClientMode === 'existing' ? (
              <div className="space-y-2">
                <input
                  type="text"
                  value={bookingClientSearch}
                  onChange={(e) => setBookingClientSearch(e.target.value)}
                  placeholder="Search by owner, pet or email..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <select
                  value={selectedBookingClientId}
                  onChange={(e) => selectExistingClient(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Choose a client...</option>
                  {clients
                    .filter(c => {
                      const q = bookingClientSearch.toLowerCase();
                      return !q ||
                        c.owner_name?.toLowerCase().includes(q) ||
                        c.pet_name?.toLowerCase().includes(q) ||
                        c.email?.toLowerCase().includes(q);
                    })
                    .map(c => (
                      <option key={c.id} value={c.id}>
                        {c.owner_name} – {c.pet_name} ({c.email})
                      </option>
                    ))}
                </select>
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                Enter the new client's details in "Your Information" after choosing a time. They'll be added to your client list automatically.
              </p>
            )}
          </div>
        )}

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Select Service</label>
          <select
            required
            value={bookingForm.service}
            onChange={(e) => setBookingForm({ ...bookingForm, service: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="">Choose a service...</option>
            {services.map(service => (
              <option key={service.id} value={service.id}>
                {service.name} - {service.duration} mins - £{service.price}
              </option>
            ))}
          </select>
        </div>

        {bookingForm.service && (
          <>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Date</label>
              <input
                type="date"
                required
                value={bookingForm.date}
                min={new Date().toISOString().split('T')[0]}
                onChange={(e) => setBookingForm({ ...bookingForm, date: e.target.value, time: '' })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {bookingForm.date && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Time</label>
                {userMode === 'client' && (
                  <p className="text-xs text-gray-500 mb-2">
                    Bookings need at least 24 hours' notice, so nearer slots are greyed out.
                  </p>
                )}
                {timeSlotData.closed ? (
                  <p className="text-red-600">This day is closed. Please select another date.</p>
                ) : allSlots.length > 0 ? (
                  <>
                    {availableSlots.length === 0 && (
                      <p className="text-amber-600 mb-3 text-sm">
                        ⚠️ We don't have enough availability for this service on this day. Please choose another date or a shorter service.
                      </p>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                      {allSlots.map(slot => {
                        const isAvailable = availableSlots.includes(slot);
                        return (
                          <button
                            key={slot}
                            type="button"
                            onClick={() => isAvailable && setBookingForm({ ...bookingForm, time: slot })}
                            disabled={!isAvailable}
                            className={`px-4 py-2 rounded-lg border transition-colors ${bookingForm.time === slot
                                ? 'bg-purple-600 text-white border-purple-600'
                                : isAvailable
                                  ? 'bg-white text-gray-700 border-gray-300 hover:border-purple-600 cursor-pointer'
                                  : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                              }`}
                          >
                            {slot}
                          </button>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <p className="text-gray-500">No time slots available for this date.</p>
                )}
              </div>
            )}

           {bookingForm.time && (
  <div className="border-t pt-6 mt-6">
    <h3 className="text-lg font-semibold mb-4 text-gray-800">Your Information</h3>
    {formFields.map(field => (
      <div key={field.id} className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {field.label} {field.required && <span className="text-red-500">*</span>}
        </label>
        {field.type === 'textarea' ? (
          <textarea
            required={field.required}
            value={bookingForm.formData[field.id] || ''}
            onChange={(e) => setBookingForm({
              ...bookingForm,
              formData: { ...bookingForm.formData, [field.id]: e.target.value }
            })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            rows="3"
          />
        ) : field.type === 'select' ? (
          <select
            required={field.required}
            value={bookingForm.formData[field.id] || ''}
            onChange={(e) => setBookingForm({
              ...bookingForm,
              formData: { ...bookingForm.formData, [field.id]: e.target.value }
            })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="">Select...</option>
            {field.options?.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        ) : (
          <input
            type={field.type}
            required={field.required}
            readOnly={userMode === 'admin' && bookingClientMode === 'existing' && field.type === 'email'}
            value={bookingForm.formData[field.id] || ''}
            onChange={(e) => setBookingForm({
              ...bookingForm,
              formData: { ...bookingForm.formData, [field.id]: e.target.value }
            })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        )}
        {field.help_text && (
          <p className="text-sm text-gray-500 mt-1">{field.help_text}</p>
        )}
      </div>
    ))}

                {/* Privacy Policy Consent */}
                <div className="pt-4 border-t border-gray-200">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={privacyConsent}
                      onChange={(e) => setPrivacyConsent(e.target.checked)}
                      className="mt-1 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      required
                    />
                    <span className="text-sm text-gray-700">
                      I agree to the{' '}
                      <button
                        type="button"
                        onClick={() => window.open('/privacy-policy.html', '_blank')}
                        className="text-purple-600 hover:text-purple-700 underline"
                      >
                        Privacy Policy
                      </button>
                      {' '}and consent to my personal data being stored and processed for appointment booking and service delivery purposes.
                    </span>
                  </label>
                </div>

                <button
                  onClick={handleBookingSubmit}
                  disabled={bookingSubmitting}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {bookingSubmitting ? 'Booking...' : 'Book Appointment'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
console.log('🔍 Loading check - loading:', loading);

  // TEMPORARY: Skip loading check
/*
if (loading) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading...</p>
      </div>
    </div>
  );
}
*/

    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="max-w-7xl mx-auto p-6">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-4 md:p-6">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2 md:gap-3">
                  {businessSettings.logo ? (
                    <img src={businessSettings.logo} alt="Company Logo" className="w-10 h-10 md:w-12 md:h-12 rounded-lg object-cover bg-white p-1" />
                  ) : (
                    <Scissors className="w-6 h-6 md:w-8 md:h-8 text-white" />
                  )}
                  {editingCompanyName && userMode === 'admin' ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={tempCompanyName}
                        onChange={(e) => setTempCompanyName(e.target.value)}
                        className="text-xl md:text-2xl font-bold px-2 md:px-3 py-1 rounded-lg border-2 border-white"
                        autoFocus
                      />
                      <button
                        onClick={saveCompanyName}
                        className="bg-white text-purple-600 p-2 rounded-lg hover:bg-purple-50"
                        title="Save"
                      >
                        <Check className="w-4 h-4 md:w-5 md:h-5" />
                      </button>
                      <button
                        onClick={() => setEditingCompanyName(false)}
                        className="bg-white/20 text-white p-2 rounded-lg hover:bg-white/30"
                        title="Cancel"
                      >
                        <X className="w-4 h-4 md:w-5 md:h-5" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-white">{businessSettings.companyName}</h1>
                      {userMode === 'admin' && (
                        <button
                          onClick={startEditingCompanyName}
                          className="text-white/70 hover:text-white p-1"
                          title="Edit company name"
                        >
                          <Edit2 className="w-4 h-4 md:w-5 md:h-5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {userMode === 'admin' && (
                  <div className="flex items-center gap-2 md:gap-3">
                    {userProfile && (
  <div className="relative user-menu-container">
    <button
      onClick={() => setShowUserMenu(!showUserMenu)}
      className="flex items-center gap-2 bg-white/20 text-white px-3 md:px-4 py-2 rounded-lg hover:bg-white/30 transition-colors"
    >
      <UserIcon className="w-4 h-4 md:w-5 md:h-5" />
      <span className="hidden sm:inline text-sm md:text-base">{businessSettings.adminName || userProfile.full_name || 'Admin'}</span>
    </button>

    {showUserMenu && (
      <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
        <div className="px-4 py-3 border-b border-gray-200">
          <p className="text-sm font-semibold text-gray-800 truncate">{userProfile.full_name}</p>
          <p className="text-xs text-gray-600 truncate">{user.email}</p>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-medium capitalize">
              {userProfile.role}
            </span>
            <span className="text-xs text-gray-500 truncate">
              {organization.name}
            </span>
          </div>
        </div>

        <button
          onClick={async () => {
            if (window.confirm('Are you sure you want to sign out?')) {
              await signOut();
              setShowUserMenu(false);
            }
          }}
          className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 border-t border-gray-200 transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </button>
      </div>
    )}
  </div>
)}
                    <button
                      onClick={() => setActiveTab('settings')}
                      className="bg-white/20 text-white p-2 md:p-3 rounded-lg hover:bg-white/30 transition-colors"
                      title="Settings"
                    >
                      <Settings className="w-4 h-4 md:w-5 md:h-5" />
                    </button>
                    <button
                      onClick={switchToClientMode}
                      className="bg-white text-purple-600 px-3 md:px-6 py-2 rounded-lg text-sm md:text-base font-semibold hover:bg-purple-50 transition-colors whitespace-nowrap"
                    >
                      <span className="hidden sm:inline">Preview Client View</span>
                      <span className="sm:hidden">Preview</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            {userMode === 'client' ? (
              <div className="p-4 md:p-6 lg:p-8">
                <div className="max-w-2xl mx-auto">
                  <div className="mb-6 text-center">
                    <h2 className="text-xl md:text-2xl font-bold text-gray-800">Book Your Appointment</h2>
                    <p className="text-sm md:text-base text-gray-600 mt-2">Select a service and available time slot</p>
                  </div>
                  {renderBookingForm()}
                </div>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap border-b bg-gray-50">
                  {/* Navigation Groups */}
                  {[
                    { id: 'dashboard', label: 'Dashboard', icon: Calendar, items: null },
                    {
                      id: 'appointments-group',
                      label: 'Appointments',
                      icon: Calendar,
                      items: [
                        { id: 'booking', label: 'Book', icon: Plus },
                        { id: 'appointments', label: 'Calendar', icon: Calendar }
                      ]
                    },
                    { id: 'tasks', label: 'Tasks', icon: Send, items: null },
                    { id: 'clients', label: 'Clients', icon: Users, items: null },
                    { id: 'services', label: 'Services', icon: Scissors, items: null },
                    {
                      id: 'forms-group',
                      label: 'Forms',
                      icon: Edit2,
                      items: [
                        { id: 'form', label: 'Fields', icon: Edit2 },
                        { id: 'bookingForm', label: 'Booking Form', icon: Calendar },
                        { id: 'enquiryForms', label: 'Custom Forms', icon: Settings }
                      ]
                    },
                    { id: 'hours', label: 'Business Hours', shortLabel: 'Hours', icon: Clock, items: null },
                    { id: 'emails', label: 'Emails', icon: Mail, items: null }
                  ].map(menuItem => {
                    // If it has subitems, render as dropdown
                    if (menuItem.items) {
                      const isOpen = openDropdown === menuItem.id;
                      const isActive = menuItem.items.some(item => item.id === activeTab);

                      return (
                        <div key={menuItem.id} className="relative">
                          <button
                            onClick={() => setOpenDropdown(isOpen ? null : menuItem.id)}
                            className={`flex items-center gap-1 md:gap-2 px-3 md:px-4 py-3 font-medium transition-colors whitespace-nowrap text-sm md:text-base ${isActive
                                ? 'bg-white text-purple-600 border-b-2 border-purple-600'
                                : 'text-gray-600 hover:text-purple-600'
                              }`}
                          >
                            <menuItem.icon className="w-4 h-4" />
                            <span className="hidden sm:inline">{menuItem.label}</span>
                            <span className="sm:hidden">{menuItem.label.split(' ')[0]}</span>
                            <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                          </button>

                          {/* Dropdown Menu */}
                          {isOpen && (
                            <div className="absolute top-full left-0 mt-0 bg-white border border-gray-200 rounded-b-lg shadow-lg z-50 min-w-[160px]">
                              {menuItem.items.map(subItem => (
                                <button
                                  key={subItem.id}
                                  onClick={() => {
                                    setActiveTab(subItem.id);
                                    setOpenDropdown(null);
                                  }}
                                  className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${activeTab === subItem.id
                                      ? 'bg-purple-50 text-purple-600 font-medium'
                                      : 'text-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                  <subItem.icon className="w-4 h-4" />
                                  {subItem.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    } else {
                      // Render as single tab
                      return (
                        <button
                          key={menuItem.id}
                          onClick={() => setActiveTab(menuItem.id)}
                          className={`flex items-center gap-1 md:gap-2 px-3 md:px-4 py-3 font-medium transition-colors whitespace-nowrap text-sm md:text-base ${activeTab === menuItem.id
                              ? 'bg-white text-purple-600 border-b-2 border-purple-600'
                              : 'text-gray-600 hover:text-purple-600'
                            }`}
                        >
                          <menuItem.icon className="w-4 h-4" />
                          <span className="hidden sm:inline">{menuItem.label}</span>
                          <span className="sm:hidden">{menuItem.shortLabel || menuItem.label.split(' ')[0]}</span>
                        </button>
                      );
                    }
                  })}
                </div>

                <div className="p-4 md:p-6 lg:p-8">
                  {activeTab === 'dashboard' && (
                    <div>
                      <h2 className="text-2xl font-bold mb-6 text-gray-800">Business Overview</h2>

                      {/* Metrics Grid - 2 Cards Only */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                        {/* Active Clients */}
                        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-sm font-medium opacity-90">Active Clients</div>
                            <Users className="w-5 h-5 opacity-75" />
                          </div>
                          <div className="text-3xl font-bold mb-1">{clients.length}</div>
                          <div className="text-sm opacity-90">Total registered</div>
                        </div>

                        {/* All-Time Attendance Rate */}
                        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-sm font-medium opacity-90">Attendance Rate</div>
                            <Check className="w-5 h-5 opacity-75" />
                          </div>
                          <div className="text-3xl font-bold mb-1">
                            {(() => {
                              const allAppointments = appointments.filter(a => a.task_type !== 'enquiry');
                              const attended = allAppointments.filter(a => a.attendance_status === 'attended').length;
                              const total = allAppointments.length;

                              if (total === 0) return '0%';
                              return ((attended / total) * 100).toFixed(1) + '%';
                            })()}
                          </div>
                          <div className="text-sm opacity-90">All-time conversion rate</div>
                        </div>
                      </div>

                      {/* Charts Grid */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Revenue Gauge Chart */}
                        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                            <Calendar className="w-5 h-5 text-green-600" />
                            Revenue Comparison
                          </h3>
                          <div className="h-64 flex items-center justify-center">
                            {(() => {
                              const now = new Date();
                              const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

                              const thisMonthRevenue = appointments.filter(a => {
                                const apptDate = new Date(a.date);
                                return apptDate.getMonth() === now.getMonth() &&
                                  apptDate.getFullYear() === now.getFullYear() &&
                                  a.task_type !== 'enquiry' &&
                                  a.attendance_status === 'attended';
                              }).reduce((sum, a) => {
                                const service = services.find(s => s.id === a.service_id);
                                return sum + (service?.price || 0);
                              }, 0);

                              const lastMonthRevenue = appointments.filter(a => {
                                const apptDate = new Date(a.date);
                                return apptDate.getMonth() === lastMonth.getMonth() &&
                                  apptDate.getFullYear() === lastMonth.getFullYear() &&
                                  a.task_type !== 'enquiry' &&
                                  a.attendance_status === 'attended';
                              }).reduce((sum, a) => {
                                const service = services.find(s => s.id === a.service_id);
                                return sum + (service?.price || 0);
                              }, 0);

                              const change = lastMonthRevenue === 0 ? 0 : ((thisMonthRevenue - lastMonthRevenue) / lastMonthRevenue * 100);
                              const isPositive = change >= 0;

                              return (
                                <div className="w-full text-center">
                                  <div className="mb-4">
                                    <div className="text-sm text-gray-600 mb-1">Last Month</div>
                                    <div className="text-2xl font-bold text-gray-700">£{lastMonthRevenue.toFixed(2)}</div>
                                  </div>

                                  <div className="relative h-32 flex items-center justify-center mb-4">
                                    <div className={`text-6xl font-bold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                                      {isPositive ? '↗' : '↘'} {Math.abs(change).toFixed(1)}%
                                    </div>
                                  </div>

                                  <div>
                                    <div className="text-sm text-gray-600 mb-1">This Month</div>
                                    <div className="text-2xl font-bold text-purple-600">£{thisMonthRevenue.toFixed(2)}</div>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </div>

                        {/* Bookings vs Attendance Bar Chart */}
                        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                            <Calendar className="w-5 h-5 text-blue-600" />
                            All-Time Performance
                          </h3>
                          <div className="h-64 flex items-center justify-center">
                            {(() => {
                              const allAppointments = appointments.filter(a => a.task_type !== 'enquiry');
                              const totalBookings = allAppointments.length;
                              const totalAttended = allAppointments.filter(a => a.attendance_status === 'attended').length;
                              const noShows = totalBookings - totalAttended;

                              const maxValue = Math.max(totalBookings, totalAttended);
                              const bookingHeight = maxValue > 0 ? (totalBookings / maxValue) * 100 : 0;
                              const attendedHeight = maxValue > 0 ? (totalAttended / maxValue) * 100 : 0;

                              return (
                                <div className="w-full h-full flex items-end justify-center gap-12">
                                  <div className="flex flex-col items-center">
                                    <div
                                      className="w-24 bg-gradient-to-t from-blue-500 to-blue-400 rounded-t-lg flex items-end justify-center pb-2"
                                      style={{ height: `${bookingHeight}%`, minHeight: '40px' }}
                                    >
                                      <span className="text-white font-bold text-xl">{totalBookings}</span>
                                    </div>
                                    <div className="mt-3 text-center">
                                      <div className="text-sm font-medium text-gray-700">Total Bookings</div>
                                    </div>
                                  </div>

                                  <div className="flex flex-col items-center">
                                    <div
                                      className="w-24 bg-gradient-to-t from-green-500 to-green-400 rounded-t-lg flex items-end justify-center pb-2"
                                      style={{ height: `${attendedHeight}%`, minHeight: '40px' }}
                                    >
                                      <span className="text-white font-bold text-xl">{totalAttended}</span>
                                    </div>
                                    <div className="mt-3 text-center">
                                      <div className="text-sm font-medium text-gray-700">Attended</div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </div>

                        {/* UTM Source Pie Chart */}
                        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                            <Mail className="w-5 h-5 text-purple-600" />
                            Booking Sources
                          </h3>
                          <div className="h-64">
                            {(() => {
                              const utmSources = {};
                              let directBookings = 0;

                              appointments.filter(a => a.task_type !== 'enquiry').forEach(a => {
                                if (a.utm_source) {
                                  utmSources[a.utm_source] = (utmSources[a.utm_source] || 0) + 1;
                                } else {
                                  directBookings++;
                                }
                              });

                              if (directBookings > 0) {
                                utmSources['Direct'] = directBookings;
                              }

                              const sources = Object.entries(utmSources);
                              const total = sources.reduce((sum, [_, count]) => sum + count, 0);

                              if (total === 0) {
                                return (
                                  <div className="h-full flex items-center justify-center text-gray-500">
                                    No booking data yet
                                  </div>
                                );
                              }

                              const colors = [
                                '#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
                                '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'
                              ];

                              return (
                                <div className="h-full flex flex-col">
                                  <div className="flex-1 flex items-center justify-center">
                                    <svg viewBox="0 0 200 200" className="w-48 h-48">
                                      {(() => {
                                        let currentAngle = -90;
                                        return sources.map(([source, count], index) => {
                                          const percentage = (count / total) * 100;
                                          const angle = (percentage / 100) * 360;
                                          const startAngle = currentAngle;
                                          const endAngle = currentAngle + angle;

                                          const x1 = 100 + 80 * Math.cos((startAngle * Math.PI) / 180);
                                          const y1 = 100 + 80 * Math.sin((startAngle * Math.PI) / 180);
                                          const x2 = 100 + 80 * Math.cos((endAngle * Math.PI) / 180);
                                          const y2 = 100 + 80 * Math.sin((endAngle * Math.PI) / 180);

                                          const largeArc = angle > 180 ? 1 : 0;
                                          const path = `M 100 100 L ${x1} ${y1} A 80 80 0 ${largeArc} 1 ${x2} ${y2} Z`;

                                          currentAngle = endAngle;

                                          return (
                                            <path
                                              key={source}
                                              d={path}
                                              fill={colors[index % colors.length]}
                                              className="hover:opacity-80 transition-opacity"
                                            />
                                          );
                                        });
                                      })()}
                                    </svg>
                                  </div>
                                  <div className="mt-4 grid grid-cols-2 gap-2">
                                    {sources.map(([source, count], index) => {
                                      const percentage = ((count / total) * 100).toFixed(1);
                                      return (
                                        <div key={source} className="flex items-center gap-2 text-sm">
                                          <div
                                            className="w-3 h-3 rounded-full flex-shrink-0"
                                            style={{ backgroundColor: colors[index % colors.length] }}
                                          />
                                          <span className="text-gray-700 truncate">
                                            {source} ({percentage}%)
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </div>

                        {/* Top 3 Services */}
                        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                            <Scissors className="w-5 h-5 text-purple-600" />
                            Top 3 Services
                          </h3>
                          <div className="space-y-3">
                            {(() => {
                              const now = new Date();
                              const thisMonthAppointments = appointments.filter(a => {
                                const apptDate = new Date(a.date);
                                return apptDate.getMonth() === now.getMonth() &&
                                  apptDate.getFullYear() === now.getFullYear() &&
                                  a.task_type !== 'enquiry';
                              });

                              // Count bookings per service
                              const serviceCounts = {};
                              thisMonthAppointments.forEach(a => {
                                const service = services.find(s => s.id === a.service_id);
                                if (service) {
                                  if (!serviceCounts[service.id]) {
                                    serviceCounts[service.id] = {
                                      name: service.name,
                                      count: 0,
                                      revenue: 0,
                                      price: service.price
                                    };
                                  }
                                  serviceCounts[service.id].count++;
                                  serviceCounts[service.id].revenue += service.price;
                                }
                              });

                              const topServices = Object.values(serviceCounts)
                                .sort((a, b) => b.revenue - a.revenue)
                                .slice(0, 3);

                              if (topServices.length === 0) {
                                return <p className="text-gray-500 text-sm">No services booked this month yet</p>;
                              }

                              return topServices.map((service, index) => (
                                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center font-bold">
                                      {index + 1}
                                    </div>
                                    <div>
                                      <div className="font-semibold text-gray-800">{service.name}</div>
                                      <div className="text-sm text-gray-600">{service.count} bookings</div>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <div className="font-bold text-green-600">£{service.revenue.toFixed(2)}</div>
                                    <div className="text-xs text-gray-500">£{service.price}/service</div>
                                  </div>
                                </div>
                              ));
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'booking' && (
                    <div className="max-w-2xl">
                      <h2 className="text-2xl font-bold mb-6 text-gray-800">Book an Appointment</h2>
                      {renderBookingForm()}
                    </div>
                  )}

                  {activeTab === 'appointments' && (
                    <div>
                      {/* Calendar Header */}
                      <div className="flex justify-between items-center mb-6">
                        <h2 className="text-2xl font-bold text-gray-800">Appointments Calendar</h2>
                        <div className="flex items-center gap-3">
                          {/* View Toggle */}
                          <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                            <button
                              onClick={() => setCalendarView('day')}
                              className={`px-4 py-2 text-sm font-medium ${calendarView === 'day' ? 'bg-purple-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                            >
                              Day
                            </button>
                            <button
                              onClick={() => setCalendarView('week')}
                              className={`px-4 py-2 text-sm font-medium border-x border-gray-300 ${calendarView === 'week' ? 'bg-purple-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                            >
                              Week
                            </button>
                            <button
                              onClick={() => setCalendarView('month')}
                              className={`px-4 py-2 text-sm font-medium ${calendarView === 'month' ? 'bg-purple-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                            >
                              Month
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Calendar Navigation */}
                      <div className="flex justify-between items-center mb-6 bg-gray-50 p-4 rounded-lg">
                        <button
                          onClick={() => navigateCalendar(-1)}
                          className="p-2 hover:bg-white rounded-lg transition-colors"
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-semibold text-gray-800">{formatCalendarDate()}</h3>
                          <button
                            onClick={goToToday}
                            className="px-3 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700"
                          >
                            Today
                          </button>
                        </div>
                        <button
                          onClick={() => navigateCalendar(1)}
                          className="p-2 hover:bg-white rounded-lg transition-colors"
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </div>

                      {/* Calendar Views */}
                      {calendarView === 'month' && (
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                          {/* Month Header */}
                          <div className="grid grid-cols-7 bg-gray-100 border-b border-gray-200">
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                              <div key={day} className="p-3 text-center font-semibold text-gray-700 text-sm">
                                {day}
                              </div>
                            ))}
                          </div>

                          {/* Month Grid */}
                          <div className="grid grid-cols-7">
                            {(() => {
                              const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentDate);
                              const days = [];

                              // Empty cells before first day
                              for (let i = 0; i < startingDayOfWeek; i++) {
                                days.push(<div key={`empty-${i}`} className="min-h-[120px] bg-gray-50 border-b border-r border-gray-200"></div>);
                              }

                              // Days of month
                              for (let day = 1; day <= daysInMonth; day++) {
                                const date = new Date(year, month, day);
                                const dateAppointments = getAppointmentsForDate(date);
                                const isToday = date.toDateString() === new Date().toDateString();

                                days.push(
                                  <div
                                    key={day}
                                    className={`min-h-[120px] border-b border-r border-gray-200 p-2 hover:bg-gray-50 transition-colors ${isToday ? 'bg-purple-50' : 'bg-white'}`}
                                  >
                                    <div className={`text-sm font-semibold mb-1 ${isToday ? 'text-purple-600' : 'text-gray-700'}`}>
                                      {day}
                                    </div>
                                    <div className="space-y-1">
                                      {dateAppointments.slice(0, 3).map(appt => (
                                        <div
                                          key={appt.id}
                                          onClick={() => setSelectedAppointment(appt)}
                                          className="text-xs bg-purple-100 text-purple-800 p-1 rounded cursor-pointer hover:bg-purple-200 truncate"
                                        >
                                          {appt.time} - {(() => {
                                            const petNameField = formFields.find(f => f.label.toLowerCase().includes('pet') && f.label.toLowerCase().includes('name'));
                                            return petNameField ? appt.form_data?.[petNameField.id] : appt.form_data?.[4];
                                          })()}
                                        </div>
                                      ))}
                                      {dateAppointments.length > 3 && (
                                        <div className="text-xs text-gray-500 font-medium">
                                          +{dateAppointments.length - 3} more
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              }

                              return days;
                            })()}
                          </div>
                        </div>
                      )}

                      {calendarView === 'week' && (
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="grid grid-cols-8 bg-gray-100 border-b border-gray-200">
                            <div className="p-3 text-center font-semibold text-gray-700 text-sm border-r border-gray-200">
                              Time
                            </div>
                            {getWeekDays(currentDate).map((day, idx) => {
                              const isToday = day.toDateString() === new Date().toDateString();
                              return (
                                <div key={idx} className={`p-3 text-center border-r border-gray-200 last:border-r-0 ${isToday ? 'bg-purple-100' : ''}`}>
                                  <div className="text-xs text-gray-600">{day.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                                  <div className={`text-lg font-semibold ${isToday ? 'text-purple-600' : 'text-gray-800'}`}>
                                    {day.getDate()}
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Time slots grid */}
                          <div className="overflow-y-auto max-h-[600px]">
                            {generateCalendarTimeSlots().map(timeSlot => (
                              <div key={timeSlot} className="grid grid-cols-8 border-b border-gray-200">
                                {/* Time column */}
                                <div className="p-2 text-xs text-gray-600 text-right border-r border-gray-200 bg-gray-50">
                                  {timeSlot}
                                </div>

                                {/* Day columns */}
                                {getWeekDays(currentDate).map((day, idx) => {
                                  const dateAppointments = getAppointmentsForDate(day);
                                  const slotAppointment = dateAppointments.find(appt => {
                                    // Normalize both times to compare
                                    const [apptHour, apptMin] = appt.time.split(':').map(Number);
                                    const apptTime = `${apptHour.toString().padStart(2, '0')}:${apptMin.toString().padStart(2, '0')}`;
                                    return apptTime === timeSlot;
                                  });

                                  return (
                                    <div key={idx} className="min-h-[40px] border-r border-gray-200 last:border-r-0 p-1 bg-white relative">
                                      {slotAppointment && (
                                        <div
                                          onClick={() => setSelectedAppointment(slotAppointment)}
                                          className="absolute inset-x-1 bg-purple-100 border-l-4 border-purple-600 text-purple-800 p-2 rounded cursor-pointer hover:bg-purple-200 shadow-sm z-10 overflow-hidden"
                                          style={{ height: `${getAppointmentHeight(slotAppointment)}px` }}
                                        >
                                          <div className="text-xs font-semibold truncate">{slotAppointment.time}</div>
                                          <div className="text-xs truncate">
                                            {(() => {
                                              const petNameField = formFields.find(f => f.label.toLowerCase().includes('pet') && f.label.toLowerCase().includes('name'));
                                              const petName = petNameField ? slotAppointment.form_data?.[petNameField.id] : '';
                                              return petName || slotAppointment.form_data?.[1];
                                            })()}
                                          </div>
                                          <div className="text-xs truncate">{slotAppointment.service_name}</div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {calendarView === 'day' && (
                        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                          <div className="p-4 bg-gray-50 border-b border-gray-200">
                            <h3 className="font-semibold text-gray-800">
                              {currentDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                            </h3>
                          </div>
                          <div className="overflow-y-auto max-h-[600px]">
                            {(() => {
                              const dateAppointments = getAppointmentsForDate(currentDate);
                              const timeSlots = generateCalendarTimeSlots();

                              return timeSlots.map(timeSlot => {
                                const slotAppointment = dateAppointments.find(appt => {
                                  // Normalize both times to compare
                                  const [apptHour, apptMin] = appt.time.split(':').map(Number);
                                  const apptTime = `${apptHour.toString().padStart(2, '0')}:${apptMin.toString().padStart(2, '0')}`;
                                  return apptTime === timeSlot;
                                });

                                return (
                                  <div key={timeSlot} className="flex border-b border-gray-200">
                                    {/* Time column */}
                                    <div className="w-24 p-3 text-sm text-gray-600 text-right border-r border-gray-200 bg-gray-50">
                                      {timeSlot}
                                    </div>

                                    {/* Appointment column */}
                                    <div className="flex-1 min-h-[50px] p-2 relative">
                                      {slotAppointment ? (
                                        <div
                                          onClick={() => setSelectedAppointment(slotAppointment)}
                                          className="border-l-4 border-purple-600 bg-purple-100 text-purple-800 p-3 rounded cursor-pointer hover:bg-purple-200 shadow-sm"
                                          style={{ height: `${getAppointmentHeight(slotAppointment)}px` }}
                                        >
                                          <div className="flex justify-between items-start mb-2">
                                            <div className="flex items-center gap-2">
                                              <Clock className="w-4 h-4 text-purple-600" />
                                              <span className="font-semibold">{slotAppointment.time}</span>
                                            </div>
                                          </div>
                                          <h4 className="font-semibold text-gray-800">{slotAppointment.service_name}</h4>
                                          <p className="text-sm mt-1">
                                            {(() => {
                                              const ownerField = formFields.find(f => f.label.toLowerCase().includes('owner'));
                                              const petNameField = formFields.find(f => f.label.toLowerCase().includes('pet') && f.label.toLowerCase().includes('name'));
                                              const ownerName = ownerField ? slotAppointment.form_data?.[ownerField.id] : slotAppointment.form_data?.[1];
                                              const petName = petNameField ? slotAppointment.form_data?.[petNameField.id] : slotAppointment.form_data?.[4];
                                              return <><strong>Owner:</strong> {ownerName} | <strong>Pet:</strong> {petName}</>;
                                            })()}
                                          </p>
                                          <p className="text-sm">
                                            <strong>Email:</strong> {slotAppointment.client_email}
                                          </p>
                                        </div>
                                      ) : null}
                                    </div>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'tasks' && (
  <div>
    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
      <h2 className="text-xl md:text-2xl font-bold text-gray-800">Tasks & Enquiries</h2>
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
        {/* Task Type Filter */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Type:</label>
          <select
            value={taskTypeFilter}
            onChange={(e) => setTaskTypeFilter(e.target.value)}
            className="flex-1 sm:flex-initial px-3 md:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm md:text-base"
          >
            <option value="all">All</option>
            <option value="appointment">Appointments</option>
            <option value="enquiry">Enquiries</option>
          </select>
        </div>

        {/* Completion Status Filter - NEW */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Status:</label>
          <select
            value={taskCompletionFilter}
            onChange={(e) => setTaskCompletionFilter(e.target.value)}
            className="flex-1 sm:flex-initial px-3 md:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm md:text-base"
          >
            <option value="all">All</option>
            <option value="incomplete">Incomplete</option>
            <option value="complete">Complete</option>
          </select>
        </div>

        {/* Attendance Filter - NEW (only for appointments) */}
        {taskTypeFilter !== 'enquiry' && (
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Attended:</label>
            <select
              value={taskAttendanceFilter}
              onChange={(e) => setTaskAttendanceFilter(e.target.value)}
              className="flex-1 sm:flex-initial px-3 md:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm md:text-base"
            >
              <option value="all">All</option>
              <option value="attended">Attended</option>
              <option value="missed">Missed</option>
            </select>
          </div>
        )}

        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Filter:</label>
          <select
            value={tasksDateFilter}
            onChange={(e) => setTasksDateFilter(e.target.value)}
            className="flex-1 sm:flex-initial px-3 md:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm md:text-base"
          >
            <option value="all">All {taskTypeFilter === 'appointment' ? 'Appointments' : taskTypeFilter === 'enquiry' ? 'Enquiries' : 'Items'}</option>
            <option value="today">Today</option>
            <option value="tomorrow">Tomorrow</option>
            <option value="past">Past Only</option>
            <option value="future">Upcoming Only</option>
            <option value="old">Older than 4 weeks</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Sort:</label>
          <select
            value={tasksSortOrder}
            onChange={(e) => setTasksSortOrder(e.target.value)}
            className="flex-1 sm:flex-initial px-3 md:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm md:text-base"
          >
            <option value="oldest">Oldest First</option>
            <option value="newest">Newest First</option>
          </select>
        </div>
      </div>
    </div>

    {(() => {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const dayAfterTomorrow = new Date(tomorrow);
      dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 1);
      const fourWeeksAgo = new Date(now.getTime() - (28 * 24 * 60 * 60 * 1000));

      // Filter appointments based on all selections
      const filteredAppointments = appointments.filter(appt => {
        // Filter by task type
        if (taskTypeFilter === 'appointment' && appt.task_type === 'enquiry') return false;
        if (taskTypeFilter === 'enquiry' && (!appt.task_type || appt.task_type === 'appointment')) return false;

        // Filter by completion status
        if (taskCompletionFilter === 'complete' && !appt.completed) return false;
        if (taskCompletionFilter === 'incomplete' && appt.completed) return false;

        // Filter by attendance (only for appointments, not enquiries)
        if (taskTypeFilter !== 'enquiry' && appt.task_type !== 'enquiry') {
          if (taskAttendanceFilter === 'attended' && appt.attendance_status !== 'attended') return false;
          if (taskAttendanceFilter === 'missed' && appt.attendance_status === 'attended') return false;
        }

        // Filter by date
        const apptDate = new Date(appt.date);
        apptDate.setHours(0, 0, 0, 0);
        if (tasksDateFilter === 'today') return apptDate.getTime() === now.getTime();
        if (tasksDateFilter === 'tomorrow') return apptDate.getTime() === tomorrow.getTime();
        if (tasksDateFilter === 'past') return apptDate < now;
        if (tasksDateFilter === 'future') return apptDate >= now;
        if (tasksDateFilter === 'old') return apptDate < fourWeeksAgo;
        return true;
      });

      // Sort based on selected order
      const sortedAppointments = [...filteredAppointments].sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        if (tasksSortOrder === 'newest') {
          return dateB - dateA;
        } else {
          return dateA - dateB;
        }
      });

      return filteredAppointments.length === 0 ? (
        <p className="text-gray-500">No appointments found for this filter.</p>
      ) : (
        <>
          <div className="mb-3 text-sm text-gray-600">
            Showing {filteredAppointments.length} of {appointments.length} appointment{appointments.length !== 1 ? 's' : ''}
          </div>
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type/Date</th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Email</th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedAppointments.map(appt => {
                  const apptDate = new Date(appt.date);
                  const isOld = apptDate < fourWeeksAgo;
                  const isEnquiry = appt.task_type === 'enquiry';
                  const isCompleted = appt.completed;

                  const ownerField = formFields.find(f => f.label.toLowerCase().includes('owner'));
                  const phoneField = formFields.find(f => f.type === 'tel' || f.label.toLowerCase().includes('phone'));
                  const petNameField = formFields.find(f => f.label.toLowerCase().includes('pet') && f.label.toLowerCase().includes('name'));

                  const ownerName = isEnquiry
                    ? (appt.client_name || '-')
                    : (ownerField ? appt.form_data?.[ownerField.id] : appt.form_data?.[1]);

                  const phone = phoneField ? appt.form_data?.[phoneField.id] : appt.form_data?.[3];
                  const petName = petNameField ? appt.form_data?.[petNameField.id] : appt.form_data?.[4];

                  return (
                    <tr
                      key={appt.id}
                      onClick={() => openTaskDetail(appt)}
                      className={`hover:bg-purple-50 transition-colors cursor-pointer ${
                        isCompleted ? 'bg-green-50' : 
                        isOld ? 'bg-red-50' : 
                        isEnquiry ? 'bg-blue-50' : ''
                      }`}
                    >
                      <td className="px-3 md:px-6 py-4">
                        {isCompleted && (
                          <span className="inline-block mb-1 text-xs bg-green-600 text-white px-2 py-1 rounded font-semibold">
                            ✓ COMPLETE
                          </span>
                        )}
                        {isEnquiry && !isCompleted && (
                          <span className="inline-block mb-1 text-xs bg-blue-500 text-white px-2 py-1 rounded font-semibold">
                            📧 ENQUIRY
                          </span>
                        )}
                        {!isEnquiry && !isCompleted && appt.attendance_status === 'attended' && (
                          <span className="inline-block mb-1 text-xs bg-green-500 text-white px-2 py-1 rounded font-semibold">
                            ✓ ATTENDED
                          </span>
                        )}
                        <div className={`text-sm ${isOld ? 'text-red-700 font-semibold' : 'text-gray-900'}`}>
                          {apptDate.toLocaleDateString()}
                          {isOld && <span className="ml-2 text-xs bg-red-200 text-red-800 px-2 py-0.5 rounded">⚠️ Old</span>}
                        </div>
                        <div className="text-sm text-gray-500">{appt.time}</div>
                      </td>
                      <td className="px-3 md:px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{ownerName || '-'}</div>
                        <div className="text-xs text-gray-500 md:hidden">{appt.client_email}</div>
                      </td>
                      <td className="px-3 md:px-6 py-4 hidden md:table-cell">
                        <div className="text-sm text-gray-600">{appt.client_email}</div>
                      </td>
                      <td className="px-3 md:px-6 py-4">
                        {isEnquiry ? (
                       <div className="text-sm text-gray-900">
                       {appt.enquiry_form_id && (
                       <div className="text-xs font-semibold text-purple-600 mb-1">
                        Form: {enquiryForms.find(f => f.id === appt.enquiry_form_id)?.name || `Form #${appt.enquiry_form_id}`}
                    </div>
                    )}
                  {appt.enquiry_message && (
                <>
               <div className="text-xs text-gray-500 mb-1">Message:</div>
             <div className="text-gray-700 line-clamp-2">
           {appt.enquiry_message.length > 60
            ? appt.enquiry_message.substring(0, 60) + '...'
            : appt.enquiry_message}
        </div>
      </>
    )}
    {!appt.enquiry_message && (
      <div className="text-xs text-gray-400 italic">No message</div>
    )}
  </div>
                        ) : (
                          <div>
                            <div className="text-sm text-gray-900">
                              <span className="font-medium">Pet:</span> {petName || '-'}
                            </div>
                            <div className="text-xs text-gray-500">
                              <span className="font-medium">Service:</span> {appt.service_name}
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="px-3 md:px-6 py-4">
                        <div className="flex flex-col sm:flex-row gap-2">
                          {/* Email/Reply Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openFollowUpModal(appt);
                            }}
                            className="inline-flex items-center justify-center gap-1 bg-purple-600 text-white px-3 py-1.5 rounded hover:bg-purple-700 text-xs sm:text-sm font-medium whitespace-nowrap"
                          >
                            <Send className="w-3 h-3 sm:w-4 sm:h-4" />
                            {isEnquiry ? 'Reply' : 'Email'}
                          </button>

                          {/* Mark Complete Button - NEW */}
                          {!isCompleted && (
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                await markTaskComplete(appt.id);
                              }}
                              className="inline-flex items-center justify-center gap-1 bg-green-600 text-white px-3 py-1.5 rounded hover:bg-green-700 text-xs sm:text-sm font-medium whitespace-nowrap"
                            >
                              <Check className="w-3 h-3 sm:w-4 sm:h-4" />
                              Complete
                            </button>
                          )}

                          {/* Mark Attended Button - Only for non-attended, non-completed appointments */}
                          {!isEnquiry && !isCompleted && appt.attendance_status !== 'attended' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setAttendanceAppointment(appt);
                                setShowAttendanceModal(true);
                              }}
                              className="inline-flex items-center justify-center gap-1 bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 text-xs sm:text-sm font-medium whitespace-nowrap"
                            >
                              <Check className="w-3 h-3 sm:w-4 sm:h-4" />
                              Mark Attended
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      );
    })()}
  </div>
)}

                  {activeTab === 'clients' && (
                    <div>
                      <div className="flex justify-between items-center mb-6">
                        <h2 className="text-2xl font-bold text-gray-800">Client Database</h2>
                        <button onClick={() => setShowAddClient(!showAddClient)} className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700">
                          <Plus className="w-5 h-5" />
                          Add Client
                        </button>
                      </div>

                      {showAddClient && (
                        <div className="mb-6 p-6 border border-gray-200 rounded-lg bg-gray-50">
                          <h3 className="font-semibold mb-4 text-lg">New Client</h3>
                          <div className="space-y-4 max-w-2xl">
                            {formFields.map(field => (
                              <div key={field.id}>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  {field.label} {field.required && <span className="text-red-500">*</span>}
                                </label>
                                {field.type === 'textarea' ? (
                                  <textarea value={newClientForm[field.id] || ''} onChange={(e) => setNewClientForm({ ...newClientForm, [field.id]: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" rows="3" />
                                ) : field.type === 'select' ? (
                                  <select value={newClientForm[field.id] || ''} onChange={(e) => setNewClientForm({ ...newClientForm, [field.id]: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent">
                                    <option value="">Select...</option>
                                    {field.options?.map(opt => (
                                      <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <input type={field.type} value={newClientForm[field.id] || ''} onChange={(e) => setNewClientForm({ ...newClientForm, [field.id]: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
                                )}
                              </div>
                            ))}
                            <div className="flex gap-2 pt-2">
                              <button onClick={addManualClient} className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 font-semibold">Add Client</button>
                              <button onClick={() => { setShowAddClient(false); setNewClientForm({}); }} className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400 font-semibold">Cancel</button>
                            </div>
                          </div>
                        </div>
                      )}{clients.length === 0 ? (
                        <p className="text-gray-500">No clients yet. Book an appointment or add a client manually!</p>
                      ) : (
                        <div>
                          {/* Search and Export Controls */}
                          <div className="mb-4 flex gap-3 items-center">
                            <div className="flex-1 relative">
                              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                              <input
                                type="text"
                                placeholder="Search by owner name, email, pet name, breed..."
                                value={clientFilter}
                                onChange={(e) => setClientFilter(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              />
                            </div>
                            <button
                              onClick={exportClientsToCSV}
                              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 font-semibold whitespace-nowrap"
                            >
                              <Download className="w-5 h-5" />
                              Export CSV
                            </button>
                          </div>

                          {/* Results count */}
                          <div className="mb-3 text-sm text-gray-600">
                            Showing {filteredClients.length} of {clients.length} client{clients.length !== 1 ? 's' : ''}
                          </div>

                          {/* Table */}
                          <div className="overflow-x-auto border border-gray-200 rounded-lg">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner Name</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pet Name</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pet Type</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Breed</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Service</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visits</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Visit</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {filteredClients.map(client => (
                                  <tr
                                    key={client.id}
                                    onClick={() => openClientProfile(client)}
                                    className="hover:bg-purple-50 transition-colors cursor-pointer"
                                  >
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm font-medium text-gray-900">{client.owner_name}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm text-gray-600">{client.email}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm text-gray-600">{client.form_data?.[3] || '-'}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm font-medium text-gray-900">{client.pet_name}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm text-gray-600">{client.form_data?.[5] || '-'}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm text-gray-600">{client.form_data?.[6] || '-'}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm text-gray-600">
                                        {client.services && client.services.length > 0
                                          ? client.services[client.services.length - 1].name
                                          : '-'}
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                        {client.appointment_count} {client.appointment_count === 1 ? 'visit' : 'visits'}
                                      </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm text-gray-600">{new Date(client.last_visit).toLocaleDateString()}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation(); // Prevent row click
                                          deleteClientData(client.id);
                                        }}
                                        className="text-red-600 hover:text-red-800"
                                        title="Delete client data (GDPR)"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {filteredClients.length === 0 && clientFilter && (
                            <div className="text-center py-8 text-gray-500">
                              No clients found matching "{clientFilter}"
                            </div>
                          )}
                        </div>
                      )}

                      {/* Client Profile Modal */}
                      {selectedClient && editingClient && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                            {/* Header */}
                            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                              <h2 className="text-2xl font-bold text-gray-800">Client Profile</h2>
                              <button
                                onClick={() => {
                                  setSelectedClient(null);
                                  setEditingClient(null);
                                  setClientVisitHistory([]);
                                }}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                <X className="w-6 h-6" />
                              </button>
                            </div>

                            <div className="p-6 space-y-6">
                              {/* Pet Photo Section */}
                              <div className="flex items-start gap-6">
                                <div className="flex-shrink-0">
                                  <div className="w-32 h-32 rounded-lg overflow-hidden bg-gray-100 border-2 border-gray-200 flex items-center justify-center">
                                    {editingClient.pet_photo_url ? (
                                      <img
                                        src={editingClient.pet_photo_url}
                                        alt={editingClient.pet_name}
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      <div className="text-center text-gray-400">
                                        <div className="text-4xl mb-1">🐾</div>
                                        <div className="text-xs">No photo</div>
                                      </div>
                                    )}
                                  </div>
                                  <label className="mt-2 block">
                                    <input
                                      type="file"
                                      accept="image/*"
                                      onChange={(e) => uploadPetPhoto(e.target.files[0])}
                                      className="hidden"
                                    />
                                    <span className="text-xs text-purple-600 hover:text-purple-700 cursor-pointer underline">
                                      Upload Photo
                                    </span>
                                  </label>
                                </div>

                                <div className="flex-1">
                                  <h3 className="text-xl font-semibold text-gray-800 mb-1">
                                    {editingClient.pet_name}
                                  </h3>
                                  <p className="text-gray-600 mb-4">
                                    Owner: {editingClient.owner_name}
                                  </p>
                                  <div className="flex gap-2">
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                                      {selectedClient.appointment_count} {selectedClient.appointment_count === 1 ? 'visit' : 'visits'}
                                    </span>
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                                      Last visit: {new Date(selectedClient.last_visit).toLocaleDateString()}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Edit Client Information */}
                              <div className="border-t pt-6">
                                <h3 className="text-lg font-semibold text-gray-800 mb-4">Contact Information</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Owner Name
                                    </label>
                                    <input
                                      type="text"
                                      value={editingClient.owner_name || ''}
                                      onChange={(e) => setEditingClient({ ...editingClient, owner_name: e.target.value })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Email
                                    </label>
                                    <input
                                      type="email"
                                      value={editingClient.email || ''}
                                      onChange={(e) => setEditingClient({ ...editingClient, email: e.target.value })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Phone
                                    </label>
                                    <input
                                      type="tel"
                                      value={editingClient.form_data?.[3] || ''}
                                      onChange={(e) => setEditingClient({
                                        ...editingClient,
                                        form_data: { ...editingClient.form_data, 3: e.target.value }
                                      })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Pet Name
                                    </label>
                                    <input
                                      type="text"
                                      value={editingClient.pet_name || ''}
                                      onChange={(e) => setEditingClient({ ...editingClient, pet_name: e.target.value })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    />
                                  </div>
                                </div>
                                <div className="mt-4">
                                  <button
                                    onClick={updateClientInfo}
                                    className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 font-semibold"
                                  >
                                    Save Changes
                                  </button>
                                </div>
                              </div>

                              {/* Appointments */}
<div className="border-t pt-6">
  <h3 className="text-lg font-semibold text-gray-800 mb-4">Appointments</h3>
  {clientVisitHistory.length === 0 ? (
    <p className="text-gray-500 text-sm">No appointments yet</p>
  ) : (
    <div className="space-y-3">
      {clientVisitHistory.map((visit, index) => {
        const payment = Array.isArray(visit.payment) ? visit.payment[0] : visit.payment;
        const isAttended = visit.attendance_status === 'attended';

        return (
          <div
            key={visit.id}
            className="p-4 border border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-medium text-gray-900">
                    {visit.service_name || 'Service'}
                  </span>
                  {index === 0 && (
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full font-medium">
                      Most Recent
                    </span>
                  )}
                  {isAttended && (
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-medium">
                      ✓ Attended
                    </span>
                  )}
                  {!isAttended && visit.attendance_status === 'no_show' && (
                    <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded-full font-medium">
                      No Show
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-600">
                  📅 {new Date(visit.date).toLocaleDateString()} at {visit.time}
                </div>
                {payment && (
                  <div className="mt-2 space-y-1">
                    <div className="text-sm text-gray-700">
                      💳 {payment.payment_method === 'card' ? 'Card' : 'Cash'} - £{payment.amount?.toFixed(2)}
                    </div>
                    {payment.invoice_url && (
                      <div className="flex gap-3 mt-1">
                        <button
                          onClick={async () => {
                            const url = await getInvoiceUrl(payment.invoice_url);
                            window.open(url, '_blank');
                          }}
                          className="inline-flex items-center gap-1 text-sm text-purple-600 hover:text-purple-700 font-medium hover:underline cursor-pointer"
                        >
                          <Download className="w-4 h-4" />
                          Download Invoice ({payment.invoice_number})
                        </button>
                        <button
                          onClick={() => sendInvoiceEmail(visit, payment)}
                          className="inline-flex items-center gap-1 text-sm text-green-600 hover:text-green-700 font-medium hover:underline cursor-pointer"
                        >
                          <Mail className="w-4 h-4" />
                          Email Invoice
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {visit.notes && (
                  <div className="text-sm text-gray-500 mt-2">
                    📝 {visit.notes}
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">
                  {visit.status || (isAttended ? 'Completed' : 'Scheduled')}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  )}
</div>

{/* Form Submissions Section */}
<div className="border-t pt-6 mt-6">
  <h3 className="text-lg font-semibold text-gray-800 mb-4">📋 Form Submissions</h3>
  {clientEnquiries.length === 0 ? (
    <p className="text-gray-500 text-sm">No form submissions yet</p>
  ) : (
    <div className="space-y-3">
      {clientEnquiries.map((enquiry) => {
        const formName = enquiryForms.find(f => f.id === enquiry.enquiry_form_id)?.name || 'Unknown Form';
        return (
          <div
            key={enquiry.id}
            className="p-4 border border-gray-200 rounded-lg bg-blue-50"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="font-medium text-gray-900">{formName}</span>
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                {new Date(enquiry.created_at).toLocaleDateString()}
              </span>
            </div>
            {enquiry.enquiry_message && (
              <div className="text-sm text-gray-700 mb-2">
                <span className="font-medium">Message:</span> {enquiry.enquiry_message}
              </div>
            )}
            {enquiry.form_data && Object.keys(enquiry.form_data).length > 0 && (
              <div className="text-sm text-gray-600 space-y-1">
                {Object.entries(enquiry.form_data).map(([fieldId, value]) => {
                  const field = formFields.find(f => f.id === parseInt(fieldId));
                  if (!field) return null;
                  return (
                    <div key={fieldId}>
                      <span className="font-medium">{field.label}:</span> {value}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  )}
</div>

{/* Complete Client Data Section */}
<div className="border-t pt-6 mt-6">
  <h3 className="text-lg font-semibold text-gray-800 mb-4">📝 Complete Client Data</h3>
  {(() => {
    // Merge all form_data from appointments and enquiries
    const allData = {};
    
    [...clientVisitHistory, ...clientEnquiries].forEach(record => {
      if (record.form_data) {
        Object.entries(record.form_data).forEach(([fieldId, value]) => {
          const field = formFields.find(f => f.id === parseInt(fieldId));
          if (field && value && !allData[field.label]) {
            allData[field.label] = value;
          }
        });
      }
    });

    return Object.keys(allData).length === 0 ? (
      <p className="text-gray-500 text-sm">No additional data collected</p>
    ) : (
      <div className="grid grid-cols-2 gap-4">
        {Object.entries(allData).map(([label, value]) => (
          <div key={label} className="p-3 bg-gray-50 rounded-lg">
            <div className="text-xs font-medium text-gray-500 mb-1">{label}</div>
            <div className="text-sm text-gray-900">{value}</div>
          </div>
        ))}
      </div>
    );
  })()}
</div>
</div>
</div>
</div>
)}
</div>
)}

                  {activeTab === 'services' && (
                    <div>
                      <div className="flex justify-between items-center mb-6">
                        <h2 className="text-2xl font-bold text-gray-800">Services</h2>
                        <button onClick={() => setShowServiceModal(true)} className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700">
                          <Plus className="w-5 h-5" />
                          Add Service
                        </button>
                      </div>
                      <div className="space-y-4">
                        {services.map(service => (
                          <div key={service.id} className="border border-gray-200 rounded-lg p-4">
                            {editingService === service.id ? (
                              <div className="space-y-3">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Service Name</label>
                                  <input
                                    type="text"
                                    value={service.name}
                                    onChange={(e) => updateService(service.id, 'name', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    placeholder="Service Name"
                                    autoFocus
                                  />
                                </div>
                                <div className="flex gap-3">
                                  <div className="w-1/2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
                                    <input type="number" value={service.duration} onChange={(e) => updateService(service.id, 'duration', parseInt(e.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Duration" />
                                  </div>
                                  <div className="w-1/2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Price (£)</label>
                                    <input type="number" value={service.price} onChange={(e) => updateService(service.id, 'price', parseFloat(e.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Price" />
                                  </div>
                                </div>
                                <button onClick={() => setEditingService(null)} className="text-green-600 hover:text-green-700 flex items-center gap-1 font-medium">
                                  <Check className="w-4 h-4" />
                                  Done Editing
                                </button>
                              </div>
                            ) : (
                              <div className="flex justify-between items-center">
                                <div>
                                  <h3 className="font-semibold text-lg text-gray-800">{service.name}</h3>
                                  <p className="text-gray-600">
                                    <Clock className="w-4 h-4 inline mr-1" />
                                    {service.duration} minutes | £{service.price}
                                  </p>
                                </div>
                                <div className="flex gap-2">
                                  <button onClick={() => setEditingService(service.id)} className="text-blue-500 hover:text-blue-700">
                                    <Edit2 className="w-5 h-5" />
                                  </button>
                                  <button onClick={() => deleteService(service.id)} className="text-red-500 hover:text-red-700">
                                    <Trash2 className="w-5 h-5" />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Add Service Modal - PUT IT HERE */}
                      {showServiceModal && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                          <div className="bg-white rounded-lg p-6 max-w-md w-full">
                            <h3 className="text-xl font-bold mb-4 text-gray-800">Add New Service</h3>
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Service Name</label>
                                <input
                                  type="text"
                                  value={newService.name}
                                  onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                  placeholder="e.g. Full Groom"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
                                <input
                                  type="number"
                                  value={newService.duration}
                                  onChange={(e) => setNewService({ ...newService, duration: parseInt(e.target.value) })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Price (£)</label>
                                <input
                                  type="number"
                                  value={newService.price}
                                  onChange={(e) => setNewService({ ...newService, price: parseFloat(e.target.value) })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                />
                              </div>
                            </div>
                            <div className="flex gap-2 mt-6">
                              <button
                                onClick={addService}
                                className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
                              >
                                Add Service
                              </button>
                              <button
                                onClick={() => {
                                  setShowServiceModal(false);
                                  setNewService({ name: '', duration: 60, price: 0 });
                                }}
                                className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                    </div>
                  )}

                  {activeTab === 'hours' && (
                    <div>
                      <h2 className="text-2xl font-bold mb-6 text-gray-800">Business Hours</h2>
                      <div className="max-w-3xl space-y-4">
                        {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                          <div key={day} className="border border-gray-200 rounded-lg p-4 bg-white">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4 flex-1">
                                <div className="w-32">
                                  <span className="font-semibold text-gray-800 capitalize">{day}</span>
                                </div>
                                <label className="flex items-center gap-2">
                                  <input type="checkbox" checked={businessHours[day]?.open} onChange={(e) => updateBusinessHours(day, 'open', e.target.checked)} className="w-4 h-4" />
                                  <span className="text-sm text-gray-600">Open</span>
                                </label>
                                {businessHours[day]?.open && (
                                  <div className="flex items-center gap-3 flex-1">
                                    <div>
                                      <label className="text-xs text-gray-500 block mb-1">Opening Time</label>
                                      <input type="time" value={businessHours[day].start} onChange={(e) => updateBusinessHours(day, 'start', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                                    </div>
                                    <span className="text-gray-400 mt-5">to</span>
                                    <div>
                                      <label className="text-xs text-gray-500 block mb-1">Closing Time</label>
                                      <input type="time" value={businessHours[day].end} onChange={(e) => updateBusinessHours(day, 'end', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                                    </div>
                                  </div>
                                )}
                                {!businessHours[day]?.open && <span className="text-gray-400 italic text-sm">Closed</span>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg max-w-3xl">
                        <p className="text-sm text-blue-800"><strong>Note:</strong> These hours will be used to generate available time slots for appointment bookings.</p>
                      </div>

                      {/* Holiday & Closure Calendar */}
                      <div className="mt-8 border-t-2 border-gray-300 pt-8">
                        <div className="flex justify-between items-center mb-4">
                          <div>
                            <h3 className="text-xl font-semibold text-gray-800">Holiday & Closure Calendar</h3>
                            <p className="text-sm text-gray-600 mt-1">Block specific dates when your business is closed</p>
                          </div>
                          <button
                            onClick={() => setShowAddClosure(!showAddClosure)}
                            className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
                          >
                            <Plus className="w-5 h-5" />
                            Add Closure
                          </button>
                        </div>

                        {showAddClosure && (
                          <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
                            <h4 className="font-semibold mb-3">Add Closure Date(s)</h4>
                            <div className="space-y-3">
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                                  <input
                                    type="date"
                                    value={newClosure.startDate}
                                    min={new Date().toISOString().split('T')[0]}
                                    onChange={(e) => setNewClosure({ ...newClosure, startDate: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    End Date <span className="text-gray-500 font-normal">(Optional - for range)</span>
                                  </label>
                                  <input
                                    type="date"
                                    value={newClosure.endDate}
                                    min={newClosure.startDate || new Date().toISOString().split('T')[0]}
                                    onChange={(e) => setNewClosure({ ...newClosure, endDate: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    disabled={!newClosure.startDate}
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Reason (Optional)</label>
                                <input
                                  type="text"
                                  value={newClosure.reason}
                                  onChange={(e) => setNewClosure({ ...newClosure, reason: e.target.value })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                  placeholder="e.g., Christmas Holiday, Summer Vacation, etc."
                                />
                              </div>
                              {newClosure.startDate && newClosure.endDate && (
                                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                  <p className="text-sm text-blue-800">
                                    📅 This will block <strong>{Math.ceil((new Date(newClosure.endDate) - new Date(newClosure.startDate)) / (1000 * 60 * 60 * 24)) + 1} days</strong> from {new Date(newClosure.startDate).toLocaleDateString()} to {new Date(newClosure.endDate).toLocaleDateString()}
                                  </p>
                                </div>
                              )}
                              <div className="flex gap-2">
                                <button
                                  onClick={addClosureDate}
                                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                                >
                                  {newClosure.endDate ? 'Add Date Range' : 'Add Single Date'}
                                </button>
                                <button
                                  onClick={() => {
                                    setShowAddClosure(false);
                                    setNewClosure({ startDate: '', endDate: '', reason: '' });
                                  }}
                                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="space-y-3">
                          {closureDates.length === 0 ? (
                            <p className="text-gray-500 text-center py-4">No closure dates set. Your business is open on all days according to business hours.</p>
                          ) : (
                            (() => {
                              // Group consecutive dates with same reason
                              const grouped = [];
                              const sorted = [...closureDates].sort((a, b) =>
                                new Date(a.closure_date) - new Date(b.closure_date)
                              );

                              let i = 0;
                              while (i < sorted.length) {
                                const current = sorted[i];
                                const group = [current];
                                let j = i + 1;

                                // Check for consecutive dates with same reason
                                while (j < sorted.length) {
                                  const next = sorted[j];
                                  const currentDate = new Date(sorted[j - 1].closure_date);
                                  const nextDate = new Date(next.closure_date);
                                  const daysDiff = (nextDate - currentDate) / (1000 * 60 * 60 * 24);

                                  if (daysDiff === 1 && current.reason === next.reason) {
                                    group.push(next);
                                    j++;
                                  } else {
                                    break;
                                  }
                                }

                                grouped.push(group);
                                i = j;
                              }

                              return grouped.map((group, idx) => {
                                const isRange = group.length > 1;
                                const startDate = new Date(group[0].closure_date);
                                const endDate = new Date(group[group.length - 1].closure_date);

                                return (
                                  <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-white hover:shadow-md transition-shadow">
                                    <div className="flex justify-between items-start">
                                      <div className="flex-1">
                                        <div className="font-semibold text-gray-800">
                                          {isRange ? (
                                            <>
                                              {startDate.toLocaleDateString('en-US', {
                                                weekday: 'short',
                                                month: 'short',
                                                day: 'numeric'
                                              })}
                                              {' → '}
                                              {endDate.toLocaleDateString('en-US', {
                                                weekday: 'short',
                                                month: 'short',
                                                day: 'numeric',
                                                year: 'numeric'
                                              })}
                                              <span className="ml-2 text-sm text-purple-600">({group.length} days)</span>
                                            </>
                                          ) : (
                                            startDate.toLocaleDateString('en-US', {
                                              weekday: 'long',
                                              year: 'numeric',
                                              month: 'long',
                                              day: 'numeric'
                                            })
                                          )}
                                        </div>
                                        {group[0].reason && (
                                          <div className="text-sm text-gray-600 mt-1">{group[0].reason}</div>
                                        )}
                                      </div>
                                      <button
                                        onClick={async () => {
                                          if (!window.confirm(`Remove ${group.length} closure date${group.length > 1 ? 's' : ''}?`)) return;

                                          try {
                                            const ids = group.map(c => c.id);
                                            const { error } = await supabase
                                              .from('closure_dates')
                                              .delete()
                                              .in('id', ids);

                                            if (error) {
                                              console.error('Error deleting closures:', error);
                                              alert('Error deleting closure dates');
                                            } else {
                                              await loadData();
                                            }
                                          } catch (error) {
                                            console.error('Error deleting closures:', error);
                                            alert('Error deleting closure dates');
                                          }
                                        }}
                                        className="text-red-500 hover:text-red-700 ml-3"
                                        title={`Remove ${group.length} day${group.length > 1 ? 's' : ''}`}
                                      >
                                        <Trash2 className="w-5 h-5" />
                                      </button>
                                    </div>
                                  </div>
                                );
                              });
                            })()
                          )}
                        </div>

                        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-sm text-amber-800">
                            <strong>⚠️ Note:</strong> Clients will not be able to book appointments on closure dates. Any existing appointments on these dates will need to be manually rescheduled or cancelled.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'form' && (
  <div>
    <div className="flex justify-between items-center mb-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800">Form Fields</h2>
        <p className="text-gray-600 mt-1">Manage all fields used across your forms and bookings</p>
      </div>
      <button 
        onClick={() => setShowAddField(!showAddField)} 
        className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
      >
        <Plus className="w-5 h-5" />
        Add Field
      </button>
    </div>

    {showAddField && (
      <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
        <h3 className="font-semibold mb-3">New Field</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Field Label</label>
            <input 
              type="text" 
              value={newField.label} 
              onChange={(e) => setNewField({ ...newField, label: e.target.value })} 
              placeholder="e.g., Owner Name, Pet Age" 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
            <select 
              value={newField.type} 
              onChange={(e) => setNewField({ ...newField, type: e.target.value })} 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="text">Text</option>
              <option value="email">Email</option>
              <option value="tel">Phone</option>
              <option value="number">Number</option>
              <option value="textarea">Textarea</option>
              <option value="select">Select Dropdown</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Help Text (Optional)</label>
            <input 
              type="text" 
              value={newField.helpText || ''} 
              onChange={(e) => setNewField({ ...newField, helpText: e.target.value })} 
              placeholder="e.g., We'll use this to contact you about your appointment" 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" 
            />
            <p className="text-xs text-gray-500 mt-1">Optional guidance text shown below the field</p>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={addFormField} 
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
            >
              Create Field
            </button>
            <button 
              onClick={() => {
                setShowAddField(false);
                setNewField({ label: '', type: 'text', required: false });
              }} 
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}

    <div className="bg-white rounded-lg shadow-sm">
      {allFormFields.length === 0 ? (
        <div className="p-8 text-center text-gray-500">
          <p>No fields created yet.</p>
          <p className="text-sm mt-2">Click "Add Field" to create your first field.</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-200">
          {allFormFields.map(field => {
            // Find which forms use this field
            const formsUsingField = enquiryForms.filter(form => 
              allFormFields.some(f => f.form_id === form.id && f.id === field.id)
            );
            
            const usedInBooking = formFields.some(f => f.id === field.id);
            const inUse = formsUsingField.length > 0 || usedInBooking;
            const isSystemField = field.is_system_field;

            return (
              <div key={field.id} className={`p-4 hover:bg-gray-50 transition-colors ${isSystemField ? 'bg-blue-50' : ''}`}>
                <div className="flex items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {editingField?.id === field.id && !isSystemField ? (
                        <div className="flex-1 space-y-2">
                          <input
                            type="text"
                            value={editingField.label}
                            onChange={(e) => setEditingField({ ...editingField, label: e.target.value })}
                            className="font-medium text-lg border border-purple-300 rounded px-2 py-1 w-full"
                            placeholder="Field Label"
                            autoFocus
                          />
                          <input
                            type="text"
                            value={editingField.help_text || ''}
                            onChange={(e) => setEditingField({ ...editingField, help_text: e.target.value })}
                            className="text-sm border border-gray-300 rounded px-2 py-1 w-full"
                            placeholder="Help text (optional)"
                          />
                        </div>
                      ) : (
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-lg text-gray-900">{field.label}</h3>
                            {isSystemField && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                                🔒 System Field
                              </span>
                            )}
                          </div>
                          {field.help_text && (
                            <p className="text-sm text-gray-500 mt-1">💡 {field.help_text}</p>
                          )}
                        </div>
                      )}
                      <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {field.type}
                      </span>
                    </div>
                    
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Used in:</span>
                      {!inUse && (
                        <span className="ml-2 text-gray-400">Not in use</span>
                      )}
                      {usedInBooking && (
                        <span className="ml-2 inline-flex items-center gap-1 bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                          Booking Form
                        </span>
                      )}
                      {formsUsingField.map(form => (
                        <span key={form.id} className="ml-2 inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                          {form.name}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {editingField?.id === field.id && !isSystemField ? (
                      <>
                        <button
                          onClick={async () => {
                            const { data: { session } } = await supabase.auth.getSession();
                            const accessToken = session?.access_token;
                            
                            await fetch(
                              `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/enquiry_form_fields?id=eq.${field.id}`,
                              {
                                method: 'PATCH',
                                headers: {
                                  'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
                                  'Authorization': `Bearer ${accessToken}`,
                                  'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({
                                  label: editingField.label,
                                  help_text: editingField.help_text || null
                                })
                              }
                            );
                            
                            // Reload fields
                            setAllFormFields(allFormFields.map(f => 
                              f.id === field.id 
                                ? { ...f, label: editingField.label, help_text: editingField.help_text }
                                : f
                            ));
                            setEditingField(null);
                            console.log('✅ Field updated');
                          }}
                          className="text-green-600 hover:text-green-700 p-2"
                          title="Save"
                        >
                          <Check className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => setEditingField(null)}
                          className="text-gray-600 hover:text-gray-700 p-2"
                          title="Cancel"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </>
                    ) : (
                      <>
                        {!isSystemField && (
                          <button
                            onClick={() => setEditingField(field)}
                            className="text-blue-600 hover:text-blue-700 p-2"
                            title="Edit field name and help text"
                          >
                            <Edit2 className="w-5 h-5" />
                          </button>
                        )}
                        {!inUse && !isSystemField && (
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Delete "${field.label}" field? This cannot be undone.`)) return;
                              
                              const { data: { session } } = await supabase.auth.getSession();
                              const accessToken = session?.access_token;
                              
                              await fetch(
                                `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/enquiry_form_fields?id=eq.${field.id}`,
                                {
                                  method: 'DELETE',
                                  headers: {
                                    'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
                                    'Authorization': `Bearer ${accessToken}`
                                  }
                                }
                              );
                              
                              setAllFormFields(allFormFields.filter(f => f.id !== field.id));
                              console.log('✅ Field deleted');
                            }}
                            className="text-red-600 hover:text-red-700 p-2"
                            title="Delete field"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        )}
                        {(inUse || isSystemField) && (
                          <div className="text-xs text-gray-400 px-2">
                            {isSystemField ? '🔒 Protected' : 'In use'}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  </div>
)}
{activeTab === 'bookingForm' && (
  <div>
    <div className="mb-6">
      <h2 className="text-2xl font-bold text-gray-800">Booking Form Configuration</h2>
      <p className="text-gray-600 mt-1">Configure which fields appear when creating a new booking</p>
    </div>

    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Available Fields</h3>
      <p className="text-sm text-gray-600 mb-4">
        Check fields to include, toggle required status, and reorder using the arrows.
      </p>

      {allFormFields.filter((field, index, self) => 
        index === self.findIndex(f => f.label === field.label && f.type === field.type)
      ).length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No fields available yet.</p>
          <p className="text-sm mt-2">Create fields in the "Fields" tab first.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {formFields.map((field, index) => (
            <div
              key={field.id}
              className="flex items-center gap-3 p-4 border-2 border-purple-200 bg-purple-50 rounded-lg"
            >
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => {
                    if (index === 0) return;
                    const newFields = [...formFields];
                    [newFields[index - 1], newFields[index]] = [newFields[index], newFields[index - 1]];
                    setFormFields(newFields);
                    saveBookingFormConfig(newFields);
                  }}
                  disabled={index === 0}
                  className={`p-1 ${index === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:text-purple-600'}`}
                  title="Move up"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    if (index === formFields.length - 1) return;
                    const newFields = [...formFields];
                    [newFields[index], newFields[index + 1]] = [newFields[index + 1], newFields[index]];
                    setFormFields(newFields);
                    saveBookingFormConfig(newFields);
                  }}
                  disabled={index === formFields.length - 1}
                  className={`p-1 ${index === formFields.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:text-purple-600'}`}
                  title="Move down"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>

              <div className="flex-1">
                <div className="font-medium text-gray-900">{field.label}</div>
                <div className="text-sm text-gray-500">{field.type}</div>
              </div>

              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={field.required || false}
                    onChange={(e) => {
                      const newFields = formFields.map(f => 
                        f.id === field.id ? { ...f, required: e.target.checked } : f
                      );
                      setFormFields(newFields);
                      saveBookingFormConfig(newFields);
                    }}
                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                  />
                  <span className="text-gray-700">Required</span>
                </label>
              </div>

              <button
                onClick={() => {
                  const newFields = formFields.filter(f => f.id !== field.id);
                  setFormFields(newFields);
                  saveBookingFormConfig(newFields);
                }}
                className="text-red-600 hover:text-red-700 p-2"
                title="Remove field"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 border-t pt-6">
        <h4 className="font-semibold text-gray-800 mb-3">Add More Fields</h4>
        <div className="space-y-2">
          {allFormFields
            .filter(field => field.form_id === 4) // Only show shared fields
            .filter(field => !formFields.find(f => f.id === field.id))
            .map(field => (
              <button
                key={field.id}
                onClick={() => {
                  const newFields = [...formFields, { ...field, required: false }];
                  setFormFields(newFields);
                  saveBookingFormConfig(newFields);
                }}
                className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-purple-300 transition-colors"
              >
                <div className="font-medium text-gray-900">{field.label}</div>
                <div className="text-sm text-gray-500">{field.type}</div>
              </button>
            ))}
        </div>
      </div>

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Selected:</strong> {formFields.length} field{formFields.length !== 1 ? 's' : ''} will appear in the booking form
        </p>
      </div>
    </div>
  </div>
)}
                  {activeTab === 'enquiryForms' && (
                    <div>
                      <div className="flex justify-between items-center mb-6">
                        <div>
                          <h2 className="text-2xl font-bold text-gray-800">Enquiry Forms</h2>
                          <p className="text-gray-600 mt-1">Create and manage custom enquiry forms for your business</p>
                        </div>
                        <button
                          onClick={() => setShowAddEnquiryForm(true)}
                          className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                        >
                          <Plus className="w-5 h-5" />
                          <span className="hidden sm:inline">New Form</span>
                        </button>
                      </div>

                      {/* Form List */}
                      <div className="grid md:grid-cols-2 gap-6 mb-6">
                        {enquiryForms.map(form => (
                          <div
                            key={form.id}
                            className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${selectedEnquiryForm === form.id
                                ? 'border-purple-500 bg-purple-50'
                                : 'border-gray-200 hover:border-purple-300'
                              }`}
                            onClick={() => {
                              setSelectedEnquiryForm(form.id);
                              loadEnquiryFormFields(form.id);
                            }}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <h3 className="font-bold text-lg text-gray-800">{form.name}</h3>
                              <div className="flex gap-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setNewEnquiryForm({ 
                                      id: form.id,
                                      name: form.name, 
                                      description: form.description || '',
                                      customEmailTemplateId: form.custom_email_template_id,
                                      redirectUrl: form.redirect_url || ''
                                    });
                                    setShowAddEnquiryForm(true);
                                  }}
                                  className="text-gray-600 hover:text-gray-800"
                                  title="Edit form settings"
                                >
                                  <Settings className="w-5 h-5" />
                                </button>
                                <label className="relative inline-flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={form.is_active}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      updateEnquiryForm(form.id, { is_active: !form.is_active });
                                    }}
                                    className="sr-only peer"
                                  />
                                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                                </label>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteEnquiryForm(form.id);
                                  }}
                                  className="text-red-600 hover:text-red-700"
                                  title="Delete form"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            {form.description && (
                              <p className="text-gray-600 text-sm mb-2">{form.description}</p>
                            )}
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span className={form.is_active ? 'text-green-600 font-medium' : 'text-gray-400'}>
                                {form.is_active ? '● Active' : '○ Inactive'}
                              </span>
                              <span>Form ID: {form.id}</span>
                            </div>
                            <div className="mt-3 bg-purple-50 p-3 rounded border border-purple-200">
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <span className="text-xs font-medium text-purple-800">Public URL:</span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const url = `${window.location.origin}/enquiry?form=${form.id}`;
                                    navigator.clipboard.writeText(url);
                                    alert('URL copied to clipboard!');
                                  }}
                                  className="text-xs bg-purple-600 text-white px-2 py-1 rounded hover:bg-purple-700 transition-colors"
                                  title="Copy URL"
                                >
                                  Copy
                                </button>
                              </div>
                              <a
                                href={`/enquiry?form=${form.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-xs text-purple-600 font-mono hover:text-purple-700 break-all block"
                              >
                                {window.location.origin}/enquiry?form={form.id}
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Selected Form Fields Editor */}
                      {selectedEnquiryForm && (
                        <div className="bg-white border-2 border-purple-200 rounded-lg p-6">
                          <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold text-gray-800">
                              Form Fields - {enquiryForms.find(f => f.id === selectedEnquiryForm)?.name}
                            </h3>
                            <button
                              onClick={() => setShowAddEnquiryField(true)}
                              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                            >
                              <Plus className="w-4 h-4" />
                              Add Field
                            </button>
                          </div>

                          {enquiryFormFields.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                              <p>No fields yet. Click "Add Field" to get started.</p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {enquiryFormFields.map((field, index) => (
                                <div
                                  key={field.id}
                                  className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition-colors"
                                >
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className="font-medium text-gray-800">{field.label}</span>
                                        {field.required && (
                                          <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded font-medium">
                                            Required
                                          </span>
                                        )}
                                        <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                                          {field.type}
                                        </span>
                                        {field.booking_field_id && (
                                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-medium" title="Linked to booking form field">
                                            🔗 Shared Field
                                          </span>
                                        )}
                                      </div>

                                      {field.type === 'select' && field.options && (
                                        <div className="mt-2">
                                          {editingEnquiryField === field.id ? (
                                            <div className="space-y-2">
                                              {editingEnquiryOptions.map((option, optIndex) => (
                                                <div key={optIndex} className="flex gap-2">
                                                  <input
                                                    type="text"
                                                    value={option}
                                                    onChange={(e) => {
                                                      const newOptions = [...editingEnquiryOptions];
                                                      newOptions[optIndex] = e.target.value;
                                                      setEditingEnquiryOptions(newOptions);
                                                    }}
                                                    className="flex-1 px-3 py-1 border border-gray-300 rounded-lg text-sm"
                                                  />
                                                  <button
                                                    onClick={() => {
                                                      setEditingEnquiryOptions(
                                                        editingEnquiryOptions.filter((_, i) => i !== optIndex)
                                                      );
                                                    }}
                                                    className="text-red-600 hover:text-red-700"
                                                  >
                                                    <X className="w-4 h-4" />
                                                  </button>
                                                </div>
                                              ))}
                                              <div className="flex gap-2">
                                                <button
                                                  onClick={() => setEditingEnquiryOptions([...editingEnquiryOptions, 'New Option'])}
                                                  className="text-purple-600 text-sm hover:text-purple-700 flex items-center gap-1"
                                                >
                                                  <Plus className="w-4 h-4" />
                                                  Add Option
                                                </button>
                                                <button
                                                  onClick={() => saveEnquiryFieldOptions(field.id)}
                                                  className="bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700"
                                                >
                                                  Save
                                                </button>
                                                <button
                                                  onClick={() => {
                                                    setEditingEnquiryField(null);
                                                    setEditingEnquiryOptions([]);
                                                  }}
                                                  className="text-gray-600 text-sm hover:text-gray-700"
                                                >
                                                  Cancel
                                                </button>
                                              </div>
                                            </div>
                                          ) : (
                                            <div>
                                              <p className="text-sm text-gray-600">Options: {field.options.join(', ')}</p>
                                              <button
                                                onClick={() => startEditingEnquiryField(field)}
                                                className="text-purple-600 text-xs hover:text-purple-700 mt-1"
                                              >
                                                Edit Options
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>

                                    <div className="flex items-center gap-2 ml-4">
                                      <button
                                        onClick={() => moveEnquiryFormField(field.id, 'up')}
                                        disabled={index === 0}
                                        className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                        title="Move up"
                                      >
                                        <ChevronLeft className="w-5 h-5 rotate-90" />
                                      </button>
                                      <button
                                        onClick={() => moveEnquiryFormField(field.id, 'down')}
                                        disabled={index === enquiryFormFields.length - 1}
                                        className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                        title="Move down"
                                      >
                                        <ChevronRight className="w-5 h-5 rotate-90" />
                                      </button>
                                      <label className="relative inline-flex items-center cursor-pointer" title="Required field">
                                        <input
                                          type="checkbox"
                                          checked={field.required}
                                          onChange={(e) => updateEnquiryFormField(field.id, { required: e.target.checked })}
                                          className="sr-only peer"
                                        />
                                        <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-600"></div>
                                      </label>
                                      <button
                                        onClick={() => deleteEnquiryFormField(field.id)}
                                        className="text-red-600 hover:text-red-700"
                                        title="Delete field"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Add Form Modal */}
{showAddEnquiryForm && (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-lg p-6 max-w-md w-full">
      <h3 className="text-xl font-bold mb-4 text-gray-800">
  {newEnquiryForm.id ? 'Form Settings' : 'Create New Enquiry Form'}
</h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Form Name *
          </label>
          <input
            type="text"
            value={newEnquiryForm.name}
            onChange={(e) => setNewEnquiryForm({ ...newEnquiryForm, name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="e.g. General Enquiry, Quote Request"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description (Optional)
          </label>
          <textarea
            value={newEnquiryForm.description}
            onChange={(e) => setNewEnquiryForm({ ...newEnquiryForm, description: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            rows="3"
            placeholder="Brief description of this form..."
          />
        </div>
        
        {/* NEW: Custom Email Template Selector */}
<div>
  <label className="block text-sm font-medium text-gray-700 mb-1">
    Auto-Reply Email Template (Optional)
  </label>
  <select
    value={newEnquiryForm.customEmailTemplateId || ''}
    onChange={(e) => setNewEnquiryForm({ ...newEnquiryForm, customEmailTemplateId: e.target.value ? parseInt(e.target.value) : null })}
    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
  >
    <option value="">No auto-reply</option>
    {customEmailTemplates.map(template => (
      <option key={template.id} value={template.id}>
        {template.name}
      </option>
    ))}
  </select>
  <p className="text-xs text-gray-500 mt-1">
    💡 Select an email template to automatically send to users who submit this form
  </p>
</div>

{/* NEW: Redirect URL Field */}
<div>
  <label className="block text-sm font-medium text-gray-700 mb-1">
    Redirect URL After Submission (Optional)
  </label>
  <input
    type="url"
    value={newEnquiryForm.redirectUrl || ''}
    onChange={(e) => setNewEnquiryForm({ ...newEnquiryForm, redirectUrl: e.target.value })}
    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
    placeholder="https://www.yoursite.com/thank-you"
  />
  <p className="text-xs text-gray-500 mt-1">
    💡 Redirect users to this page after they submit the form (e.g., a thank-you page)
  </p>
</div>
</div>
<div className="flex gap-2 mt-6">
  <button
    onClick={createEnquiryForm}
    className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
  >
    {newEnquiryForm.id ? 'Update Form' : 'Create Form'}
  </button>
  <button
    onClick={() => {
      setShowAddEnquiryForm(false);
      setNewEnquiryForm({ id: null, name: '', description: '', customEmailTemplateId: null, redirectUrl: '' });
    }}
    className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
  >
    Cancel
  </button>
</div>
</div>
</div>
)}

                      {/* Add Field Modal */}
                      {showAddEnquiryField && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                          <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
                            <h3 className="text-xl font-bold mb-4 text-gray-800">Add Form Field</h3>

                            {/* Toggle between existing and new field */}
                            <div className="mb-6 border-b pb-4">
                              <label className="block text-sm font-medium text-gray-700 mb-3">
                                Field Source
                              </label>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => setNewEnquiryField({ ...newEnquiryField, useExisting: false, existingFieldId: null })}
                                  className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${!newEnquiryField.useExisting
                                      ? 'bg-purple-600 text-white'
                                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                  Create New Field
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setNewEnquiryField({ ...newEnquiryField, useExisting: true })}
                                  className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${newEnquiryField.useExisting
                                      ? 'bg-purple-600 text-white'
                                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                  Use Existing Field
                                </button>
                              </div>
                            </div>

                            <div className="space-y-4">
                              {newEnquiryField.useExisting ? (
                                /* Use Existing Field */
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Select Field from Library *
                                  </label>
                                  <select
                                    value={newEnquiryField.existingFieldId || ''}
                                    onChange={(e) => {
                                      const fieldId = parseInt(e.target.value);
                                      const field = allFormFields.find(f => f.id === fieldId);
                                      if (field) {
                                        setNewEnquiryField({
                                          ...newEnquiryField,
                                          existingFieldId: fieldId,
                                          label: field.label,
                                          type: field.type,
                                          required: field.required,
                                          options: field.options
                                        });
                                        if (field.type === 'select' && field.options) {
                                          setEditingEnquiryOptions([...field.options]);
                                        }
                                      }
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                  >
                                    <option value="">-- Select a field --</option>
                                    {allFormFields.filter(f => f.form_id === 4).map(field => (
                                      <option key={field.id} value={field.id}>
                                        {field.label} ({field.type})
                                      </option>
                                    ))}
                                  </select>
                                  <p className="text-xs text-gray-500 mt-2">
                                    💡 This will link to a shared field, ensuring consistent data across forms.
                                  </p>

                                  {newEnquiryField.existingFieldId && (
                                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                      <p className="text-sm font-medium text-blue-800 mb-1">Field Preview:</p>
                                      <p className="text-sm text-blue-700">
                                        <strong>Label:</strong> {newEnquiryField.label}<br />
                                        <strong>Type:</strong> {newEnquiryField.type}<br />
                                        <strong>Required:</strong> {newEnquiryField.required ? 'Yes' : 'No'}
                                        {newEnquiryField.options && newEnquiryField.options.length > 0 && (
                                          <>
                                            <br /><strong>Options:</strong> {newEnquiryField.options.join(', ')}
                                          </>
                                        )}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                /* Create New Field */
                                <>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Field Label *
                                    </label>
                                    <input
                                      type="text"
                                      value={newEnquiryField.label}
                                      onChange={(e) => setNewEnquiryField({ ...newEnquiryField, label: e.target.value })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                      placeholder="e.g. Name, Email, Message"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Field Type *
                                    </label>
                                    <select
                                      value={newEnquiryField.type}
                                      onChange={(e) => setNewEnquiryField({ ...newEnquiryField, type: e.target.value })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    >
                                      <option value="text">Text</option>
                                      <option value="email">Email</option>
                                      <option value="tel">Phone</option>
                                      <option value="textarea">Text Area (Long Text)</option>
                                      <option value="select">Dropdown</option>
                                      <option value="number">Number</option>
                                      <option value="date">Date</option>
                                    </select>
                                  </div>

                                  {newEnquiryField.type === 'select' && (
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Dropdown Options
                                      </label>
                                      {editingEnquiryOptions.map((option, index) => (
                                        <div key={index} className="flex gap-2 mb-2">
                                          <input
                                            type="text"
                                            value={option}
                                            onChange={(e) => {
                                              const newOptions = [...editingEnquiryOptions];
                                              newOptions[index] = e.target.value;
                                              setEditingEnquiryOptions(newOptions);
                                            }}
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                            placeholder={`Option ${index + 1}`}
                                          />
                                          <button
                                            onClick={() => setEditingEnquiryOptions(editingEnquiryOptions.filter((_, i) => i !== index))}
                                            className="text-red-600 hover:text-red-700"
                                          >
                                            <X className="w-4 h-4" />
                                          </button>
                                        </div>
                                      ))}
                                      <button
                                        onClick={() => setEditingEnquiryOptions([...editingEnquiryOptions, ''])}
                                        className="text-purple-600 text-sm hover:text-purple-700 flex items-center gap-1"
                                      >
                                        <Plus className="w-4 h-4" />
                                        Add Option
                                      </button>
                                    </div>
                                  )}

                                  <div className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      id="required-field"
                                      checked={newEnquiryField.required}
                                      onChange={(e) => setNewEnquiryField({ ...newEnquiryField, required: e.target.checked })}
                                      className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                                    />
                                    <label htmlFor="required-field" className="text-sm text-gray-700">
                                      Make this field required
                                    </label>
                                  </div>
                                </>
                              )}
                            </div>
                            <div className="flex gap-2 mt-6">
                              <button
                                onClick={addEnquiryFormField}
                                className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                              >
                                Add Field
                              </button>
                              <button
                                onClick={() => {
                                  setShowAddEnquiryField(false);
                                  setNewEnquiryField({ label: '', type: 'text', required: false, useExisting: false, existingFieldId: null });
                                  setEditingEnquiryOptions([]);
                                }}
                                className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'emails' && (
                    <div className="max-w-2xl">
                      <h2 className="text-2xl font-bold mb-6 text-gray-800">Email Settings</h2>

                      {/* Admin Email Settings */}
                      <div className="mb-6 p-6 bg-blue-50 border-2 border-blue-200 rounded-lg">
                        <div className="flex items-start gap-3 mb-4">
                          <div className="bg-blue-600 text-white p-2 rounded-lg">
                            <Mail className="w-5 h-5" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg text-gray-800 mb-1">Admin Email Address</h3>
                            <p className="text-sm text-gray-600 mb-3">
                              This email will receive notifications when new enquiries are submitted through your forms.
                            </p>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Your Email Address *
                            </label>
                            <input
                              type="email"
                              value={businessSettings.adminEmail || ''}
                              onChange={(e) => setBusinessSettings({ ...businessSettings, adminEmail: e.target.value })}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="admin@example.com"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              💡 Make sure to use an email you check regularly!
                            </p>
                          </div>

                          <button
                            onClick={async () => {
                              try {
                                const { error } = await supabase
                                  .from('business_settings')
                                  .update({
                                    admin_email: businessSettings.adminEmail
                                  })
                                  .eq('organization_id', CURRENT_ORG_ID);

                                if (error) throw error;
                                alert('✅ Admin email saved successfully! You will now receive enquiry notifications.');
                              } catch (error) {
                                console.error('Error saving admin email:', error);
                                alert('Error saving admin email. Please try again.');
                              }
                            }}
                            className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                          >
                            💾 Save Admin Email
                          </button>
                        </div>

                        {businessSettings.adminEmail && (
                          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <p className="text-sm text-green-800">
                              ✅ Enquiry notifications will be sent to: <strong>{businessSettings.adminEmail}</strong>
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Email Template HTML Editor */}
                      <div className="mb-6 p-6 bg-indigo-50 border-2 border-indigo-200 rounded-lg">
                        <div className="flex items-start gap-3 mb-4">
                          <div className="bg-indigo-600 text-white p-2 rounded-lg">
                            <Mail className="w-5 h-5" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg text-gray-800 mb-1">Custom Email Template</h3>
                            <p className="text-sm text-gray-600 mb-3">
                              Customize the HTML template used for all automated emails sent from your organization.
                            </p>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Email Template HTML
                            </label>
                            <p className="text-xs text-gray-500 mb-2">
                              Use <code className="bg-gray-100 px-1 rounded">{"{{companyName}}"}</code>, <code className="bg-gray-100 px-1 rounded">{"{{adminEmail}}"}</code>, and <code className="bg-gray-100 px-1 rounded">{"{{{message}}}"}</code> as placeholders
                            </p>
                            <textarea
                              value={businessSettings.emailTemplateHtml || ''}
                              onChange={(e) => setBusinessSettings({ ...businessSettings, emailTemplateHtml: e.target.value })}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono text-xs"
                              rows="15"
                              placeholder="Enter your custom email template HTML..."
                            />
                          </div>

                          <button
                            onClick={async () => {
                              try {
                                const { data: { session } } = await supabase.auth.getSession();
                                const accessToken = session?.access_token;

                                if (!accessToken) {
                                  alert('Session expired. Please refresh and try again.');
                                  return;
                                }

                                const response = await fetch(
                                  `https://bfffxyrfgvgprxjgqrvf.supabase.co/rest/v1/business_settings?organization_id=eq.${CURRENT_ORG_ID}`,
                                  {
                                    method: 'PATCH',
                                    headers: {
                                      'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
                                      'Authorization': `Bearer ${accessToken}`,
                                      'Content-Type': 'application/json'
                                    },
                                    body: JSON.stringify({
                                      email_template_html: businessSettings.emailTemplateHtml
                                    })
                                  }
                                );

                                if (response.ok) {
                                  alert('✅ Email template saved successfully!');
                                } else {
                                  const data = await response.json();
                                  console.error('Error saving template:', data);
                                  alert('Error saving email template. Please try again.');
                                }
                              } catch (error) {
                                console.error('Error saving email template:', error);
                                alert('Error saving email template. Please try again.');
                              }
                            }}
                            className="w-full bg-indigo-600 text-white px-4 py-3 rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                          >
                            💾 Save Email Template
                          </button>
                        </div>

                        {businessSettings.emailTemplateHtml && (
                          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <p className="text-sm text-green-800">
                              ✅ Custom email template is active
                            </p>
                          </div>
                        )}
                      </div>

                      <h3 className="text-xl font-bold mb-4 text-gray-800">Email Automation Settings</h3>

                      <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                        <p className="text-sm font-semibold text-purple-800 mb-2">Available Placeholders:</p>
                        <div className="flex flex-wrap gap-2">
                          <span className="text-xs bg-white text-purple-700 px-2 py-1 rounded font-mono border border-purple-200">{'{OwnerName}'}</span>
                          <span className="text-xs bg-white text-purple-700 px-2 py-1 rounded font-mono border border-purple-200">{'{PetName}'}</span>
                          <span className="text-xs bg-white text-purple-700 px-2 py-1 rounded font-mono border border-purple-200">{'{Email}'}</span>
                          <span className="text-xs bg-white text-purple-700 px-2 py-1 rounded font-mono border border-purple-200">{'{Phone}'}</span>
                          <span className="text-xs bg-white text-purple-700 px-2 py-1 rounded font-mono border border-purple-200">{'{PetBreed}'}</span>
                          <span className="text-xs bg-white text-purple-700 px-2 py-1 rounded font-mono border border-purple-200">{'{SpecialRequirements}'}</span>
                          <span className="text-xs bg-white text-purple-700 px-2 py-1 rounded font-mono border border-purple-200">{'{date}'}</span>
                          <span className="text-xs bg-white text-purple-700 px-2 py-1 rounded font-mono border border-purple-200">{'{time}'}</span>
                          <span className="text-xs bg-white text-purple-700 px-2 py-1 rounded font-mono border border-purple-200">{'{serviceName}'}</span>
                          {formFields.map(field => (
                            <span key={field.id} className="text-xs bg-white text-purple-700 px-2 py-1 rounded font-mono border border-purple-200">
                              {`{${field.label.replace(/\s+/g, '')}}`}
                            </span>
                          ))}
                        </div>
                        <p className="text-xs text-purple-700 mt-2 italic">Copy and paste these placeholders into your email body to personalize messages</p>
                      </div>

                      <div className="space-y-6">
                        <div className="p-4 border border-gray-200 rounded-lg">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="font-semibold text-lg">Confirmation Email</h3>
                            <label className="flex items-center gap-2">
                              <input type="checkbox" checked={emailSettings.confirmationEnabled} onChange={(e) => setEmailSettings({ ...emailSettings, confirmationEnabled: e.target.checked })} className="w-5 h-5" />
                              <span className="text-sm">Enabled</span>
                            </label>
                          </div>
                          <div className="space-y-3">
                            <input type="text" value={emailSettings.confirmationSubject} onChange={(e) => setEmailSettings({ ...emailSettings, confirmationSubject: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Subject" />
                            <RichTextEditor
                              value={emailSettings.confirmationBody}
                              onChange={(e) => setEmailSettings({ ...emailSettings, confirmationBody: e.target.value })}
                              placeholder="Message body"
                              rows={4}
                            />
                            <p className="text-sm text-gray-500">Use placeholders like {'{date}'}, {'{time}'}, {'{serviceName}'}, or any form field name</p>
                          </div>
                        </div>

                        <div className="p-4 border border-gray-200 rounded-lg">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="font-semibold text-lg">Reminder Email</h3>
                            <label className="flex items-center gap-2">
                              <input type="checkbox" checked={emailSettings.reminderEnabled} onChange={(e) => setEmailSettings({ ...emailSettings, reminderEnabled: e.target.checked })} className="w-5 h-5" />
                              <span className="text-sm">Enabled</span>
                            </label>
                          </div>
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Send reminder before appointment</label>
                              <select value={emailSettings.reminderDays} onChange={(e) => setEmailSettings({ ...emailSettings, reminderDays: parseInt(e.target.value) })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                                <option value="1">1 day</option>
                                <option value="2">2 days</option>
                                <option value="3">3 days</option>
                                <option value="7">1 week</option>
                              </select>
                            </div>
                            <input type="text" value={emailSettings.reminderSubject} onChange={(e) => setEmailSettings({ ...emailSettings, reminderSubject: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Subject" />
                            <RichTextEditor
                              value={emailSettings.reminderBody}
                              onChange={(e) => setEmailSettings({ ...emailSettings, reminderBody: e.target.value })}
                              placeholder="Message body"
                              rows={4}
                            />
                            <p className="text-sm text-gray-500">Use placeholders like {'{date}'}, {'{time}'}, {'{serviceName}'}, or any form field name</p>
                          </div>
                        </div>

                        <div className="p-4 border border-gray-200 rounded-lg">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="font-semibold text-lg">Follow-Up Email</h3>
                            <label className="flex items-center gap-2">
                              <input type="checkbox" checked={emailSettings.followUpEnabled} onChange={(e) => setEmailSettings({ ...emailSettings, followUpEnabled: e.target.checked })} className="w-5 h-5" />
                              <span className="text-sm">Enabled</span>
                            </label>
                          </div>
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Send follow-up after appointment</label>
                              <select value={emailSettings.followUpDays} onChange={(e) => setEmailSettings({ ...emailSettings, followUpDays: parseInt(e.target.value) })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                                <option value="1">1 day</option>
                                <option value="2">2 days</option>
                                <option value="3">3 days</option>
                                <option value="7">1 week</option>
                              </select>
                            </div>
                            <input type="text" value={emailSettings.followUpSubject} onChange={(e) => setEmailSettings({ ...emailSettings, followUpSubject: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Subject" />
                            <RichTextEditor
                              value={emailSettings.followUpBody}
                              onChange={(e) => setEmailSettings({ ...emailSettings, followUpBody: e.target.value })}
                              placeholder="Message body"
                              rows={4}
                            />
                            <p className="text-sm text-gray-500">Use placeholders like {'{date}'}, {'{time}'}, {'{serviceName}'}, or any form field name</p>
                          </div>
                        </div>

                        <div className="p-4 border border-gray-200 rounded-lg">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="font-semibold text-lg">Rebooking Reminder Email</h3>
                            <label className="flex items-center gap-2">
                              <input type="checkbox" checked={emailSettings.rebookingEnabled} onChange={(e) => setEmailSettings({ ...emailSettings, rebookingEnabled: e.target.checked })} className="w-5 h-5" />
                              <span className="text-sm">Enabled</span>
                            </label>
                          </div>
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Send rebooking reminder after appointment</label>
                              <select value={emailSettings.rebookingDays} onChange={(e) => setEmailSettings({ ...emailSettings, rebookingDays: parseInt(e.target.value) })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                                <option value="14">2 weeks</option>
                                <option value="21">3 weeks</option>
                                <option value="30">1 month</option>
                                <option value="45">6 weeks</option>
                                <option value="60">2 months</option>
                                <option value="90">3 months</option>
                              </select>
                            </div>
                            <input type="text" value={emailSettings.rebookingSubject} onChange={(e) => setEmailSettings({ ...emailSettings, rebookingSubject: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Subject" />
                            <RichTextEditor
                              value={emailSettings.rebookingBody}
                              onChange={(e) => setEmailSettings({ ...emailSettings, rebookingBody: e.target.value })}
                              placeholder="Message body"
                              rows={4}
                            />
                            <p className="text-sm text-gray-500">Use placeholders like {'{date}'}, {'{time}'}, {'{serviceName}'}, or any form field name</p>
                          </div>
                        </div>

                        {/* Save Button */}
                        <button
                          onClick={saveEmailSettings}
                          className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition-all"
                        >
                          Save Email Settings
                        </button>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <p className="text-sm text-green-800"><strong>✓ Connected to EmailJS:</strong> Your email templates are now managed from this page! Edit the content above and click "Save Email Settings" to update your templates.</p>
                        </div>

                        {/* Custom Email Templates Section */}
<div className="border-t-2 border-gray-300 pt-6 mt-8">
  <div className="flex justify-between items-center mb-4">
    <div>
      <h3 className="text-xl font-semibold text-gray-800">Custom Email Templates</h3>
      <p className="text-sm text-gray-600 mt-1">Create reusable templates for quick emailing from the Tasks page</p>
    </div>
    <button
      onClick={() => setShowAddTemplate(!showAddTemplate)}
      className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
    >
      <Plus className="w-5 h-5" />
      New Template
    </button>
  </div>

  {showAddTemplate && (
    <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
      <h4 className="font-semibold mb-3">{newTemplate.id ? 'Edit Template' : 'Create New Template'}</h4>
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
          <input
            type="text"
            value={newTemplate.name}
            onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="e.g., Birthday Wishes, Promotion, etc."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
          <input
            type="text"
            value={newTemplate.subject}
            onChange={(e) => setNewTemplate({ ...newTemplate, subject: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="Email subject line"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Message Body</label>
          <textarea
            value={newTemplate.body}
            onChange={(e) => setNewTemplate({ ...newTemplate, body: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            rows="6"
            placeholder="Email message (use placeholders like {date}, {time}, {serviceName})"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={addCustomTemplate}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
          >
            {newTemplate.id ? 'Update Template' : 'Save Template'}
          </button>
          <button
            onClick={() => {
              setShowAddTemplate(false);
              setNewTemplate({ name: '', subject: '', body: '' });
            }}
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )}

  <div className="space-y-3">
    {customEmailTemplates.length === 0 ? (
      <p className="text-gray-500 text-center py-4">No custom templates yet. Create one to get started!</p>
    ) : (
      customEmailTemplates.map(template => (
        <div key={template.id} className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <h4 className="font-semibold text-gray-800">{template.name}</h4>
              <p className="text-sm text-gray-600 mt-1"><strong>Subject:</strong> {template.subject}</p>
              <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{template.body.substring(0, 150)}{template.body.length > 150 ? '...' : ''}</p>
            </div>
            <div className="flex gap-2 ml-4">
              <button
                onClick={() => {
                  setNewTemplate({ 
                    id: template.id,
                    name: template.name, 
                    subject: template.subject, 
                    body: template.body 
                  });
                  setShowAddTemplate(true);
                }}
                className="text-blue-500 hover:text-blue-700"
                title="Edit template"
              >
                <Edit2 className="w-5 h-5" />
              </button>
              <button
                onClick={() => deleteCustomTemplate(template.id)}
                className="text-red-500 hover:text-red-700"
                title="Delete template"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      ))
    )}
  </div>
</div>
</div>
</div>
)}

                  {activeTab === 'settings' && (
                    <div className="max-w-4xl">
                      <h2 className="text-2xl font-bold mb-6 text-gray-800">Settings</h2>

                      <div className="space-y-6">
  {/* Client Booking URL */}
  <div className="border border-purple-200 bg-purple-50 rounded-lg p-6">
    <h3 className="text-xl font-semibold mb-3 text-gray-800 flex items-center gap-2">
      <Calendar className="w-5 h-5 text-purple-600" />
      Client Booking URL
    </h3>
    <p className="text-sm text-gray-600 mb-4">
      Share this link with your clients to allow them to book appointments online:
    </p>
    <div className="bg-white border-2 border-purple-300 rounded-lg p-4">
      <div className="flex items-center justify-between gap-3">
        <code className="text-purple-700 font-mono text-sm break-all flex-1">
          {window.location.origin}/book?org={organization?.slug || 'your-business-name'}
        </code>
        <button
          onClick={() => {
            const bookingUrl = `${window.location.origin}/book?org=${organization?.slug || 'your-business-name'}`;
            navigator.clipboard.writeText(bookingUrl);
            alert('Booking URL copied to clipboard!');
          }}
          className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 font-semibold whitespace-nowrap text-sm"
        >
          Copy Link
        </button>
      </div>
    </div>
    <p className="text-xs text-gray-500 mt-3">
      💡 <strong>Tip:</strong> Add this link to your website, social media profiles, or include it in emails to clients.
    </p>
  </div>

                        {/* Admin Information */}
                        <div className="border border-gray-200 rounded-lg p-6 bg-white">
                          <h3 className="text-xl font-semibold mb-4 text-gray-800">Admin Information</h3>
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                              <input
                                type="text"
                                value={businessSettings.adminName}
                                onChange={(e) => setBusinessSettings({ ...businessSettings, adminName: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                placeholder="Your name"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                              <input
                                type="email"
                                value={businessSettings.adminEmail}
                                onChange={(e) => setBusinessSettings({ ...businessSettings, adminEmail: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                placeholder="admin@example.com"
                              />
                            </div>
                            <button
                              onClick={saveAdminInfo}
                              className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 font-semibold"
                            >
                              Save Admin Info
                            </button>
                          </div>
                        </div>

                        {/* Booking Settings */}
                        <div className="border border-gray-200 rounded-lg p-6 bg-white">
                          <h3 className="text-xl font-semibold mb-4 text-gray-800">Booking Settings</h3>
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Redirect URL After Booking (Optional)
                              </label>
                              <input
                                type="url"
                                value={businessSettings.redirectUrl}
                                onChange={(e) => setBusinessSettings({ ...businessSettings, redirectUrl: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                placeholder="https://example.com/thank-you"
                              />
                              <p className="text-sm text-gray-500 mt-2">
                                After a client books an appointment, they will be redirected to this URL. Leave blank to show a standard success message.
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                <strong>Tip:</strong> Create a thank-you page with booking confirmation details, next steps, or special offers.
                              </p>
                            </div>

                            <div className="pt-4 border-t">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Privacy Policy URL *
                              </label>
                              <input
                                type="url"
                                value={businessSettings.privacyPolicyUrl || ''}
                                onChange={(e) => setBusinessSettings({ ...businessSettings, privacyPolicyUrl: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                placeholder="https://yourwebsite.com/privacy-policy"
                              />
                              <p className="text-sm text-gray-500 mt-2">
                                Link to your privacy policy page. This is required for enquiry forms (GDPR compliance).
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                <strong>Tip:</strong> Host your privacy policy on your main website or use a free privacy policy generator.
                              </p>
                            </div>

                            <button
                              onClick={saveRedirectUrl}
                              className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 font-semibold"
                            >
                              Save Settings
                            </button>
                          </div>
                        </div>

                        {/* Company Logo */}
                        <div className="border border-gray-200 rounded-lg p-6 bg-white">
                          <h3 className="text-xl font-semibold mb-4 text-gray-800">Company Logo</h3>
                          <div className="space-y-4">
                            {businessSettings.logo && (
                              <div className="flex items-center gap-4">
                                <img
                                  src={businessSettings.logo}
                                  alt="Company Logo"
                                  className="w-24 h-24 rounded-lg object-cover border-2 border-gray-200"
                                />
                                <button
                                  onClick={() => setBusinessSettings({ ...businessSettings, logo: null })}
                                  className="text-red-600 hover:text-red-700 flex items-center gap-1"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Remove Logo
                                </button>
                              </div>
                            )}
                            <div>
                              <label className="flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 cursor-pointer inline-flex font-semibold">
                                <Upload className="w-5 h-5" />
                                {businessSettings.logo ? 'Change Logo' : 'Upload Logo'}
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={handleLogoUpload}
                                  className="hidden"
                                />
                              </label>
                              <p className="text-sm text-gray-500 mt-2">Recommended: Square image, at least 200x200px</p>
                            </div>
                          </div>
                        </div>

                        {/* Company Name */}
                        <div className="border border-gray-200 rounded-lg p-6 bg-white">
                          <h3 className="text-xl font-semibold mb-4 text-gray-800">Company Name</h3>
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Business Name</label>
                              <input
                                type="text"
                                value={businessSettings.companyName}
                                onChange={(e) => setBusinessSettings({ ...businessSettings, companyName: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                placeholder="Your business name"
                              />
                              <p className="text-sm text-gray-500 mt-2">This name appears in the header and can also be edited by clicking the edit icon next to it</p>
                            </div>
                          </div>
                        </div>

                        {/* Password Reset */}
                        <div className="border border-gray-200 rounded-lg p-6 bg-white">
                          <h3 className="text-xl font-semibold mb-4 text-gray-800">Change Password</h3>
                          <form onSubmit={handlePasswordReset} className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                              <input
                                type="password"
                                value={passwordForm.currentPassword}
                                onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                              <input
                                type="password"
                                value={passwordForm.newPassword}
                                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                required
                                minLength="6"
                              />
                              <p className="text-sm text-gray-500 mt-1">Minimum 6 characters</p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                              <input
                                type="password"
                                value={passwordForm.confirmPassword}
                                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                required
                              />
                            </div>
                            <button
                              type="submit"
                              className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 font-semibold"
                            >
                              Update Password
                            </button>
                          </form>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <p className="text-sm text-blue-800"><strong>Note:</strong> In a production environment, these settings would be saved to your database and authentication would be handled securely via Supabase Auth.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Modals - Outside bg-white card but inside max-w-7xl */}
          {followUpModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-bold text-gray-800">Send Email</h2>
                    <button
                      onClick={() => {
                        setFollowUpModal(null);
                        setFollowUpEmail({ subject: '', message: '' });
                        setSelectedTemplateId('');
                        setEmailImages([]);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>

                  <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold text-gray-700 mb-2">Client Information:</h3>
                    <p className="text-sm text-gray-600"><strong>Name:</strong> {followUpModal.form_data?.[1]}</p>
                    <p className="text-sm text-gray-600"><strong>Email:</strong> {followUpModal.client_email}</p>
                    <p className="text-sm text-gray-600"><strong>Pet:</strong> {followUpModal.form_data?.[4]}</p>
                    <p className="text-sm text-gray-600"><strong>Last Service:</strong> {followUpModal.service_name} on {new Date(followUpModal.date).toLocaleDateString()}</p>
                  </div>

                  {/* Template Selector */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Load Template (Optional)</label>
                    <select
                      value={selectedTemplateId}
                      onChange={(e) => loadEmailTemplate(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">-- Select a template --</option>
                      <option value="followup">Follow-Up Email (default)</option>
                      <option value="rebooking">Rebooking Email</option>
                      {customEmailTemplates.map(template => (
                        <option key={template.id} value={template.id}>
                          {template.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                      <input
                        type="text"
                        value={followUpEmail.subject}
                        onChange={(e) => setFollowUpEmail({ ...followUpEmail, subject: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Email subject"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                      <textarea
                        value={followUpEmail.message}
                        onChange={(e) => setFollowUpEmail({ ...followUpEmail, message: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        rows="10"
                        placeholder="Email message"
                      />
                    </div>

                    {/* Image Upload Section */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">📸 Attach Photos (Optional)</label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageUpload}
                          className="hidden"
                          id="email-image-upload"
                        />
                        <label
                          htmlFor="email-image-upload"
                          className="flex flex-col items-center justify-center cursor-pointer"
                        >
                          <Upload className="w-8 h-8 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-600">Click to upload images</span>
                          <span className="text-xs text-gray-400 mt-1">PNG, JPG up to 5MB each</span>
                        </label>
                      </div>

                      {/* Display uploaded images */}
                      {emailImages.length > 0 && (
                        <div className="mt-4 grid grid-cols-2 gap-3">
                          {emailImages.map((image, index) => (
                            <div key={index} className="relative group">
                              <img
                                src={image.url}
                                alt={image.name}
                                className="w-full h-32 object-cover rounded-lg border border-gray-200"
                              />
                              <button
                                onClick={() => removeImage(index)}
                                className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Remove image"
                              >
                                <X className="w-4 h-4" />
                              </button>
                              <p className="text-xs text-gray-600 mt-1 truncate">{image.name}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={sendFollowUpEmail}
                        className="flex-1 bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition-all flex items-center justify-center gap-2"
                      >
                        <Send className="w-5 h-5" />
                        Send Email
                      </button>
                      <button
                        onClick={() => {
                          setFollowUpModal(null);
                          setFollowUpEmail({ subject: '', message: '' });
                          setSelectedTemplateId('');
                        }}
                        className="px-6 py-3 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-all"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Appointment Details Modal */}
          {selectedAppointment && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-lg w-full">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-bold text-gray-800">Appointment Details</h2>
                    <button
                      onClick={() => setSelectedAppointment(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="w-5 h-5 text-purple-600" />
                        <span className="font-semibold text-gray-800">
                          {new Date(selectedAppointment.date).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-5 h-5 text-purple-600" />
                        <span className="font-semibold text-gray-800">{selectedAppointment.time}</span>
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <h3 className="font-semibold text-gray-700 mb-3">Service</h3>
                      <p className="text-gray-800 text-lg">{selectedAppointment.service_name}</p>
                    </div>

                    <div className="border-t pt-4">
                      <h3 className="font-semibold text-gray-700 mb-3">Client Information</h3>
                      <div className="space-y-2">
                        {(() => {
                          // Find fields dynamically by label
                          const ownerField = formFields.find(f => f.label.toLowerCase().includes('owner'));
                          const phoneField = formFields.find(f => f.type === 'tel' || f.label.toLowerCase().includes('phone'));
                          const petNameField = formFields.find(f => f.label.toLowerCase().includes('pet') && f.label.toLowerCase().includes('name'));
                          const breedField = formFields.find(f => f.label.toLowerCase().includes('breed'));
                          const specialField = formFields.find(f => f.label.toLowerCase().includes('special') || f.label.toLowerCase().includes('requirement'));

                          return (
                            <>
                              {ownerField && selectedAppointment.form_data?.[ownerField.id] && (
                                <p className="text-gray-600"><strong>Owner:</strong> {selectedAppointment.form_data[ownerField.id]}</p>
                              )}
                              <p className="text-gray-600"><strong>Email:</strong> {selectedAppointment.client_email}</p>
                              {phoneField && selectedAppointment.form_data?.[phoneField.id] && (
                                <p className="text-gray-600"><strong>Phone:</strong> {selectedAppointment.form_data[phoneField.id]}</p>
                              )}
                              {petNameField && selectedAppointment.form_data?.[petNameField.id] && (
                                <p className="text-gray-600"><strong>Pet Name:</strong> {selectedAppointment.form_data[petNameField.id]}</p>
                              )}
                              {breedField && selectedAppointment.form_data?.[breedField.id] && (
                                <p className="text-gray-600"><strong>Breed:</strong> {selectedAppointment.form_data[breedField.id]}</p>
                              )}
                              {specialField && selectedAppointment.form_data?.[specialField.id] && (
                                <p className="text-gray-600"><strong>Special Requirements:</strong> {selectedAppointment.form_data[specialField.id]}</p>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    <div className="flex gap-3 pt-4">
                      {/* Mark Attended Button - only show if not already attended */}
                      {selectedAppointment.attendance_status !== 'attended' && (
                        <button
                          onClick={() => {
                            setAttendanceAppointment(selectedAppointment);
                            setShowAttendanceModal(true);
                          }}
                          className="flex-1 bg-green-600 text-white py-2 rounded-lg font-semibold hover:bg-green-700 transition-all flex items-center justify-center gap-2"
                        >
                          <Check className="w-4 h-4" />
                          Mark Attended
                        </button>
                      )}

                      {/* Email Client Button */}
                      <button
                        onClick={() => {
                          openFollowUpModal(selectedAppointment);
                          setSelectedAppointment(null);
                        }}
                        className="flex-1 bg-purple-600 text-white py-2 rounded-lg font-semibold hover:bg-purple-700 transition-all flex items-center justify-center gap-2"
                      >
                        <Send className="w-4 h-4" />
                        Email Client
                      </button>

                      {/* Delete Button */}
                      <button
                        onClick={async () => {
                          if (window.confirm('Are you sure you want to delete this appointment?')) {
                            await deleteAppointment(selectedAppointment.id);
                            setSelectedAppointment(null);
                          }
                        }}
                        className="px-4 py-2 border border-red-300 text-red-600 rounded-lg font-semibold hover:bg-red-50 transition-all flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Task/Enquiry Detail Modal */}
          {selectedTask && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-2xl font-bold text-gray-800">
                    {selectedTask.task_type === 'enquiry' ? '📧 Enquiry Details' : '📅 Appointment Details'}
                  </h3>
                  <button
                    onClick={() => {
                      setSelectedTask(null);
                      setSelectedTaskPayment(null);
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Type Badge */}
                  {selectedTask.task_type === 'enquiry' && (
                    <div className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                      Enquiry
                    </div>
                  )}
                  {selectedTask.task_type !== 'enquiry' && selectedTask.attendance_status === 'attended' && (
                    <div className="inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                      ✓ Attended
                    </div>
                  )}

                  {/* Date & Time */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Date</label>
                      <div className="text-lg font-semibold text-gray-800">
                        {new Date(selectedTask.date).toLocaleDateString()}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Time</label>
                      <div className="text-lg font-semibold text-gray-800">{selectedTask.time}</div>
                    </div>
                  </div>

                  {/* Client Info */}
                  <div>
                    <label className="text-sm font-medium text-gray-500">Client Name</label>
                    <div className="text-lg font-semibold text-gray-800">
                      {selectedTask.task_type === 'enquiry'
                        ? (selectedTask.client_name || '-')
                        : (() => {
                          const ownerField = formFields.find(f => f.label.toLowerCase().includes('owner'));
                          return ownerField ? selectedTask.form_data?.[ownerField.id] : selectedTask.form_data?.[1] || '-';
                        })()
                      }
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="text-sm font-medium text-gray-500">Email</label>
                    <div className="text-lg text-gray-800">{selectedTask.client_email}</div>
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="text-sm font-medium text-gray-500">Phone</label>
                    <div className="text-lg text-gray-800">
                      {(() => {
                        const phoneField = formFields.find(f => f.type === 'tel' || f.label.toLowerCase().includes('phone'));
                        return phoneField ? selectedTask.form_data?.[phoneField.id] : selectedTask.form_data?.[3] || '-';
                      })()}
                    </div>
                  </div>

                  {/* Enquiry Message or Appointment Details */}
                  {selectedTask.task_type === 'enquiry' ? (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Message</label>
                      <div className="mt-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-gray-800 whitespace-pre-wrap">{selectedTask.enquiry_message || 'No message'}</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Pet Name</label>
                        <div className="text-lg text-gray-800">
                          {(() => {
                            const petNameField = formFields.find(f => f.label.toLowerCase().includes('pet') && f.label.toLowerCase().includes('name'));
                            return petNameField ? selectedTask.form_data?.[petNameField.id] : selectedTask.form_data?.[4] || '-';
                          })()}
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Service</label>
                        <div className="text-lg text-gray-800">{selectedTask.service_name}</div>
                      </div>

                      {/* Payment Information */}
                      {selectedTaskPayment && (
                        <div className="border-t pt-4 mt-4">
                          <label className="text-sm font-medium text-gray-500 mb-3 block">Payment Details</label>
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-gray-700">Amount Paid:</span>
                              <span className="text-lg font-bold text-green-700">£{selectedTaskPayment.amount?.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-gray-700">Payment Method:</span>
                              <span className="text-sm text-gray-900">{selectedTaskPayment.payment_method === 'card' ? '💳 Card' : '💷 Cash'}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-gray-700">Invoice Number:</span>
                              <span className="text-sm font-mono text-gray-900">{selectedTaskPayment.invoice_number}</span>
                            </div>

                            {/* Invoice Actions */}
                            <div className="flex gap-2 pt-2 border-t border-green-200">
                              <button
                                onClick={async () => {
                                  const url = await getInvoiceUrl(selectedTaskPayment.invoice_url);
                                  window.open(url, '_blank');
                                }}
                                className="flex-1 bg-purple-600 text-white py-2 px-3 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                              >
                                <Download className="w-4 h-4" />
                                View Invoice
                              </button>
                              <button
                                onClick={() => sendInvoiceEmail(selectedTask, selectedTaskPayment)}
                                className="flex-1 bg-green-600 text-white py-2 px-3 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                              >
                                <Mail className="w-4 h-4" />
                                Email Invoice
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* All Form Data */}
                  {selectedTask.form_data && Object.keys(selectedTask.form_data).length > 0 && (
                    <div className="border-t pt-4">
                      <label className="text-sm font-medium text-gray-500 mb-2 block">Additional Information</label>
                      <div className="space-y-2">
                        {formFields.map(field => {
                          const value = selectedTask.form_data[field.id];
                          if (value) {
                            return (
                              <div key={field.id} className="flex justify-between py-1">
                                <span className="text-sm font-medium text-gray-600">{field.label}:</span>
                                <span className="text-sm text-gray-800">{value}</span>
                              </div>
                            );
                          }
                          return null;
                        })}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
<div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
  <button
    onClick={() => {
      openFollowUpModal(selectedTask);
      setSelectedTask(null);
    }}
    className="flex-1 bg-purple-600 text-white px-4 py-3 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 font-medium"
  >
    <Send className="w-5 h-5" />
    {selectedTask.task_type === 'enquiry' ? 'Reply to Enquiry' : 'Send Email'}
  </button>
  
  {!selectedTask.completed && (
    <button
      onClick={async () => {
        await markTaskComplete(selectedTask.id);
        setSelectedTask(null);
      }}
      className="flex-1 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 font-medium"
    >
      <Check className="w-5 h-5" />
      Mark Complete
    </button>
  )}
  
  {selectedTask.completed && (
    <div className="flex-1 bg-green-100 text-green-800 px-4 py-3 rounded-lg flex items-center justify-center gap-2 font-medium border-2 border-green-300">
      <Check className="w-5 h-5" />
      Completed
    </div>
  )}
</div>
</div>
</div>
</div>
)}

          {/* Attendance/Payment Modal */}
          {showAttendanceModal && attendanceAppointment && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-gray-800">Mark Appointment as Attended</h3>
                  <button 
                    onClick={() => {
                      setShowAttendanceModal(false);
                      setAttendanceAppointment(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Client: <strong>{attendanceAppointment.client_name || attendanceAppointment.client_email}</strong></p>
                    <p className="text-sm text-gray-600 mb-2">Service: <strong>{attendanceAppointment.service_name}</strong></p>
                    <p className="text-sm text-gray-600 mb-4">
                      Amount: <strong>£{services.find(s => s.id === attendanceAppointment.service_id)?.price?.toFixed(2) || '0.00'}</strong>
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method:</label>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setPaymentMethod('card')}
                        className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                          paymentMethod === 'card'
                            ? 'border-purple-600 bg-purple-50 text-purple-600'
                            : 'border-gray-300 text-gray-600 hover:border-gray-400'
                        }`}
                      >
                        💳 Card
                      </button>
                      <button
                        onClick={() => setPaymentMethod('cash')}
                        className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                          paymentMethod === 'cash'
                            ? 'border-purple-600 bg-purple-50 text-purple-600'
                            : 'border-gray-300 text-gray-600 hover:border-gray-400'
                        }`}
                      >
                        💷 Cash
                      </button>
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      ✅ Mark appointment as attended
                      <br />✅ Record payment method
                      <br />✅ Generate invoice PDF
                      <br />✅ Store invoice in database
                    </p>
                  </div>
                  
                  <button
                    onClick={async () => {
                      await markAsAttended(attendanceAppointment, paymentMethod);
                      setShowAttendanceModal(false);
                      setAttendanceAppointment(null);
                      setSelectedAppointment(null);
                    }}
                    disabled={processingPayment}
                    className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processingPayment ? '⏳ Processing...' : '✅ Confirm Attendance & Payment'}
                  </button>
                </div>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default PetGroomingCRM;